// DESIGN_AUDIT Phase 11D: spacing and typography fixes
// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useFacility } from '../contexts/FacilityContext.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { api } from '../api/client.ts';
import { getImageSrc } from '../utils/imageUrl.ts';
import { downloadExport as exportFile } from '../utils/exportHelper.ts';
import { User, Shield, Database, Bell, Tag, Pencil, Trash2, Plus, X, Settings2, Download, Upload, FileJson, FileText, HardDrive, CheckCircle, AlertCircle, XCircle, Sun, Moon, Monitor, Building2, Lock, Globe, Puzzle, DollarSign, Eye, EyeOff, AlertTriangle, ChevronDown, Copy, ListChecks, Search, GripVertical, Mail, CreditCard, ShieldCheck } from 'lucide-react';
import AuditLog from '../components/settings/audit/AuditLog';
import { PlanTierId, TierPricing, EffectiveBillingConfig } from '../contexts/BillingConfigContext.tsx';
import SlidingToggle from '../components/SlidingToggle.tsx';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ServiceCategory } from '../types/index.ts';
import { KNOWN_SUPPLIERS } from '../constants/suppliers.ts';
import { LogbookFormRenderer, type LogbookFormSchema } from '../components/LogbookFormRenderer.tsx';
import Toast, { useToast } from '../components/Toast';
import { spinIconOut } from '../utils/iconAnimations.ts';
import { useEscapeToClose } from '../hooks/useEscapeToClose.ts';
import { useDropdownOptions, invalidateDropdowns } from '../hooks/useDropdownOptions.ts';
import '../styles/buttonAnimations.css';

interface UserRow {
  id: number;
  username: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  last_login: string | null;
  facility_id: number | null;
  facility_name: string | null;
  org_id: number | null;
  org_name: string | null;
  department_id: number | null;
  department_name: string | null;
  department_color: string | null;
}

interface Department {
  id: number;
  name: string;
  color: string;
  org_id: number | null;
}

type SettingsTab = 'profile' | 'users' | 'appearance' | 'services' | 'import-export' | 'dropdowns' | 'facility' | 'orders' | 'notifications' | 'financials' | 'organization' | 'dev-todos';

interface OrgRow {
  id: number;
  name: string;
  slug: string;
  plan_tier: string;
  facility_count: number;
  user_count: number;
  // v143 admin bypass fields -- already included in GET /organizations's `SELECT o.*` (see
  // server/src/routes/organizations.ts), just not typed here until now.
  bypass_active: boolean;
  bypass_tier: string | null;
  bypass_expires_at: string | null;
}

interface FacilityBypassRow {
  facility_id: number;
  name: string;
  bypass_active: boolean;
  bypass_tier: string | null;
  bypass_expires_at: string | null;
}

interface BypassFormShape { active: boolean; tier: string; duration: '24h' | '7d' | '30d' | 'custom'; customDate: string }

// ─── BypassFieldsEditor — org- and facility-level admin bypass controls (v143) ─────────────────
// Reused for both the org-level bypass (Settings.tsx's bypassOrgForm) and each per-facility row
// under a Per-Facility-mode org (bypassFacilityForms) -- same shape, same fields, just a different
// backing form + onChange the two call sites wire up. Kept generic here rather than duplicated
// inline JSX in both places.
function BypassFieldsEditor({ form, onChange }: { form: BypassFormShape; onChange: (next: BypassFormShape) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SlidingToggle
        options={[{ id: 'off', label: 'Bypass Off' }, { id: 'on', label: 'Bypass On' }]}
        value={form.active ? 'on' : 'off'}
        onChange={(v) => onChange({ ...form, active: v === 'on' })}
      />
      {form.active && (
        <>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Grant Tier</label>
            <SlidingToggle
              options={[{ id: 'standard', label: 'Standard' }, { id: 'pro', label: 'Pro' }, { id: 'enterprise', label: 'Enterprise' }]}
              value={form.tier}
              onChange={(v) => onChange({ ...form, tier: v })}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Maintain Until</label>
            <SlidingToggle
              options={[{ id: '24h', label: '24h' }, { id: '7d', label: '7 days' }, { id: '30d', label: '30 days' }, { id: 'custom', label: 'Custom' }]}
              value={form.duration}
              onChange={(v) => onChange({ ...form, duration: v as BypassFormShape['duration'] })}
            />
            {form.duration === 'custom' && (
              <input
                type="datetime-local"
                className="form-input"
                style={{ marginTop: 8 }}
                value={form.customDate}
                onChange={(e) => onChange({ ...form, customDate: e.target.value })}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── DarkSelect — single-select portal dropdown ──────────────────────────────
function DarkSelect({ value, onChange, options, placeholder = 'Select…' }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 202;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px',
          border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6,
          background: 'var(--bg-elevated)',
          color: selectedLabel ? 'var(--text-primary)' : 'var(--text-muted)',
          fontSize: 12,
          height: 34,
          boxSizing: 'border-box',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span>{selectedLabel || placeholder}</span>
        <ChevronDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 10001,
            background: 'var(--bg-elevated)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            color: 'var(--text-primary)',
            maxHeight: 200,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: value === opt.value ? 'var(--accent-primary)' : 'var(--text-primary)', background: value === opt.value ? 'var(--bg-hover)' : 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = value === opt.value ? 'var(--bg-hover)' : 'transparent')}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    return (sessionStorage.getItem('settings_active_tab') as SettingsTab) ?? 'profile';
  });

  function handleSetActiveTab(tab: SettingsTab) {
    sessionStorage.setItem('settings_active_tab', tab);
    setActiveTab(tab);
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'org_admin';
  const isSuperAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canManageUsers = isAdmin; // admin/org_admin can add/edit/delete users; manager is read-only + can add viewer

  // ── Profile state ─────────────────────────────────────────────────────────
  const { refreshUser } = useAuth();
  const [profileForm, setProfileForm] = useState({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '', email: user?.email ?? '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Keep profileForm in sync when user loads
  useEffect(() => {
    if (user) setProfileForm({ firstName: user.firstName ?? '', lastName: user.lastName ?? '', email: user.email ?? '' });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSaveProfile() {
    setProfileError('');
    setProfileSaved(false);
    if (!profileForm.email.trim()) { setProfileError('Email is required.'); return; }
    setProfileSaving(true);
    try {
      await api.put('/auth/profile', {
        first_name: profileForm.firstName.trim() || null,
        last_name: profileForm.lastName.trim() || null,
        email: profileForm.email.trim(),
      });
      setProfileSaved(true);
      await refreshUser();
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setProfileError(msg ?? 'Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  }

  // ── Profile Change Password state ─────────────────────────────────────────
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  async function changeOwnPassword() {
    setPwError('');
    setPwSuccess(false);
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { setPwError('All fields are required.'); return; }
    if (pwForm.newPw.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('Passwords do not match.'); return; }
    setPwSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.newPw });
      setPwSuccess(true);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setPwError(msg ?? 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  }

  // ── User modal state ──────────────────────────────────────────────────────
  const [userModal, setUserModal] = useState<{ mode: 'add' | 'edit'; row?: UserRow } | null>(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', role: 'user', first_name: '', last_name: '', is_active: true, facility_id: null as number | null, org_id: null as number | null, department_id: null as number | null });
  const [userFormError, setUserFormError] = useState('');
  const [userSaving, setUserSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<UserRow | null>(null);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [assignOrgState, setAssignOrgState] = useState<{ userId: number; orgId: number } | null>(null);
  const [assignOrgSaving, setAssignOrgSaving] = useState(false);
  const mouseDownOnOverlay = useRef(false);
  function closeUserModal() { setUserModal(null); }

  // Orgs for admin user modal org picker
  const { data: userModalOrgsRes } = useQuery({
    queryKey: ['user-modal-orgs'],
    queryFn: () => api.get<OrgRow[]>('/organizations'),
    enabled: isSuperAdmin && activeTab === 'users',
  });
  const userModalOrgs: OrgRow[] = (userModalOrgsRes as unknown as { data: OrgRow[] })?.data ?? [];

  // Facilities filtered by org selected in modal (for admin) or own org (for org_admin)
  const { data: orgFacilitiesRes } = useQuery({
    queryKey: ['org-facilities', userForm.org_id ?? 0],
    queryFn: () => {
      if (isSuperAdmin && userForm.org_id) {
        return api.get<{ id: number; name: string }[]>(`/facilities?orgId=${userForm.org_id}`);
      }
      return api.get<{ id: number; name: string }[]>('/facilities');
    },
    enabled: (isAdmin || isManager) && (!isSuperAdmin || !!userForm.org_id),
  });
  const orgFacilities = (orgFacilitiesRes as unknown as { data: { id: number; name: string }[] })?.data ?? [];

  // Departments for user modal
  const [departments, setDepartments] = useState<Department[]>([]);
  useEffect(() => {
    if (isAdmin || isManager) {
      api.get<Department[]>('/departments')
        .then(res => setDepartments(res.data ?? []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openAddUser() {
    setUserForm({ username: '', email: '', password: '', role: 'user', first_name: '', last_name: '', is_active: true, facility_id: null, org_id: isSuperAdmin ? null : (user?.orgId ?? null), department_id: null });
    setUserFormError('');
    setShowUserPassword(false);
    setUserModal({ mode: 'add' });
  }
  function openEditUser(row: UserRow) {
    setUserForm({ username: row.username, email: row.email ?? '', password: '', role: row.role, first_name: row.first_name ?? '', last_name: row.last_name ?? '', is_active: row.is_active, facility_id: row.facility_id ?? null, org_id: row.org_id ?? null, department_id: row.department_id ?? null });
    setUserFormError('');
    setShowUserPassword(false);
    setUserModal({ mode: 'edit', row });
  }
  async function saveUser() {
    setUserFormError('');
    setUserSaving(true);
    try {
      const isAdminEditor = user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'manager';
      if (userModal?.mode === 'add') {
        if (!userForm.username.trim() || !userForm.email.trim() || !userForm.password.trim()) {
          setUserFormError('Username, email, and password are required.'); setUserSaving(false); return;
        }
        const needsFacility = userForm.role === 'manager' || userForm.role === 'viewer' || userForm.role === 'user';
        if (isAdminEditor && needsFacility && !userForm.facility_id) {
          setUserFormError('Please select a facility for this user.'); setUserSaving(false); return;
        }
        await api.post('/settings/users', {
          ...userForm,
          facility_id: needsFacility ? userForm.facility_id : undefined,
          org_id: userForm.org_id ?? undefined,
        });
        await refetchUsers();
      } else if (userModal?.row) {
        const needsFacility = userForm.role === 'manager' || userForm.role === 'viewer' || userForm.role === 'user';
        const body: Record<string, unknown> = {
          email: userForm.email, role: userForm.role, first_name: userForm.first_name,
          last_name: userForm.last_name, is_active: userForm.is_active,
          department_id: userForm.department_id,
          ...(isAdminEditor && needsFacility && userForm.facility_id ? { facility_id: userForm.facility_id } : {}),
          ...(isSuperAdmin ? { org_id: userForm.org_id } : {}),
          ...(isSuperAdmin && userForm.username.trim() ? { username: userForm.username.trim() } : {}),
        };
        await api.put(`/settings/users/${userModal.row.id}`, body);
        if (userForm.password.trim()) {
          await api.put(`/users/${userModal.row.id}/password`, { newPassword: userForm.password });
        }
      }
      qc.invalidateQueries({ queryKey: ['settings-users'] });
      closeUserModal();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setUserFormError(msg ?? 'Save failed.');
    } finally { setUserSaving(false); }
  }
  async function deleteUser(row: UserRow) {
    const deletedId = row.id;
    try {
      await api.delete(`/settings/users/${deletedId}`);
      setUsers(prev => prev.filter(u => u.id !== deletedId));
    } catch { /* ignore */ }
    setDeleteConfirm(null);
  }
  async function handleAssignOrg() {
    if (!assignOrgState?.userId || !assignOrgState?.orgId) return;
    setAssignOrgSaving(true);
    try {
      await api.put(`/settings/users/${assignOrgState.userId}`, { org_id: assignOrgState.orgId });
      qc.invalidateQueries({ queryKey: ['settings-users'] });
      setAssignOrgState(null);
    } catch { /* ignore */ }
    finally { setAssignOrgSaving(false); }
  }

  const { data: usersRes, refetch: refetchUsers } = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => api.get<{ users: UserRow[]; admins: UserRow[] }>('/settings/users'),
    enabled: (isAdmin || isManager) && activeTab === 'users',
  });
  const [users, setUsers] = useState<UserRow[]>([]);
  const [adminUsers, setAdminUsers] = useState<UserRow[]>([]);
  const collapsedInitRef = useRef(false);
  useEffect(() => {
    if (usersRes?.data) {
      const newUsers = usersRes.data.users ?? [];
      setUsers(newUsers);
      setAdminUsers(usersRes.data.admins ?? []);
      if (!collapsedInitRef.current) {
        collapsedInitRef.current = true;
        const ids = new Set<number>();
        newUsers.forEach(u => { if (u.org_id) ids.add(u.org_id); });
        if (newUsers.some(u => !u.org_id)) ids.add(0);
        setCollapsedOrgs(ids);
      }
    }
  }, [usersRes]);
  // visibleUsers excludes admin (backend already filters, but guard for safety)
  const visibleUsers = users.filter(u => u.role !== 'admin');
  const [collapsedOrgs, setCollapsedOrgs] = useState<Set<number>>(new Set());
  const [orgFilters, setOrgFilters] = useState<Record<number, { search: string; facility: number | 'all'; role: string }>>({});
  const getOrgFilter = (orgId: number) => orgFilters[orgId] ?? { search: '', facility: 'all' as const, role: 'all' };
  const setOrgFilter = (orgId: number, patch: Partial<{ search: string; facility: number | 'all'; role: string }>) => {
    setOrgFilters(prev => ({ ...prev, [orgId]: { ...(prev[orgId] ?? { search: '', facility: 'all' as const, role: 'all' }), ...patch } }));
  };

  const { data: catRes, refetch: refetchCats } = useQuery({
    queryKey: ['service-categories'],
    queryFn: () => api.get<ServiceCategory[]>('/vendors/service-categories'),
    enabled: activeTab === 'services',
  });
  const serviceCategories: ServiceCategory[] = catRes?.data ?? [];

  // ── Organization management (admin only) ──────────────────────────────────
  const { data: orgsRes, refetch: refetchOrgs } = useQuery({
    queryKey: ['settings-orgs'],
    queryFn: () => api.get<OrgRow[]>('/organizations'),
    enabled: isSuperAdmin && activeTab === 'organization',
  });
  const orgs: OrgRow[] = (orgsRes as unknown as { data: OrgRow[] })?.data ?? [];

  const [addOrgModal, setAddOrgModal] = useState(false);
  const [addOrgForm, setAddOrgForm] = useState({ name: '', slug: '' });
  const [addOrgError, setAddOrgError] = useState('');
  const [addOrgSaving, setAddOrgSaving] = useState(false);

  const [deleteOrgConfirm, setDeleteOrgConfirm] = useState<OrgRow | null>(null);
  const [deleteOrgPassword, setDeleteOrgPassword] = useState('');
  const [deleteOrgError, setDeleteOrgError] = useState('');
  const [deleteOrgDeleting, setDeleteOrgDeleting] = useState(false);

  const [editOrgModal, setEditOrgModal] = useState<OrgRow | null>(null);
  const [editOrgForm, setEditOrgForm] = useState({ name: '', slug: '', plan_tier: 'standard' });
  const [editOrgError, setEditOrgError] = useState('');
  const [editOrgSaving, setEditOrgSaving] = useState(false);
  const [editPlanTierOpen, setEditPlanTierOpen] = useState(false);
  const PLAN_TIERS = [
    { value: 'standard', label: 'Standard' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  // ── Admin bypass modal (v143) ──────────────────────────────────────────────
  // One modal serves both org-level bypass AND (for Per-Facility-mode orgs) every facility under
  // it -- mirrors the existing org-level-vs-per-facility split the tier resolver already uses
  // (server/src/lib/planTier.ts). billingMode is fetched lazily on open via GET
  // /billing/orgs/:orgId (admin-only, same endpoint the Billing Config panel elsewhere in this
  // file already uses) rather than joined into the org list query -- it's only needed once a
  // specific org's bypass panel is opened, not for every row in the list.
  type DurationPreset = '24h' | '7d' | '30d' | 'custom';
  interface BypassFormState { active: boolean; tier: string; duration: DurationPreset; customDate: string }
  const DEFAULT_BYPASS_FORM: BypassFormState = { active: false, tier: 'standard', duration: '7d', customDate: '' };

  const [bypassModal, setBypassModal] = useState<OrgRow | null>(null);
  const [bypassOrgForm, setBypassOrgForm] = useState<BypassFormState>(DEFAULT_BYPASS_FORM);
  const [bypassBillingMode, setBypassBillingMode] = useState<'centralized' | 'per_facility' | null>(null);
  const [bypassFacilities, setBypassFacilities] = useState<FacilityBypassRow[]>([]);
  const [bypassFacilityForms, setBypassFacilityForms] = useState<Record<number, BypassFormState>>({});
  const [bypassLoading, setBypassLoading] = useState(false);
  const [bypassError, setBypassError] = useState('');
  const [bypassSavingOrg, setBypassSavingOrg] = useState(false);
  const [bypassSavingFacilityId, setBypassSavingFacilityId] = useState<number | null>(null);

  function durationToExpiresAt(duration: DurationPreset, customDate: string): string | null {
    if (duration === 'custom') {
      if (!customDate) return null;
      const d = new Date(customDate);
      return isNaN(d.getTime()) ? null : d.toISOString();
    }
    const hoursByPreset: Record<Exclude<DurationPreset, 'custom'>, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };
    return new Date(Date.now() + hoursByPreset[duration] * 60 * 60 * 1000).toISOString();
  }

  function bypassFormFromRow(row: { bypass_active: boolean; bypass_tier: string | null; bypass_expires_at: string | null }): BypassFormState {
    if (!row.bypass_active || !row.bypass_expires_at) return DEFAULT_BYPASS_FORM;
    return { active: true, tier: row.bypass_tier ?? 'standard', duration: 'custom', customDate: row.bypass_expires_at.slice(0, 16) };
  }

  async function openBypassModal(org: OrgRow) {
    setBypassModal(org);
    setBypassOrgForm(bypassFormFromRow(org));
    setBypassError('');
    setBypassBillingMode(null);
    setBypassFacilities([]);
    setBypassFacilityForms({});
    setBypassLoading(true);
    try {
      const [modeRes, facRes] = await Promise.all([
        api.get<{ billingMode: 'centralized' | 'per_facility' }>(`/billing/orgs/${org.id}`),
        api.get<FacilityBypassRow[]>(`/organizations/${org.id}/facility-bypasses`),
      ]);
      const mode = (modeRes as unknown as { data: { billingMode: 'centralized' | 'per_facility' } }).data.billingMode;
      setBypassBillingMode(mode);
      const facilities = (facRes as unknown as { data: FacilityBypassRow[] }).data ?? [];
      setBypassFacilities(facilities);
      const forms: Record<number, BypassFormState> = {};
      for (const f of facilities) forms[f.facility_id] = bypassFormFromRow(f);
      setBypassFacilityForms(forms);
    } catch {
      // Fail open on the READ side too — if we can't tell the billing mode, just show the
      // org-level control alone rather than blocking the whole panel behind an error.
      setBypassBillingMode('centralized');
    } finally {
      setBypassLoading(false);
    }
  }

  async function saveOrgBypass() {
    if (!bypassModal) return;
    setBypassError('');
    const expiresAt = bypassOrgForm.active ? durationToExpiresAt(bypassOrgForm.duration, bypassOrgForm.customDate) : null;
    if (bypassOrgForm.active && !expiresAt) { setBypassError('Choose a valid expiry.'); return; }
    setBypassSavingOrg(true);
    try {
      await api.put(`/organizations/${bypassModal.id}/bypass`, bypassOrgForm.active
        ? { active: true, tier: bypassOrgForm.tier, expiresAt }
        : { active: false });
      refetchOrgs();
      setBypassModal(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setBypassError(msg ?? 'Failed to save bypass.');
    } finally { setBypassSavingOrg(false); }
  }

  async function saveFacilityBypass(facilityId: number) {
    const form = bypassFacilityForms[facilityId];
    if (!form) return;
    setBypassError('');
    const expiresAt = form.active ? durationToExpiresAt(form.duration, form.customDate) : null;
    if (form.active && !expiresAt) { setBypassError('Choose a valid expiry.'); return; }
    setBypassSavingFacilityId(facilityId);
    try {
      await api.put(`/facilities/${facilityId}/bypass`, form.active
        ? { active: true, tier: form.tier, expiresAt }
        : { active: false });
      setBypassFacilities((rows) => rows.map((r) => r.facility_id === facilityId
        ? { ...r, bypass_active: form.active, bypass_tier: form.active ? form.tier : null, bypass_expires_at: expiresAt }
        : r));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setBypassError(msg ?? 'Failed to save facility bypass.');
    } finally { setBypassSavingFacilityId(null); }
  }

  // Scroll lock: prevent background scroll while any modal is open
  useEffect(() => {
    const anyOpen = !!userModal || !!deleteConfirm || addOrgModal || !!editOrgModal || !!deleteOrgConfirm || !!bypassModal;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [userModal, deleteConfirm, addOrgModal, editOrgModal, deleteOrgConfirm, bypassModal]);

  // Esc-to-close (BUTTON_SYSTEM.md §9 standing pattern) — Bypass Subscription modal only for now,
  // not swept across this file's other modals in this pass (see BUTTON_SYSTEM.md's "not yet
  // migrated" list).
  useEscapeToClose(!!bypassModal, () => setBypassModal(null));

  function slugify(val: string) {
    return val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  async function saveOrg() {
    setAddOrgError('');
    if (!addOrgForm.name.trim() || !addOrgForm.slug.trim()) {
      setAddOrgError('Name and slug are required.'); return;
    }
    setAddOrgSaving(true);
    try {
      await api.post('/organizations', { name: addOrgForm.name.trim(), slug: addOrgForm.slug.trim() });
      setAddOrgModal(false);
      setAddOrgForm({ name: '', slug: '' });
      refetchOrgs();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setAddOrgError(msg ?? 'Failed to create organization.');
    } finally { setAddOrgSaving(false); }
  }

  async function saveEditOrg() {
    if (!editOrgModal) return;
    setEditOrgError('');
    if (!editOrgForm.name.trim() || !editOrgForm.slug.trim()) {
      setEditOrgError('Name and slug are required.'); return;
    }
    setEditOrgSaving(true);
    try {
      await api.patch(`/organizations/${editOrgModal.id}`, {
        name: editOrgForm.name.trim(),
        slug: editOrgForm.slug.trim(),
        plan_tier: editOrgForm.plan_tier,
      });
      setEditOrgModal(null);
      refetchOrgs();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setEditOrgError(msg ?? 'Failed to update organization.');
    } finally { setEditOrgSaving(false); }
  }

  async function confirmDeleteOrg() {
    if (!deleteOrgConfirm) return;
    setDeleteOrgError('');
    if (!deleteOrgPassword) { setDeleteOrgError('Password is required.'); return; }
    setDeleteOrgDeleting(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/organizations/${deleteOrgConfirm.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ password: deleteOrgPassword }),
      });
      setDeleteOrgConfirm(null);
      setDeleteOrgPassword('');
      refetchOrgs();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setDeleteOrgError(msg ?? 'Deletion failed.');
    } finally { setDeleteOrgDeleting(false); }
  }

  const tabs: Array<{ id: SettingsTab; label: string; icon: React.ReactNode; adminOnly?: boolean; superAdminOnly?: boolean; managerVisible?: boolean; viewerHidden?: boolean }> = [
    { id: 'profile', label: 'Profile', icon: <User size={15} className="btn-icon" /> },
    { id: 'users', label: 'Users', icon: <Shield size={15} className="btn-icon" />, adminOnly: true, managerVisible: true },
    { id: 'organization', label: 'Organization', icon: <Globe size={15} className="btn-icon" />, superAdminOnly: true },
    { id: 'facility', label: 'Facility', icon: <Building2 size={15} className="btn-icon" /> },
    { id: 'appearance', label: 'Appearance', icon: <Sun size={15} className="btn-icon" /> },
    { id: 'services', label: 'Services', icon: <Settings2 size={15} className="btn-icon" /> },
    { id: 'import-export', label: 'Import / Export', icon: <Database size={15} className="btn-icon" />, viewerHidden: true },
    { id: 'dropdowns', label: 'Dropdowns', icon: <Tag size={15} className="btn-icon" /> },
    { id: 'orders', label: 'Orders', icon: <Tag size={15} className="btn-icon" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={15} className="btn-icon" /> },
    { id: 'financials', label: 'Financials', icon: <DollarSign size={15} className="btn-icon" /> },
    // adminOnly (not superAdminOnly) so org_admin can still reach the Template Builder tab inside
    // Development — Dev Todos/Database/Used Emails stay gated to isSuperAdmin further down,
    // inside DevelopmentTab itself, preserving their original superadmin-only boundary.
    { id: 'dev-todos', label: 'Development', icon: <Database size={16} className="btn-icon" />, adminOnly: true },
  ];

  function displayRole(role: string) {
    switch (role) {
      case 'admin': return 'Admin';
      case 'org_admin': return 'Org Admin';
      case 'manager': return 'Manager';
      case 'user': return 'User';
      case 'viewer': return 'Viewer (Demo)';
      default: return role;
    }
  }

  const ROLE_BADGE_STYLES: Record<string, React.CSSProperties> = {
    admin:     { background: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
    org_admin: { background: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    manager:   { background: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
    user:      { background: 'rgba(16,185,129,0.15)', color: '#10b981' },
    viewer:    { background: 'rgba(139,92,246,0.15)', color: '#8b5cf6' },
  };
  const ROLE_BADGE_BASE: React.CSSProperties = {
    fontSize: 9, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
    display: 'inline-block', whiteSpace: 'nowrap',
  };

  const modalLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
    letterSpacing: '0.07em', color: 'rgba(255,255,255,0.45)', marginBottom: 6,
  };
  const modalInputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.045)',
    color: '#f9fafb', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Settings</h2>
          <p className="page-subtitle">Manage your account and facility configuration</p>
        </div>
      </div>

      {/* Left sidebar nav items are custom-styled (not the shared .btn/combo classes), so the
          standard icon wiggle needs its own scoped hover hook here — same pattern Sidebar.css
          uses for the app's main left nav (.sidebar-nav-item:hover .btn-icon), just page-local
          since this sidebar has no dedicated CSS file. */}
      <style>{`.settings-nav-item:hover .btn-icon { animation: iconWiggle 0.45s ease; }`}</style>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Sidebar tabs */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <div className="card" style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tabs.filter((t) => (!t.adminOnly || isAdmin || (t.managerVisible && isManager)) && (!t.superAdminOnly || isSuperAdmin) && (!t.viewerHidden || user?.role !== 'viewer')).map((tab) => (
              <button
                key={tab.id}
                className="settings-nav-item"
                onClick={() => handleSetActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px', borderRadius: 'var(--radius-sm)',
                  background: activeTab === tab.id ? 'rgba(59,130,246,0.12)' : 'none',
                  border: 'none',
                  color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
                  textAlign: 'left', transition: 'all var(--transition)',
                }}
                onMouseEnter={(e) => { if (activeTab !== tab.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { if (activeTab !== tab.id) (e.currentTarget as HTMLElement).style.background = 'none'; spinIconOut(e); }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Profile */}
          {activeTab === 'profile' && (
            <div className="card">
              <h3 style={{ marginBottom: 20 }}>My Profile</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 480 }}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" value={profileForm.firstName} onChange={(e) => setProfileForm((f) => ({ ...f, firstName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" value={profileForm.lastName} onChange={(e) => setProfileForm((f) => ({ ...f, lastName: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={profileForm.email} onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Username</label>
                  <input className="form-input" value={user?.username ?? ''} disabled style={{ opacity: 0.6 }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Role</label>
                  <div>
                    <span style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: 99, display: 'inline-block', fontWeight: 600, whiteSpace: 'nowrap', ...(ROLE_BADGE_STYLES[user?.role ?? 'viewer'] ?? {}) }}>
                      {displayRole(user?.role ?? '')}
                    </span>
                  </div>
                </div>
              </div>
              {profileError && <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{profileError}</div>}
              {profileSaved && <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(34,197,94,0.1)', color: '#16a34a', fontSize: '0.875rem' }}>Profile updated</div>}
              <div style={{ marginTop: 20 }}>
                <button className="btn btn-primary" onClick={handleSaveProfile} disabled={profileSaving}>{profileSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>

              <hr className="divider" />

              <h4 style={{ marginBottom: 16 }}>Change Password</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 360 }}>
                {pwError && <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{pwError}</div>}
                {pwSuccess && <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(34,197,94,0.1)', color: '#16a34a', fontSize: '0.875rem' }}>Password updated successfully.</div>}
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input className="form-input" type={showCurrentPw ? 'text' : 'password'} placeholder="Enter current password" autoComplete="current-password" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} style={{ width: '100%', paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowCurrentPw((p) => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center' }}>
                      {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input className="form-input" type={showNewPw ? 'text' : 'password'} placeholder="At least 8 characters" autoComplete="new-password" value={pwForm.newPw} onChange={(e) => setPwForm((f) => ({ ...f, newPw: e.target.value }))} style={{ width: '100%', paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowNewPw((p) => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center' }}>
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {pwForm.newPw && (() => {
                  const pw = pwForm.newPw;
                  let score = 0;
                  if (pw.length >= 8) score++;
                  if (/[A-Z]/.test(pw)) score++;
                  if (/[0-9]/.test(pw)) score++;
                  if (/[^A-Za-z0-9]/.test(pw)) score++;
                  const strengthColors = ['', '#ef4444', '#f59e0b', '#10b981', '#10b981'];
                  const strengthLabels = ['', 'Weak', 'Fair', 'Strong', 'Very Strong'];
                  const reqs = [
                    { label: 'At least 8 characters', met: pw.length >= 8 },
                    { label: 'One uppercase letter', met: /[A-Z]/.test(pw) },
                    { label: 'One number', met: /[0-9]/.test(pw) },
                    { label: 'One special character', met: /[^A-Za-z0-9]/.test(pw) },
                  ];
                  return (
                    <div style={{ marginTop: -4 }}>
                      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
                        {[1, 2, 3, 4].map((n) => (
                          <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: n <= score ? strengthColors[score] : 'var(--border-default)', transition: 'background 0.2s' }} />
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                          {reqs.map((r) => (
                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                              {r.met ? <CheckCircle size={11} color="var(--accent-success)" /> : <XCircle size={11} color="var(--text-muted)" />}
                              <span style={{ color: r.met ? 'var(--accent-success)' : 'var(--text-muted)' }}>{r.label}</span>
                            </div>
                          ))}
                        </div>
                        <span style={{ fontSize: 10, color: strengthColors[score], flexShrink: 0, marginTop: 1 }}>{strengthLabels[score]}</span>
                      </div>
                    </div>
                  );
                })()}
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input className="form-input" type={showConfirmPw ? 'text' : 'password'} placeholder="Confirm new password" autoComplete="new-password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} style={{ width: '100%', paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowConfirmPw((p) => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center' }}>
                      {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ width: 'fit-content' }} onClick={changeOwnPassword} disabled={pwSaving}>{pwSaving ? 'Updating…' : 'Update Password'}</button>
              </div>
            </div>
          )}

          {/* Users (admin only) */}
          {activeTab === 'users' && (isAdmin || isManager) && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3>User Management</h3>
                {/* admin/org_admin can add any role; manager can only add viewer */}
                <button className="btn btn-primary btn-sm" onClick={openAddUser}>
                  <Plus size={14} /> Add User
                </button>
              </div>

              {/* org_admin / manager: flat table */}
              {!isSuperAdmin && (
                <div className="table-container">
                  <table>
                    <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Facility</th><th>Status</th><th>Last Login</th><th></th></tr></thead>
                    <tbody>
                      {visibleUsers.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.username}</div>
                          </td>
                          <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                          <td>
                            <span style={{ ...ROLE_BADGE_BASE, ...(ROLE_BADGE_STYLES[u.role] ?? ROLE_BADGE_STYLES.viewer) }}>{displayRole(u.role)}</span>
                            {u.department_name && (
                              <span style={{ marginLeft: 4, fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600, background: (u.department_color || '#6b7280') + '22', border: `1px solid ${u.department_color || '#6b7280'}66`, color: u.department_color || '#6b7280', whiteSpace: 'nowrap', display: 'inline-block' }}>
                                {u.department_name}
                              </span>
                            )}
                          </td>
                          <td style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                            {(u.role === 'admin' || u.role === 'org_admin') ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>All Facilities</span> : (u.facility_name ?? '—')}
                          </td>
                          <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                          <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                          <td>
                            {u.role !== 'admin' && canManageUsers && (
                              <button className="btn btn-ghost btn-sm" onClick={() => openEditUser(u)}><Pencil size={13} /></button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* admin: grouped by org, unassigned section at top */}
              {isSuperAdmin && (() => {
                const unassigned = visibleUsers.filter(u => !u.org_id);
                const assigned = visibleUsers.filter(u => !!u.org_id);
                const byOrg = assigned.reduce((acc, u) => {
                  const key = u.org_id!;
                  if (!acc[key]) acc[key] = { org_name: u.org_name ?? 'Unknown Org', users: [] };
                  acc[key].users.push(u);
                  return acc;
                }, {} as Record<number, { org_name: string; users: UserRow[] }>);

                const UserTable = ({ orgUsers, orgKey }: { orgUsers: UserRow[]; orgKey: number }) => {
                  const stickyTh: React.CSSProperties = { position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 1 };
                  return (
                  <div className="table-container" style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={stickyTh}>User</th>
                          <th style={stickyTh}>Email</th>
                          <th style={stickyTh}>Role</th>
                          <th style={stickyTh}>Facility</th>
                          <th style={stickyTh}>Status</th>
                          <th style={stickyTh}>Last Login</th>
                          <th style={stickyTh}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgUsers.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <div style={{ fontWeight: 500 }}>{u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.username}</div>
                            </td>
                            <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                            <td>
                              <span style={{ ...ROLE_BADGE_BASE, ...(ROLE_BADGE_STYLES[u.role] ?? ROLE_BADGE_STYLES.viewer) }}>{displayRole(u.role)}</span>
                              {u.department_name && (
                                <span style={{ marginLeft: 4, fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600, background: (u.department_color || '#6b7280') + '22', border: `1px solid ${u.department_color || '#6b7280'}66`, color: u.department_color || '#6b7280', whiteSpace: 'nowrap', display: 'inline-block' }}>
                                  {u.department_name}
                                </span>
                              )}
                            </td>
                            <td style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                              {(u.role === 'admin' || u.role === 'org_admin') ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>All Facilities</span> : (u.facility_name ?? '—')}
                            </td>
                            <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                            <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                {/* Assign to Org button — only for unassigned users */}
                                {orgKey === 0 && (
                                  assignOrgState?.userId === u.id ? (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                      <select
                                        className="form-input"
                                        style={{ padding: '3px 6px', fontSize: '0.75rem', height: 28 }}
                                        value={assignOrgState.orgId}
                                        onChange={(e) => setAssignOrgState({ userId: u.id, orgId: Number(e.target.value) })}
                                      >
                                        <option value={0}>— Select Org —</option>
                                        {userModalOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                                      </select>
                                      <button className="btn btn-primary btn-sm" style={{ fontSize: '0.72rem', padding: '3px 8px' }} disabled={!assignOrgState.orgId || assignOrgSaving} onClick={handleAssignOrg}>
                                        {assignOrgSaving ? '…' : 'Save'}
                                      </button>
                                      <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px' }} onClick={() => setAssignOrgState(null)}><X size={12} /></button>
                                    </div>
                                  ) : (
                                    <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', color: '#f59e0b' }} onClick={() => setAssignOrgState({ userId: u.id, orgId: 0 })}>
                                      Assign Org
                                    </button>
                                  )
                                )}
                                <button className="btn btn-ghost btn-sm" onClick={() => openEditUser(u)}><Pencil size={13} /></button>
                                {u.id !== 1 && u.role !== 'admin' && u.id !== user?.id && (
                                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={() => setDeleteConfirm(u)}><Trash2 size={13} /></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  );
                };

                return (
                  <>
                    {/* Developer section — admin users only */}
                    {adminUsers.length > 0 && (() => {
                      return (
                        <div style={{ marginBottom: 16, border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, overflow: 'hidden' }}>
                          <div
                            style={{ background: 'rgba(239,68,68,0.08)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(239,68,68,0.15)' }}
                          >
                            <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 14 }}>⚙ Developer</span>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{adminUsers.length} user{adminUsers.length > 1 ? 's' : ''}</span>
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Facility</th><th>Status</th><th>Last Login</th><th></th></tr></thead>
                              <tbody>
                                {adminUsers.map((u) => (
                                  <tr key={u.id}>
                                    <td>
                                      <div style={{ fontWeight: 500 }}>{u.first_name ? `${u.first_name} ${u.last_name ?? ''}`.trim() : u.username}</div>
                                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.username}</div>
                                    </td>
                                    <td style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                                    <td><span style={{ ...ROLE_BADGE_BASE, ...ROLE_BADGE_STYLES.admin }}>{displayRole(u.role)}</span></td>
                                    <td style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.8125rem' }}>All Facilities</td>
                                    <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                                    <td>
                                      <button className="btn btn-ghost btn-sm" onClick={() => openEditUser(u)}><Pencil size={13} /></button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                        </div>
                      );
                    })()}

                    {/* Unassigned section */}
                    {unassigned.length > 0 && (() => {
                      const unassignedIsOpen = !collapsedOrgs.has(0);
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div
                            onClick={() => setCollapsedOrgs((prev) => { const next = new Set(prev); if (next.has(0)) next.delete(0); else next.add(0); return next; })}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'rgba(245,158,11,0.08)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', marginBottom: 4, border: '1px solid rgba(245,158,11,0.3)', userSelect: 'none' }}
                          >
                            <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f59e0b', flex: 1 }}>⚠ Unassigned</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{unassigned.length} user{unassigned.length !== 1 ? 's' : ''} — assign to an org</span>
                            <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: unassignedIsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 200ms ease' }} />
                          </div>
                          {unassignedIsOpen && <UserTable orgUsers={unassigned} orgKey={0} />}
                        </div>
                      );
                    })()}

                    {/* Assigned orgs */}
                    {Object.entries(byOrg).map(([orgId, { org_name, users: orgUsers }]) => {
                      const numOrgId = Number(orgId);
                      const orgIsOpen = !collapsedOrgs.has(numOrgId);
                      const filter = getOrgFilter(numOrgId);
                      const orgFacilityOptions = Array.from(
                        new Map(orgUsers.filter(u => u.facility_id && u.facility_name).map(u => [u.facility_id!, u.facility_name!]))
                      ).sort(([, a], [, b]) => a.localeCompare(b));
                      const filterActive = filter.search.trim() !== '' || filter.facility !== 'all' || filter.role !== 'all';
                      const filteredOrgUsers = orgUsers.filter(u => {
                        if (filter.role !== 'all' && u.role !== filter.role) return false;
                        if (filter.facility !== 'all' && (u.role === 'org_admin' || u.facility_id !== filter.facility)) return false;
                        if (filter.search.trim()) {
                          const q = filter.search.toLowerCase();
                          const fullName = `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase().trim();
                          if (!fullName.includes(q) && !u.username.toLowerCase().includes(q) && !(u.email ?? '').toLowerCase().includes(q)) return false;
                        }
                        return true;
                      });
                      return (
                        <div key={orgId} style={{ marginBottom: 16 }}>
                          <div
                            onClick={() => setCollapsedOrgs((prev) => { const next = new Set(prev); if (next.has(numOrgId)) next.delete(numOrgId); else next.add(numOrgId); return next; })}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'rgba(59,130,246,0.08)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', marginBottom: 4, border: '1px solid rgba(59,130,246,0.2)', userSelect: 'none' }}
                          >
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#3b82f6', flex: 1 }}>{org_name}</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{orgUsers.length} user{orgUsers.length !== 1 ? 's' : ''}</span>
                            <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: orgIsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 200ms ease' }} />
                          </div>
                          {orgIsOpen && (
                            <>
                              {/* LOCKED: filter bar layout — single row, inline styles only, do not modify */}
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border-subtle)' }}>
                                <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                                  <Search size={13} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }} />
                                  <input
                                    type="text"
                                    placeholder="Search name, username, or email…"
                                    value={filter.search}
                                    onChange={(e) => setOrgFilter(numOrgId, { search: e.target.value })}
                                    style={{ height: 32, width: '100%', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, paddingLeft: 30, paddingRight: filter.search ? 28 : 10, outline: 'none', boxSizing: 'border-box' }}
                                  />
                                  {filter.search && (
                                    <button style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center' }} onClick={() => setOrgFilter(numOrgId, { search: '' })}>
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                                {orgFacilityOptions.length > 1 && (
                                  <select
                                    style={{ height: 32, width: 200, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, padding: '0 10px', outline: 'none', cursor: 'pointer', flexShrink: 0 }}
                                    value={filter.facility === 'all' ? 'all' : String(filter.facility)}
                                    onChange={(e) => setOrgFilter(numOrgId, { facility: e.target.value === 'all' ? 'all' : Number(e.target.value) })}
                                  >
                                    <option value="all">All Facilities</option>
                                    {orgFacilityOptions.map(([id, name]) => (
                                      <option key={id} value={id}>{name}</option>
                                    ))}
                                  </select>
                                )}
                                <select
                                  style={{ height: 32, width: 140, background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, padding: '0 10px', outline: 'none', cursor: 'pointer', flexShrink: 0 }}
                                  value={filter.role}
                                  onChange={(e) => setOrgFilter(numOrgId, { role: e.target.value })}
                                >
                                  <option value="all">All Roles</option>
                                  <option value="org_admin">Org Admin</option>
                                  <option value="manager">Manager</option>
                                  <option value="user">User</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                                {filterActive && (
                                  <button
                                    onClick={() => setOrgFilter(numOrgId, { search: '', facility: 'all', role: 'all' })}
                                    style={{ height: 32, background: 'none', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}
                                  >
                                    <X size={12} /> Clear
                                  </button>
                                )}
                              </div>
                              {filteredOrgUsers.length === 0 ? (
                                <div style={{ padding: '12px 0', color: 'var(--text-muted)', fontSize: '0.8125rem', textAlign: 'center' }}>No users match filters</div>
                              ) : (
                                <UserTable orgUsers={filteredOrgUsers} orgKey={numOrgId} />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          )}

          {/* User Add/Edit Modal */}
          {userModal && ReactDOM.createPortal(
            <div
              className="modal-overlay"
              onMouseDown={(e) => { mouseDownOnOverlay.current = e.target === e.currentTarget; }}
              onMouseUp={(e) => { if (e.target === e.currentTarget && mouseDownOnOverlay.current) closeUserModal(); mouseDownOnOverlay.current = false; }}
            >
              <div
                className="modal"
                style={{ maxWidth: 500, width: '100%', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 500, color: '#f9fafb' }}>
                    {userModal.mode === 'add' ? 'Add User' : `Edit ${userModal.row?.first_name ? `${userModal.row.first_name} ${userModal.row.last_name ?? ''}`.trim() : userModal.row?.username ?? ''}`}
                  </span>
                  <button
                    onClick={closeUserModal}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {userFormError && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: 13 }}>
                      {userFormError}
                    </div>
                  )}

                  {/* Username */}
                  <div>
                    <label style={modalLabelStyle}>Username</label>
                    <input
                      style={userModal.mode === 'edit' ? { ...modalInputStyle, color: 'rgba(255,255,255,0.4)', cursor: 'default' } : modalInputStyle}
                      value={userForm.username}
                      onChange={(e) => { if (userModal.mode === 'add') setUserForm((f) => ({ ...f, username: e.target.value })); }}
                      readOnly={userModal.mode === 'edit'}
                      placeholder={userModal.mode === 'add' ? 'johndoe' : ''}
                      autoComplete="off"
                    />
                  </div>

                  {/* First + Last Name */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={modalLabelStyle}>First Name</label>
                      <input style={modalInputStyle} value={userForm.first_name} onChange={(e) => setUserForm((f) => ({ ...f, first_name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={modalLabelStyle}>Last Name</label>
                      <input style={modalInputStyle} value={userForm.last_name} onChange={(e) => setUserForm((f) => ({ ...f, last_name: e.target.value }))} />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label style={modalLabelStyle}>Email *</label>
                    <input style={modalInputStyle} type="email" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} />
                  </div>

                  {/* Password */}
                  <div>
                    <label style={modalLabelStyle}>{userModal.mode === 'add' ? 'Password *' : 'New Password (leave blank to keep)'}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        style={{ ...modalInputStyle, paddingRight: 40 }}
                        type={showUserPassword ? 'text' : 'password'}
                        value={userForm.password}
                        onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder={userModal.mode === 'edit' ? '••••••••' : ''}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowUserPassword((s) => !s)}
                        tabIndex={-1}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 4, display: 'flex', alignItems: 'center' }}
                      >
                        {showUserPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Role + Status */}
                  <div style={{ display: 'grid', gridTemplateColumns: userModal.mode === 'edit' ? '1fr 1fr' : '1fr', gap: 12 }}>
                    <div>
                      <label style={modalLabelStyle}>Role</label>
                      <DarkSelect
                        value={userForm.role}
                        onChange={(v) => setUserForm((f) => ({ ...f, role: v, facility_id: (v === 'admin' || v === 'org_admin') ? null : f.facility_id }))}
                        options={[
                          { value: 'user', label: 'User' },
                          { value: 'viewer', label: 'Viewer (Demo)' },
                          ...(!isManager ? [{ value: 'manager', label: 'Manager' }] : []),
                          ...(isAdmin ? [{ value: 'org_admin', label: 'Org Admin' }] : []),
                          ...(isSuperAdmin ? [{ value: 'admin', label: 'Admin' }] : []),
                        ]}
                      />
                    </div>
                    {userModal.mode === 'edit' && (
                      <div>
                        <label style={modalLabelStyle}>Status</label>
                        <DarkSelect
                          value={userForm.is_active ? 'active' : 'inactive'}
                          onChange={(v) => setUserForm((f) => ({ ...f, is_active: v === 'active' }))}
                          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                        />
                      </div>
                    )}
                  </div>

                  {/* Organization — superadmin only */}
                  {isSuperAdmin && (
                    <div>
                      <label style={modalLabelStyle}>Organization</label>
                      <DarkSelect
                        value={String(userForm.org_id ?? '')}
                        onChange={(v) => setUserForm((f) => ({ ...f, org_id: v ? Number(v) : null, facility_id: null, department_id: null }))}
                        options={[{ value: '', label: '— Select Organization —' }, ...userModalOrgs.map((org) => ({ value: String(org.id), label: org.name }))]}
                      />
                    </div>
                  )}

                  {/* Facility */}
                  {(isAdmin || isManager) && (userForm.role === 'manager' || userForm.role === 'viewer' || userForm.role === 'user') && (
                    <div>
                      <label style={modalLabelStyle}>Facility *</label>
                      <div style={isSuperAdmin && !userForm.org_id ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                        <DarkSelect
                          value={String(userForm.facility_id ?? '')}
                          onChange={(v) => setUserForm((f) => ({ ...f, facility_id: v ? Number(v) : null }))}
                          placeholder={isSuperAdmin && !userForm.org_id ? 'Select an organization first' : 'Select facility…'}
                          options={orgFacilities.map((fac) => ({ value: String(fac.id), label: fac.name }))}
                        />
                      </div>
                    </div>
                  )}

                  {/* Department */}
                  <div>
                    <label style={modalLabelStyle}>Department</label>
                    <DarkSelect
                      value={String(userForm.department_id ?? '')}
                      onChange={(v) => setUserForm((f) => ({ ...f, department_id: v ? Number(v) : null }))}
                      placeholder="No department"
                      options={(isSuperAdmin && userForm.org_id
                        ? departments.filter((d) => d.org_id === userForm.org_id)
                        : departments
                      ).map((d) => ({ value: String(d.id), label: d.name }))}
                    />
                  </div>
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {isSuperAdmin && userModal.mode === 'edit' && userModal.row && userModal.row.id !== 1 && userModal.row.id !== user?.id && (
                      <button
                        style={{ background: 'none', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', color: '#f87171', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}
                        onClick={() => { setDeleteConfirm(userModal.row!); closeUserModal(); }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}
                      onClick={closeUserModal}
                    >
                      Cancel
                    </button>
                    <button
                      style={{ background: '#3b82f6', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: userSaving ? 'default' : 'pointer', color: '#fff', fontSize: 13, fontWeight: 500, opacity: userSaving ? 0.7 : 1 }}
                      onClick={saveUser}
                      disabled={userSaving}
                    >
                      {userSaving ? 'Saving…' : userModal.mode === 'add' ? 'Create User' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Delete confirm */}
          {deleteConfirm && ReactDOM.createPortal(
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: 380, width: '95vw' }}>
                <h3 style={{ marginBottom: 12 }}>Delete User?</h3>
                <p style={{ marginBottom: 24, color: 'var(--text-secondary)' }}>
                  Are you sure you want to delete <strong>{deleteConfirm.username}</strong>? This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  <button className="btn btn-danger" onClick={() => deleteUser(deleteConfirm)}>Delete</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Appearance */}
          {activeTab === 'appearance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <h3 style={{ marginBottom: 8 }}>Appearance</h3>
              <p style={{ marginBottom: 24, fontSize: '0.875rem' }}>
                Choose how ProQure looks. Your preference is saved automatically.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 560 }}>
                {([
                  {
                    id: 'dark', label: 'Dark', icon: <Moon size={22} />,
                    preview: ['#0d1117', '#161b27', '#1e2536'],
                    desc: 'Deep navy palette (default)',
                  },
                  {
                    id: 'light', label: 'Light', icon: <Sun size={22} />,
                    preview: ['#f8fafc', '#f1f5f9', '#ffffff'],
                    desc: 'White / slate palette',
                  },
                  {
                    id: 'system', label: 'System', icon: <Monitor size={22} />,
                    preview: ['#374151', '#6b7280', '#9ca3af'],
                    desc: 'Follows OS preference',
                  },
                ] as const).map((opt) => {
                  const active = theme === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setTheme(opt.id)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                        padding: 16, borderRadius: 'var(--radius-md)', cursor: 'pointer',
                        border: active ? '2px solid var(--accent-primary)' : '1px solid var(--border-default)',
                        background: active ? 'rgba(59,130,246,0.08)' : 'var(--bg-elevated)',
                        color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        transition: 'all var(--transition)',
                      }}
                    >
                      {/* Color swatch */}
                      <div style={{ display: 'flex', gap: 3, borderRadius: 6, overflow: 'hidden' }}>
                        {opt.preview.map((c, i) => (
                          <div key={i} style={{ width: 22, height: 28, background: c }} />
                        ))}
                      </div>
                      {opt.icon}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{opt.label}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                      {active && (
                        <span style={{
                          fontSize: '0.75rem', fontWeight: 600,
                          color: 'var(--accent-primary)', marginTop: -4,
                        }}>✓ Active</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <WeatherSettingsCard />
            </div>
          )}

          {/* Import / Export — hidden for viewer */}
          {activeTab === 'import-export' && user?.role !== 'viewer' && <ImportExportTab />}

          {/* Vendor Service Categories */}
          {activeTab === 'services' && (
            <ServiceCategoriesTab
              categories={serviceCategories}
              onChanged={() => {
                qc.invalidateQueries({ queryKey: ['service-categories'] });
                refetchCats();
              }}
            />
          )}

          {/* Dropdowns */}
          {activeTab === 'dropdowns' && <DropdownsTab />}

          {/* Facility */}
          {activeTab === 'facility' && <FacilityTab />}

          {/* Orders */}
          {activeTab === 'orders' && <OrdersSettingsTab />}

          {/* Financials */}
          {activeTab === 'financials' && <FinancialsTab />}

          {/* Organization Management (admin only) */}
          {activeTab === 'organization' && isSuperAdmin && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3>Organization Management</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setAddOrgForm({ name: '', slug: '' }); setAddOrgError(''); setAddOrgModal(true); }}>
                  <Plus size={14} /> Add Organization
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {orgs.map((org) => (
                  <div key={org.id} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '14px 16px', background: 'var(--bg-elevated)',
                    borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{org.name}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        slug: <code style={{ fontFamily: 'monospace', color: 'var(--accent-primary)' }}>{org.slug}</code>
                        <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
                        {org.plan_tier}
                      </div>
                      {org.bypass_active && org.bypass_expires_at && new Date(org.bypass_expires_at).getTime() > Date.now() && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
                          padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 500,
                          background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)',
                        }}>
                          Org bypass ({org.bypass_tier}) until {new Date(org.bypass_expires_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 20, fontSize: '0.8125rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{org.facility_count}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Facilities</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{org.user_count}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Users</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Bypass Subscription"
                        onClick={() => openBypassModal(org)}
                      >
                        <Lock size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setEditOrgModal(org); setEditOrgForm({ name: org.name, slug: org.slug, plan_tier: org.plan_tier }); setEditOrgError(''); setEditPlanTierOpen(false); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--accent-danger)' }}
                        onClick={() => { setDeleteOrgConfirm(org); setDeleteOrgPassword(''); setDeleteOrgError(''); }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {orgs.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No organizations found.</div>
                )}
              </div>
            </div>
          )}

          {/* Add Org Modal */}
          {addOrgModal && ReactDOM.createPortal(
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: 440, width: '95vw' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>Add Organization</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddOrgModal(false)}><X size={16} /></button>
                </div>
                {addOrgError && (
                  <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{addOrgError}</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Organization Name *</label>
                    <input
                      className="form-input"
                      value={addOrgForm.name}
                      onChange={(e) => setAddOrgForm((f) => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
                      placeholder="Acme Healthcare"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Slug (auto-generated)</label>
                    <input
                      className="form-input"
                      value={addOrgForm.slug}
                      readOnly
                      disabled
                      style={{ opacity: 0.65, cursor: 'not-allowed' }}
                      placeholder="acme-healthcare"
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                      A default facility "{addOrgForm.name || 'Org Name'} Main Facility" will be created automatically.
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button className="btn btn-ghost" onClick={() => setAddOrgModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveOrg} disabled={addOrgSaving}>
                    {addOrgSaving ? 'Creating…' : 'Create Organization'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Edit Org Modal */}
          {editOrgModal && ReactDOM.createPortal(
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: 440, width: '95vw', overflow: 'visible' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>Edit Organization</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditOrgModal(null)}><X size={16} /></button>
                </div>
                {editOrgError && (
                  <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{editOrgError}</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Organization Name *</label>
                    <input
                      className="form-input"
                      value={editOrgForm.name}
                      onChange={(e) => setEditOrgForm((f) => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Slug (auto-generated)</label>
                    <input
                      className="form-input"
                      value={editOrgForm.slug}
                      readOnly
                      disabled
                      style={{ opacity: 0.65, cursor: 'not-allowed' }}
                    />
                  </div>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label">Plan Tier</label>
                    <div style={{ position: 'relative' }}>
                      <div
                        onClick={() => setEditPlanTierOpen((o) => !o)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '9px 12px', border: '1px solid var(--border-default)',
                          borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)',
                          color: 'var(--text-primary)', fontSize: '0.875rem', cursor: 'pointer', userSelect: 'none',
                        }}
                      >
                        <span>{PLAN_TIERS.find(t => t.value === editOrgForm.plan_tier)?.label ?? 'Standard'}</span>
                        <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                      </div>
                      {editPlanTierOpen && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 9999,
                          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-elevated)', boxShadow: 'var(--shadow-md)', overflow: 'hidden',
                        }}>
                          {PLAN_TIERS.map((t) => (
                            <div
                              key={t.value}
                              onClick={() => { setEditOrgForm((f) => ({ ...f, plan_tier: t.value })); setEditPlanTierOpen(false); }}
                              style={{
                                padding: '9px 12px', cursor: 'pointer', fontSize: '0.875rem',
                                background: t.value === editOrgForm.plan_tier ? 'var(--bg-hover)' : 'transparent',
                                color: 'var(--text-primary)',
                                borderBottom: '1px solid var(--border-subtle)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              }}
                              onMouseEnter={(e) => { if (t.value !== editOrgForm.plan_tier) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                              onMouseLeave={(e) => { if (t.value !== editOrgForm.plan_tier) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                            >
                              {t.label}
                              {t.value === editOrgForm.plan_tier && <span style={{ fontSize: '0.75rem', color: 'var(--accent-primary)' }}>✓</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button className="btn btn-ghost" onClick={() => setEditOrgModal(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveEditOrg} disabled={editOrgSaving}>
                    {editOrgSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Bypass Subscription Modal (v143) — pinned header/footer + Esc-to-close (BUTTON_SYSTEM.md §9) */}
          {bypassModal && ReactDOM.createPortal(
            <div className="modal-overlay">
              <div className="modal modal-pinned" style={{ maxWidth: 520, width: '95vw', maxHeight: '85vh' }}>
                <div className="modal-pinned-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Bypass Subscription — {bypassModal.name}</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setBypassModal(null)}><X size={16} /></button>
                </div>
                <div className="modal-pinned-body">
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 16 }}>
                  Grants access without an active Stripe subscription. Access-only — never touches this org's data.
                  A real Stripe subscription always wins over any bypass state.
                </p>
                {bypassError && (
                  <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{bypassError}</div>
                )}

                <div style={{ padding: 14, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 10 }}>Organization-Wide Bypass</div>
                  <BypassFieldsEditor form={bypassOrgForm} onChange={setBypassOrgForm} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveOrgBypass} disabled={bypassSavingOrg}>
                      {bypassSavingOrg ? 'Saving…' : 'Save Org Bypass'}
                    </button>
                  </div>
                </div>

                {bypassLoading && (
                  <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading facilities…</div>
                )}

                {!bypassLoading && bypassBillingMode === 'per_facility' && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 10 }}>
                      Per-Facility Bypass <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— this org is in Per-Facility mode</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {bypassFacilities.map((fac) => {
                        const form = bypassFacilityForms[fac.facility_id] ?? DEFAULT_BYPASS_FORM;
                        return (
                          <div key={fac.facility_id} style={{ padding: 14, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>{fac.name}</div>
                            <BypassFieldsEditor
                              form={form}
                              onChange={(next) => setBypassFacilityForms((prev) => ({ ...prev, [fac.facility_id]: next }))}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => saveFacilityBypass(fac.facility_id)}
                                disabled={bypassSavingFacilityId === fac.facility_id}
                              >
                                {bypassSavingFacilityId === fac.facility_id ? 'Saving…' : `Save ${fac.name} Bypass`}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {bypassFacilities.length === 0 && (
                        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No facilities found.</div>
                      )}
                    </div>
                  </div>
                )}
                </div>
                <div className="modal-pinned-footer">
                  <button className="btn btn-ghost combo-cancel-jello" onClick={() => setBypassModal(null)}>Close</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Delete Org Confirmation Modal */}
          {deleteOrgConfirm && ReactDOM.createPortal(
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: 420, width: '95vw' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <AlertTriangle size={22} style={{ color: 'var(--accent-danger)', flexShrink: 0 }} />
                  <h3 style={{ margin: 0, color: 'var(--accent-danger)' }}>Delete Organization?</h3>
                </div>
                <p style={{ marginBottom: 8, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  You are about to permanently delete <strong>{deleteOrgConfirm.name}</strong> and all of its data:
                </p>
                <ul style={{ margin: '0 0 16px 20px', color: 'var(--text-muted)', fontSize: '0.8125rem', lineHeight: 1.8 }}>
                  <li>{deleteOrgConfirm.facility_count} facilit{deleteOrgConfirm.facility_count === 1 ? 'y' : 'ies'}</li>
                  <li>{deleteOrgConfirm.user_count} user{deleteOrgConfirm.user_count === 1 ? '' : 's'}</li>
                  <li>All inventory, orders, tasks, projects, vendors, and chat history</li>
                </ul>
                <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  <strong>This cannot be undone.</strong> Enter your admin password to confirm.
                </p>
                {deleteOrgError && (
                  <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{deleteOrgError}</div>
                )}
                <div className="form-group">
                  <label className="form-label">Admin Password</label>
                  <input
                    className="form-input"
                    type="password"
                    value={deleteOrgPassword}
                    onChange={(e) => setDeleteOrgPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmDeleteOrg(); }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => { setDeleteOrgConfirm(null); setDeleteOrgPassword(''); setDeleteOrgError(''); }}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmDeleteOrg} disabled={deleteOrgDeleting}>
                    {deleteOrgDeleting ? 'Deleting…' : 'Delete Organization'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Development tab — admin/org_admin can reach it now (Template Builder lives here as
              a tab), but DevelopmentTab itself keeps Dev Todos/Database/Used Emails gated to
              isSuperAdmin so that original boundary doesn't loosen. */}
          {activeTab === 'dev-todos' && isAdmin && <DevelopmentTab isSuperAdmin={isSuperAdmin} />}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="card">
              <h3 style={{ marginBottom: 20 }}>Notification Preferences</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { label: 'Low stock alerts', desc: 'Notify when items fall below reorder point' },
                  { label: 'Overdue tasks', desc: 'Remind about tasks past their due date' },
                  { label: 'Upcoming events', desc: 'Alert for events in the next 7 days' },
                  { label: 'Order status changes', desc: 'Notify when order status is updated' },
                ].map(({ label, desc }) => (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', background: 'var(--bg-elevated)',
                    borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)'
                  }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{label}</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22 }}>
                      <input type="checkbox" defaultChecked style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{
                        position: 'absolute', cursor: 'pointer', inset: 0,
                        background: 'var(--accent-primary)', borderRadius: 999,
                        transition: 'var(--transition)',
                      }} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Weather Settings Card ────────────────────────────────────────────────────
function WeatherSettingsCard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { facilityId } = useFacility();
  const canEditWeather = user?.role === 'admin' || user?.role === 'manager';

  const { data: settingsRes } = useQuery({
    queryKey: ['weather-location-setting', facilityId],
    queryFn: () => api.get<Record<string, string>>('/settings/facility-settings', { keys: 'weather_location' }),
    enabled: canEditWeather,
  });
  const existing = (settingsRes as unknown as { data: Record<string, string> })?.data?.['weather_location'] ?? '';
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Initialize once loaded
  const [initialized, setInitialized] = useState(false);
  if (!initialized && existing) {
    setLocation(existing);
    setInitialized(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.post('/settings/facility-settings', { settings: { weather_location: location.trim() } });
      qc.invalidateQueries({ queryKey: ['weather-location-setting', facilityId] });
      qc.invalidateQueries({ queryKey: ['weather-settings', facilityId] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  if (!canEditWeather) return null;

  return (
    <div className="card">
      <h3 style={{ marginBottom: 8 }}>Weather Location</h3>
      <p style={{ marginBottom: 16, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        Enter a city name or ZIP code to display weather forecasts on the Dashboard.
      </p>
      <div style={{ display: 'flex', gap: 10, maxWidth: 400 }}>
        <input
          className="form-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. New York or 10001"
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saved && <p style={{ marginTop: 8, color: 'var(--accent-success)', fontSize: '0.875rem' }}>✓ Saved</p>}
    </div>
  );
}

// ─── Financials Tab ───────────────────────────────────────────────────────────
const DEFAULT_BUDGET_CATEGORIES = [
  'Maintenance Supplies', 'Maintenance Equipment', 'Utilities',
  'Administrative', 'Nursing Supplies', 'Kitchen/Food', 'Cleaning Supplies',
];

interface Budget {
  id: number;
  category_name: string;
  monthly_amount: number;
  alert_threshold: number;
  is_active: boolean;
}

function FinancialsTab() {
  const qc = useQueryClient();
  const { data: budgetsRes, isLoading } = useQuery({
    queryKey: ['budgets-settings'],
    queryFn: () => api.get<Budget[]>('/budgets'),
  });
  const budgets: Budget[] = (budgetsRes as unknown as { data: Budget[] })?.data ?? [];

  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ category_name: '', monthly_amount: 0, alert_threshold: 80, is_active: true });
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ category_name: '', monthly_amount: 0, alert_threshold: 80 });
  const [saving, setSaving] = useState(false);

  // Pre-seed if empty
  useEffect(() => {
    if (!isLoading && budgets.length === 0) {
      (async () => {
        for (const cat of DEFAULT_BUDGET_CATEGORIES) {
          await api.post('/budgets', { category_name: cat, monthly_amount: 0, alert_threshold: 80, is_active: true });
        }
        qc.invalidateQueries({ queryKey: ['budgets-settings'] });
      })();
    }
  }, [isLoading, budgets.length, qc]);

  async function handleSaveEdit() {
    if (!editId) return;
    setSaving(true);
    try {
      await api.put(`/budgets/${editId}`, editForm);
      qc.invalidateQueries({ queryKey: ['budgets-settings'] });
      setEditId(null);
    } finally { setSaving(false); }
  }

  async function handleAdd() {
    if (!newForm.category_name.trim()) return;
    setSaving(true);
    try {
      await api.post('/budgets', { ...newForm, is_active: true });
      qc.invalidateQueries({ queryKey: ['budgets-settings'] });
      setShowAdd(false);
      setNewForm({ category_name: '', monthly_amount: 0, alert_threshold: 80 });
    } finally { setSaving(false); }
  }

  async function handleDelete(id: number) {
    await api.delete(`/budgets/${id}`);
    qc.invalidateQueries({ queryKey: ['budgets-settings'] });
  }

  async function handleToggleActive(budget: Budget) {
    await api.put(`/budgets/${budget.id}`, { ...budget, is_active: !budget.is_active });
    qc.invalidateQueries({ queryKey: ['budgets-settings'] });
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0 }}>Budget Categories</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Track monthly spending by category. Set alert thresholds to get notified when spending approaches budget.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add Budget Category
        </button>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><div className="loading-spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {budgets.map((b) => (
            <div key={b.id} style={{
              padding: '12px 16px', borderRadius: 'var(--radius-md)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
              opacity: b.is_active ? 1 : 0.5,
            }}>
              {editId === b.id ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input className="form-input" value={editForm.category_name} onChange={e => setEditForm(f => ({ ...f, category_name: e.target.value }))} style={{ flex: 2, minWidth: 160 }} placeholder="Category name" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>$/mo</span>
                    <input className="form-input" type="number" min={0} value={editForm.monthly_amount} onChange={e => setEditForm(f => ({ ...f, monthly_amount: Number(e.target.value) }))} style={{ width: 100 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Alert%</span>
                    <input className="form-input" type="number" min={1} max={100} value={editForm.alert_threshold} onChange={e => setEditForm(f => ({ ...f, alert_threshold: Number(e.target.value) }))} style={{ width: 72 }} />
                  </div>
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSaveEdit}>Save</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{b.category_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      ${Number(b.monthly_amount).toLocaleString()}/mo · Alert at {b.alert_threshold}%
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleToggleActive(b)} style={{ fontSize: '0.75rem' }}>
                    {b.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(b.id); setEditForm({ category_name: b.category_name, monthly_amount: b.monthly_amount, alert_threshold: b.alert_threshold, is_active: b.is_active }); }}>
                    <Pencil size={13} />
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={() => handleDelete(b.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div style={{ marginTop: 16, padding: '16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '0.875rem' }}>Add Budget Category</h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-input" value={newForm.category_name} onChange={e => setNewForm(f => ({ ...f, category_name: e.target.value }))} placeholder="Category name" style={{ flex: 2, minWidth: 160 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>$/mo</span>
              <input className="form-input" type="number" min={0} value={newForm.monthly_amount} onChange={e => setNewForm(f => ({ ...f, monthly_amount: Number(e.target.value) }))} style={{ width: 100 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Alert%</span>
              <input className="form-input" type="number" min={1} max={100} value={newForm.alert_threshold} onChange={e => setNewForm(f => ({ ...f, alert_threshold: Number(e.target.value) }))} style={{ width: 72 }} />
            </div>
            <button className="btn btn-primary btn-sm" disabled={saving || !newForm.category_name.trim()} onClick={handleAdd}>
              <Plus size={14} /> Add
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Service Categories Tab ───────────────────────────────────────────────────

function ServiceCategoriesTab({
  categories,
  onChanged,
}: {
  categories: ServiceCategory[];
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const canEditServices = user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'manager';
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#6366f1');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6366f1');
  const [saving, setSaving] = useState(false);

  // Import / export state
  const csvRef = useRef<HTMLInputElement>(null);
  const jsonRef = useRef<HTMLInputElement>(null);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  async function saveEdit(id: number) {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/vendors/service-categories/${id}`, { name: editName.trim(), color: editColor });
      onChanged();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function deleteCategory(id: number) {
    await api.delete(`/vendors/service-categories/${id}`);
    onChanged();
  }

  async function addCategory(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/vendors/service-categories', { name: newName.trim(), color: newColor });
      onChanged();
      setNewName('');
      setNewColor('#6366f1');
    } finally {
      setSaving(false);
    }
  }

  function downloadExport(format: 'csv' | 'json') {
    exportFile(`/api/vendors/service-categories/export?format=${format}`, `service_categories.${format}`);
  }

  async function handleImport(file: File) {
    setImporting(true); setImportResult(null); setImportError(null);
    const fd = new FormData();
    fd.append('file', file); fd.append('mode', importMode);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/vendors/service-categories/import', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const json = await res.json();
      if (json.success) { setImportResult(json.data); onChanged(); }
      else { setImportError(json.error ?? 'Import failed'); }
    } catch (err: any) {
      setImportError(err.message ?? 'Network error');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="card">
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0 }}>Vendor Service Categories</h3>
        <p style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Manage the service categories used to classify vendors. Each category has a color displayed on vendor cards.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
        {categories.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            No service categories yet. Add one below.
          </div>
        )}
        {categories.map((cat) => (
          <div
            key={cat.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            {editingId === cat.id ? (
              <>
                <input
                  type="color"
                  value={editColor}
                  onChange={(e) => setEditColor(e.target.value)}
                  style={{ width: 32, height: 32, border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer', padding: 2 }}
                />
                <input
                  className="form-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{ flex: 1 }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit(cat.id);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                />
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveEdit(cat.id)}>
                  Save
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>
                  <X size={14} />
                </button>
              </>
            ) : (
              <>
                {/* Color swatch */}
                <div style={{
                  width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                  background: cat.color, border: '1px solid rgba(0,0,0,0.15)',
                }} />
                {/* Color hex */}
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace', flexShrink: 0 }}>
                  {cat.color}
                </span>
                {/* Name */}
                <span style={{ flex: 1, fontSize: '0.9375rem', fontWeight: 500 }}>{cat.name}</span>
                {/* Preview pill */}
                <span style={{
                  padding: '2px 10px', borderRadius: 999,
                  fontSize: '0.75rem', fontWeight: 500,
                  border: `1px solid ${cat.color}44`,
                  background: `${cat.color}22`,
                  color: cat.color,
                  flexShrink: 0,
                }}>
                  {cat.name}
                </span>
                {canEditServices && (
                  <>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => { setEditingId(cat.id); setEditName(cat.name); setEditColor(cat.color); }}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--accent-danger)' }}
                      onClick={() => deleteCategory(cat.id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add new — managers and above only */}
      {canEditServices && <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 20 }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '0.875rem', fontWeight: 600 }}>Add New Category</h4>
        <form onSubmit={addCategory} style={{ display: 'flex', gap: 10, alignItems: 'center', maxWidth: 480 }}>
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            title="Pick a color"
            style={{ width: 40, height: 40, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: 3, flexShrink: 0 }}
          />
          <input
            className="form-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Fire Safety"
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={saving || !newName.trim()}>
            <Plus size={15} /> Add
          </button>
        </form>
      </div>}

      {/* Import / Export — hidden for viewer/user role */}
      {user?.role !== 'viewer' && <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 20, marginTop: 4 }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '0.875rem', fontWeight: 600 }}>Import / Export</h4>

        {/* Export row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', minWidth: 52 }}>Export:</span>
          <button className="btn btn-ghost btn-sm" onClick={() => downloadExport('csv')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={13} /><FileText size={13} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => downloadExport('json')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={13} /><FileJson size={13} /> JSON
          </button>
        </div>

        {/* Import mode toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', minWidth: 52 }}>Mode:</span>
          {(['merge', 'replace'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setImportMode(m)}
              style={{
                padding: '4px 12px', borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 500,
                background: importMode === m ? 'rgba(59,130,246,0.15)' : 'var(--bg-elevated)',
                color: importMode === m ? 'var(--accent-primary)' : 'var(--text-secondary)',
                outline: importMode === m ? '1px solid var(--accent-primary)' : '1px solid var(--border-subtle)',
              }}
            >
              {m === 'merge' ? 'Merge (skip existing)' : 'Replace (clear all first)'}
            </button>
          ))}
        </div>
        {importMode === 'replace' && (
          <p style={{ fontSize: '0.8rem', color: 'var(--accent-danger)', marginBottom: 10 }}>
            Warning: all existing categories will be deleted before importing.
          </p>
        )}

        {/* Import buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', minWidth: 52 }}>Import:</span>
          <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ''; }} />
          <button className="btn btn-ghost btn-sm" disabled={importing}
            onClick={() => csvRef.current?.click()}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Upload size={13} /><FileText size={13} /> CSV
          </button>
          <input ref={jsonRef} type="file" accept=".json" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ''; }} />
          <button className="btn btn-ghost btn-sm" disabled={importing}
            onClick={() => jsonRef.current?.click()}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Upload size={13} /><FileJson size={13} /> JSON
          </button>
          {importing && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Importing…</span>}
        </div>

        {/* Import result */}
        {importResult && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={15} style={{ color: '#22c55e', flexShrink: 0 }} />
            <span style={{ fontSize: '0.875rem', color: '#22c55e' }}>
              Imported {importResult.imported}, skipped {importResult.skipped}
            </span>
          </div>
        )}
        {importError && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={15} style={{ color: 'var(--accent-danger)', flexShrink: 0 }} />
            <span style={{ fontSize: '0.875rem', color: 'var(--accent-danger)' }}>{importError}</span>
          </div>
        )}
      </div>}
    </div>
  );
}

// ─── Import / Export Tab ──────────────────────────────────────────────────────

type DataTable = 'vendors' | 'catalogue' | 'schedule' | 'facility_keys' | 'resident_rooms' | 'tasks'
  | 'scheduled_tasks' | 'qapi_entries' | 'facility_supplies' | 'facility_extinguishers' | 'facility_hvac'
  | 'projects' | 'orders';
type ImpFormat = 'json' | 'csv' | 'sqlite';
type ImpMode = 'merge' | 'replace';

interface TableResult { inserted: number; skipped: number; errors: string[]; }

const DATA_TABS: Array<{
  id: DataTable; label: string; category: string;
  exportFormats: ('json' | 'csv')[];
  importFormats: ImpFormat[];
}> = [
  // COMPLIANCE
  { id: 'schedule',         label: 'Maintenance Schedule', category: 'COMPLIANCE', exportFormats: ['csv'],         importFormats: ['csv'] },
  { id: 'scheduled_tasks',  label: 'Scheduled Tasks',      category: 'COMPLIANCE', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  { id: 'tasks',            label: 'Director Tasks',       category: 'COMPLIANCE', exportFormats: ['csv'],         importFormats: ['csv'] },
  { id: 'qapi_entries',     label: 'QAPI Entries',         category: 'COMPLIANCE', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  // INVENTORY
  { id: 'catalogue',          label: 'Catalog Items',     category: 'INVENTORY', exportFormats: ['json', 'csv'], importFormats: ['csv', 'sqlite'] },
  { id: 'facility_supplies',  label: 'Facility Supplies', category: 'INVENTORY', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  { id: 'facility_extinguishers', label: 'Extinguishers', category: 'INVENTORY', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  { id: 'facility_hvac',      label: 'HVAC',              category: 'INVENTORY', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  { id: 'facility_keys',      label: 'Facility Keys',     category: 'INVENTORY', exportFormats: ['csv'],         importFormats: ['csv', 'sqlite'] },
  { id: 'resident_rooms',     label: 'Resident Rooms',    category: 'INVENTORY', exportFormats: ['csv'],         importFormats: ['csv', 'sqlite'] },
  // VENDORS & PROJECTS
  { id: 'vendors',  label: 'Vendors',  category: 'VENDORS & PROJECTS', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv', 'sqlite'] },
  { id: 'projects', label: 'Projects', category: 'VENDORS & PROJECTS', exportFormats: ['json', 'csv'], importFormats: ['json', 'csv'] },
  // ORDERS
  { id: 'orders', label: 'Orders', category: 'ORDERS', exportFormats: ['json', 'csv'], importFormats: [] },
];

const FORMAT_ACCEPT: Record<ImpFormat, string> = {
  json: '.json', csv: '.csv', sqlite: '.db,.sqlite,.sqlite3',
};
const FORMAT_LABELS: Record<ImpFormat, { label: string; icon: React.ReactNode }> = {
  json:   { label: 'JSON',         icon: <FileJson size={14} /> },
  csv:    { label: 'CSV',          icon: <FileText size={14} /> },
  sqlite: { label: 'SQLite (.db)', icon: <HardDrive size={14} /> },
};

const DATA_TABLE_QUERY_KEYS: Record<DataTable, string[]> = {
  schedule: ['scheduled-tasks', 'scheduled-tasks-upcoming'],
  scheduled_tasks: ['scheduled-tasks', 'scheduled-tasks-upcoming'],
  tasks: ['tasks'],
  qapi_entries: ['qapi-entries'],
  catalogue: ['inventory-items', 'inventory-categories'],
  facility_supplies: ['facility-supplies'],
  facility_extinguishers: ['extinguishers'],
  facility_hvac: ['facility-hvac'],
  facility_keys: ['keys'],
  resident_rooms: ['keys'],
  vendors: ['vendors', 'vendors-expiring'],
  projects: ['projects'],
  orders: ['orders'],
};

function ImportExportTab() {
  const qc = useQueryClient();
  const [activeDataTab, setActiveDataTab] = useState<DataTable>('vendors');
  const [importFormat, setImportFormat] = useState<ImpFormat>('csv');
  const [importMode, setImportMode] = useState<ImpMode>('merge');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<TableResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const tab = DATA_TABS.find(t => t.id === activeDataTab)!;

  function switchTab(id: DataTable) {
    const newTab = DATA_TABS.find(t => t.id === id)!;
    setActiveDataTab(id);
    setImportFormat(newTab.importFormats[0]);
    setResult(null);
    setImportError(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function downloadExport(fmt: 'json' | 'csv') {
    exportFile(`/api/data/export/${activeDataTab}?format=${fmt}`, `${activeDataTab}.${fmt}`);
  }

  async function handleImport(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setImporting(true); setResult(null); setImportError(null);
    const fd = new FormData();
    fd.append('file', file); fd.append('format', importFormat); fd.append('mode', importMode);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/data/import/${activeDataTab}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const json = await res.json();
      if (json.success) {
        setResult(json.data as TableResult);
        if (fileRef.current) fileRef.current.value = '';
        DATA_TABLE_QUERY_KEYS[activeDataTab].forEach(key => qc.invalidateQueries({ queryKey: [key] }));
      } else {
        setImportError(json.error ?? 'Import failed');
      }
    } catch (err: any) {
      setImportError(err.message ?? 'Network error');
    } finally {
      setImporting(false);
    }
  }

  const btnStyle = (active: boolean) => ({
    display: 'flex' as const, alignItems: 'center' as const, gap: 6,
    padding: '7px 14px', borderRadius: 'var(--radius-md)',
    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-default)'}`,
    background: active ? 'rgba(59,130,246,0.1)' : 'var(--bg-elevated)',
    color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
    cursor: 'pointer' as const, fontSize: '0.875rem', fontWeight: 500,
    transition: 'all var(--transition)',
  });

  const categories = Array.from(new Set(DATA_TABS.map(t => t.category)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Category-grouped tab bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {categories.map(cat => (
          <div key={cat}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
              color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase' as const }}>
              {cat}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
              {DATA_TABS.filter(dt => dt.category === cat).map(dt => (
                <button key={dt.id} onClick={() => switchTab(dt.id)} style={btnStyle(activeDataTab === dt.id)}>
                  {dt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Export card */}
      <div className="card">
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>Export — {tab.label}</h3>
          <p style={{ marginTop: 5, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Download your {tab.label.toLowerCase()} data for backup or migration.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {tab.exportFormats.map(fmt => (
            <button key={fmt} className="btn btn-ghost" onClick={() => downloadExport(fmt)}
              style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Download size={14} />
              {fmt === 'json' ? <><FileJson size={14} /> Export JSON</> : <><FileText size={14} /> Export CSV</>}
            </button>
          ))}
        </div>
      </div>

      {/* Import card — hidden for export-only tables */}
      {tab.importFormats.length > 0 && <div className="card">
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ margin: 0 }}>Import — {tab.label}</h3>
          <p style={{ marginTop: 5, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Import {tab.label.toLowerCase()} from a file into ProQure.
          </p>
        </div>
        <form onSubmit={handleImport}>
          {/* Format */}
          <div style={{ marginBottom: 14 }}>
            <label className="form-label">File Format</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {tab.importFormats.map(fmt => (
                <button key={fmt} type="button"
                  onClick={() => { setImportFormat(fmt); setResult(null); setImportError(null); }}
                  style={btnStyle(importFormat === fmt)}>
                  {FORMAT_LABELS[fmt].icon} {FORMAT_LABELS[fmt].label}
                </button>
              ))}
            </div>
          </div>

          {/* Mode */}
          <div style={{ marginBottom: 14 }}>
            <label className="form-label">Import Mode</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {(['merge', 'replace'] as ImpMode[]).map(m => (
                <button key={m} type="button" onClick={() => setImportMode(m)} style={btnStyle(importMode === m)}>
                  {m === 'merge' ? 'Merge (keep existing)' : 'Replace (clear & import)'}
                </button>
              ))}
            </div>
            {importMode === 'replace' && (
              <p style={{ marginTop: 6, fontSize: '0.8125rem', color: 'var(--accent-danger)' }}>
                Warning: All existing {tab.label.toLowerCase()} will be deleted before importing.
              </p>
            )}
          </div>

          {/* File */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Select File</label>
            <input ref={fileRef} type="file" accept={FORMAT_ACCEPT[importFormat]} required
              style={{ display: 'block', marginTop: 6, padding: '8px 0', color: 'var(--text-primary)', fontSize: '0.875rem' }} />
          </div>

          <button type="submit" className="btn btn-primary" disabled={importing}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Upload size={15} /> {importing ? 'Importing…' : 'Import'}
          </button>
        </form>

        {importError && (
          <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <AlertCircle size={16} style={{ color: 'var(--accent-danger)', flexShrink: 0, marginTop: 1 }} />
            <span style={{ color: 'var(--accent-danger)', fontSize: '0.875rem' }}>{importError}</span>
          </div>
        )}

        {result && (
          <div style={{ marginTop: 16, padding: '16px', borderRadius: 'var(--radius-md)', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <CheckCircle size={16} style={{ color: '#22c55e' }} />
              <span style={{ fontWeight: 600, color: '#22c55e' }}>Import Complete</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.875rem' }}>
              {[
                { label: 'Imported', val: result.inserted },
                { label: 'Skipped',  val: result.skipped },
              ].map(({ label, val }) => (
                <div key={label} style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{label}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.25rem' }}>{val}</div>
                </div>
              ))}
            </div>
            {result.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--accent-danger)', marginBottom: 6 }}>
                  Errors ({result.errors.length}):
                </div>
                <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {result.errors.map((err, i) => <div key={i}>{err}</div>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Chrome Extension card */}
      <ChromeExtensionCard />
    </div>
  );
}

function ChromeExtensionCard() {
  const [serverStatus, setServerStatus] = useState<'unknown' | 'ok' | 'error'>('unknown');
  const [checking, setChecking] = useState(false);

  async function checkConnection() {
    setChecking(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/ping', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setServerStatus(data.status === 'ok' ? 'ok' : 'error');
    } catch {
      setServerStatus('error');
    } finally {
      setChecking(false);
    }
  }

  function downloadExtension() {
    const link = document.createElement('a');
    link.href = '/chrome-extension';
    link.target = '_blank';
    link.click();
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Puzzle size={20} style={{ color: 'var(--accent-primary)' }} />
        </div>
        <div>
          <h3 style={{ margin: 0 }}>Chrome Extension — Product Importer</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Import products directly from supplier websites into ProQure Pending Imports. Supports 17 major suppliers including Amazon, Grainger, Home Depot, McMaster-Carr, and more.
          </p>
        </div>
      </div>

      {/* Server status */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Server Connection</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
            borderRadius: 'var(--radius-md)', fontSize: '0.875rem',
            background: serverStatus === 'ok' ? 'rgba(34,197,94,0.1)' : serverStatus === 'error' ? 'rgba(239,68,68,0.1)' : 'var(--bg-elevated)',
            border: `1px solid ${serverStatus === 'ok' ? 'rgba(34,197,94,0.3)' : serverStatus === 'error' ? 'rgba(239,68,68,0.3)' : 'var(--border-default)'}`,
            color: serverStatus === 'ok' ? '#22c55e' : serverStatus === 'error' ? 'var(--accent-danger)' : 'var(--text-muted)',
          }}>
            <Globe size={14} />
            {serverStatus === 'ok' ? 'Connected — ProQure server is reachable' : serverStatus === 'error' ? 'Cannot reach ProQure server' : 'Not checked'}
          </div>
          <button className="btn btn-ghost" onClick={checkConnection} disabled={checking} style={{ fontSize: '0.8125rem' }}>
            {checking ? 'Checking…' : 'Test Connection'}
          </button>
        </div>
      </div>

      {/* Install instructions */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>Installation</div>
        <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          <li>Open Chrome and go to <strong>chrome://extensions</strong></li>
          <li>Enable <strong>Developer mode</strong> (toggle in the top right)</li>
          <li>Click <strong>Load unpacked</strong> and select the <code style={{ background: 'var(--bg-elevated)', padding: '1px 5px', borderRadius: 3, fontSize: '0.8125rem' }}>chrome-extension</code> folder from the downloaded package</li>
          <li>The <strong>ProQure Web Catalog Helper</strong> icon will appear in your Chrome toolbar</li>
          <li>Navigate to any supported product page and click the extension icon to import</li>
        </ol>
      </div>

      {/* Supported sites */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Supported Supplier Sites</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['Amazon', 'Grainger', 'HD Supply', 'Home Depot', 'McMaster-Carr', 'Uline', 'Zoro', 'WebstaurantStore', 'Global Industrial', 'Parts Town', 'Walmart', 'Genuine Replacement Parts', 'Supply House', 'USA Clean', 'Direct Supply', 'Carstens', 'Kpaul Supply'].map((site) => (
            <span key={site} style={{ padding: '4px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--accent-primary)', fontSize: '0.8125rem', fontWeight: 500 }}>
              {site}
            </span>
          ))}
        </div>
      </div>

      {/* Download button */}
      <div>
        <p style={{ margin: '0 0 10px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          Download the extension as a zip, then load it as an unpacked extension in Chrome.
          New pending imports will appear as a badge on the <strong>Catalog</strong> toolbar and trigger a toast notification.
        </p>
        <a
          href="/api/catalogue/extension/download"
          download="proqure-extension.zip"
          className="btn btn-primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', fontSize: '0.875rem' }}
        >
          <Download size={14} /> Download Extension (v3.0)
        </a>
      </div>
    </div>
  );
}

// ─── Database Viewer Tab ──────────────────────────────────────────────────────
interface DbTable {
  name: string;
  row_count: number;
  columns: string[];
}

interface DbTablePage {
  name: string;
  row_count: number;
  columns: string[];
  rows: Record<string, unknown>[];
  page: number;
  pageSize: number;
  totalPages: number;
}

interface DbOverview {
  tables: DbTable[];
  facilities: Array<{ id: number; name: string; org_id: number }>;
  organizations: Array<{ id: number; name: string; plan_tier: string }>;
}

function formatCell(col: string, val: unknown, facilityMap: Map<number, string>, orgMap: Map<number, string>) {
  if (val === null || val === undefined) {
    return <em style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>null</em>;
  }
  if (col === 'facility_id' && typeof val === 'number') {
    const fname = facilityMap.get(val);
    return fname ? `${val}: ${fname}` : String(val);
  }
  if (col === 'org_id' && typeof val === 'number') {
    const oname = orgMap.get(val);
    return oname ? `${val}: ${oname}` : String(val);
  }
  if (typeof val === 'object') {
    const str = JSON.stringify(val);
    return str.length > 80 ? str.slice(0, 80) + '…' : str;
  }
  const str = String(val);
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return str.slice(0, 10);
  return str.length > 80 ? str.slice(0, 80) + '…' : str;
}

interface TableCardProps {
  tbl: DbTable;
  facilityMap: Map<number, string>;
  orgMap: Map<number, string>;
}

function TableCard({ tbl, facilityMap, orgMap }: TableCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState<DbTablePage | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  function fetchPage(p: number) {
    setPageLoading(true);
    api.get<DbTablePage>(`/dev/db-overview?table=${tbl.name}&page=${p}`)
      .then(res => {
        setPageData((res as unknown as { data: DbTablePage }).data);
        setPageLoading(false);
      })
      .catch(() => setPageLoading(false));
  }

  function toggle() {
    if (!isOpen && !pageData) fetchPage(1);
    setIsOpen(prev => !prev);
  }

  function goToPage(p: number) {
    setPage(p);
    fetchPage(p);
  }

  function copyName() {
    navigator.clipboard.writeText(tbl.name).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      {/* Header */}
      <div
        onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: isOpen ? 'var(--bg-active)' : 'transparent', borderBottom: isOpen ? '1px solid var(--border-subtle)' : 'none', userSelect: 'none' }}
        onMouseEnter={e => { if (!isOpen) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
        onMouseLeave={e => { if (!isOpen) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      >
        <ChevronDown size={14} style={{ color: 'var(--text-muted)', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 150ms ease', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', fontFamily: 'monospace', flex: 1 }}>{tbl.name}</span>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(107,114,128,0.15)', color: 'var(--text-muted)', whiteSpace: 'nowrap', marginRight: 4 }}>
          {tbl.row_count.toLocaleString()} rows
        </span>
        <button
          className="combo-jello-spin"
          data-tint="neutral"
          onClick={e => { e.stopPropagation(); copyName(); }}
          onMouseLeave={spinIconOut}
          title="Copy table name"
          style={{ background: 'none', border: '1px solid var(--border-default)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: copied ? 'var(--accent-success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Copy size={12} className="btn-icon" />
        </button>
      </div>

      {/* Data table */}
      {isOpen && (
        <>
          {pageLoading ? (
            <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Loading…</div>
          ) : pageData && pageData.rows.length === 0 ? (
            <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)' }}>Empty table</div>
          ) : pageData ? (
            <>
              <div style={{ overflowX: 'auto', overflowY: 'scroll', maxHeight: '350px', display: 'block' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'auto' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      {pageData.columns.map(col => (
                        <th key={col} style={{ padding: '6px 10px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', background: 'var(--bg-elevated)' }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.rows.map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 === 1 ? 'var(--bg-hover)' : 'transparent', borderBottom: '1px solid var(--border-subtle)' }}>
                        {pageData.columns.map(col => (
                          <td key={col} style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatCell(col, row[col], facilityMap, orgMap)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pageData.totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
                  <button
                    className="combo1"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    style={{ background: 'none', border: '1px solid var(--border-default)', borderRadius: 4, padding: '2px 8px', cursor: page <= 1 ? 'default' : 'pointer', color: page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: page <= 1 ? 0.4 : 1 }}
                  >‹ Prev</button>
                  <span>Page {page} of {pageData.totalPages}</span>
                  <button
                    className="combo1"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= pageData.totalPages}
                    style={{ background: 'none', border: '1px solid var(--border-default)', borderRadius: 4, padding: '2px 8px', cursor: page >= pageData.totalPages ? 'default' : 'pointer', color: page >= pageData.totalPages ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: page >= pageData.totalPages ? 0.4 : 1 }}
                  >Next ›</button>
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function DatabaseViewerTab() {
  const [dbData, setDbData] = useState<DbOverview | null>(null);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbError, setDbError] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  useEffect(() => {
    api.get<DbOverview>('/dev/db-overview')
      .then((res) => {
        setDbData((res as unknown as { data: DbOverview }).data);
        setDbLoading(false);
      })
      .catch(() => {
        setDbError('Failed to load database overview.');
        setDbLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (dbLoading) return (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading database overview…</div>
    </div>
  );

  if (dbError) return <div style={{ padding: 20, color: 'var(--accent-danger)' }}>{dbError}</div>;
  if (!dbData) return null;

  const facilityMap = new Map(dbData.facilities.map(f => [f.id, f.name]));
  const orgMap = new Map(dbData.organizations.map(o => [o.id, o.name]));
  const filteredTables = dbData.tables.filter(t => t.name.toLowerCase().includes(tableSearch.toLowerCase()));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Legend */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Facilities</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {dbData.facilities.map(f => (
                <span key={f.id} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)', whiteSpace: 'nowrap' }}>
                  #{f.id} {f.name}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Organizations</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {dbData.organizations.map(o => (
                <span key={o.id} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(139,92,246,0.12)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.2)', whiteSpace: 'nowrap' }}>
                  #{o.id} {o.name} ({o.plan_tier})
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input
          className="form-input"
          placeholder="Search tables…"
          value={tableSearch}
          onChange={e => setTableSearch(e.target.value)}
          style={{ paddingLeft: 30, fontSize: '0.8125rem' }}
        />
      </div>

      {/* Count */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {filteredTables.length} of {dbData.tables.length} tables
      </div>

      {/* Table cards */}
      {filteredTables.map(tbl => (
        <TableCard key={tbl.name} tbl={tbl} facilityMap={facilityMap} orgMap={orgMap} />
      ))}
    </div>
  );
}

// ─── Template Builder — Logbook Templates JSON injector ───────────────────────
// "Paste a hand-authored JSON schema and create a template from it directly, instead of
// clicking through the UI form-builder for complex templates" (LOGBOOK_SYSTEM_DESIGN.md's
// "JSON injector"). Reuses LogbookFormRenderer for the live preview — the exact same component
// the WO Checklist tab uses to fill a form — rather than a second renderer. form_mode: 'grid' is
// injector-only for now; the visual FormSchemaBuilder in LogbookTemplates.tsx only authors
// single_form (see that file's TemplateFormModal for why).
interface TplBuilderTemplate {
  id: number; name: string; department_id: number | null; frequency: string; source: string;
  required: boolean; file_type: string; display_order: number; form_schema: LogbookFormSchema | null;
}
const TEMPLATE_BUILDER_SKELETON = `{
  "form_mode": "single_form",
  "fixed_fields": [
    { "key": "reading", "label": "Reading", "type": "number", "required": true }
  ]
}`;

function TemplateBuilderTab() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'new' | 'edit'>('new');
  const [selectedId, setSelectedId] = useState<string>('');
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [source, setSource] = useState<'building' | 'corporate'>('building');
  const [required, setRequired] = useState(false);
  const [jsonText, setJsonText] = useState(TEMPLATE_BUILDER_SKELETON);
  const [previewValues, setPreviewValues] = useState<Record<string, string | boolean>>({});
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  const { data: templatesRes } = useQuery({
    queryKey: ['logbook-templates'],
    queryFn: () => api.get<TplBuilderTemplate[]>('/logbook-templates'),
  });
  const templates = templatesRes?.data ?? [];

  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    api.get<{ id: number; name: string }[]>('/departments').then(res => setDepartments(res.data ?? [])).catch(() => {});
  }, []);

  function loadTemplateForEdit(t: TplBuilderTemplate) {
    setMode('edit');
    setSelectedId(String(t.id));
    setName(t.name);
    setDepartmentId(t.department_id != null ? String(t.department_id) : '');
    setFrequency(t.frequency);
    setSource(t.source === 'corporate' ? 'corporate' : 'building');
    setRequired(t.required);
    setJsonText(t.form_schema ? JSON.stringify(t.form_schema, null, 2) : TEMPLATE_BUILDER_SKELETON);
  }
  function resetForNew() {
    setMode('new');
    setSelectedId('');
    setName('');
    setDepartmentId('');
    setFrequency('daily');
    setSource('building');
    setRequired(false);
    setJsonText(TEMPLATE_BUILDER_SKELETON);
  }

  let parsedSchema: LogbookFormSchema | null = null;
  let parseError: string | null = null;
  try {
    const parsed = JSON.parse(jsonText);
    if (parsed && (parsed.form_mode === 'single_form' || parsed.form_mode === 'grid')) {
      parsedSchema = parsed;
    } else {
      parseError = 'JSON must have a "form_mode": "single_form" | "grid" property.';
    }
  } catch (e: any) {
    parseError = e?.message || 'Invalid JSON';
  }

  async function handleSave() {
    if (!name.trim()) { showToast('Name is required', true); return; }
    if (!parsedSchema) { showToast(`Fix the JSON before saving: ${parseError}`, true); return; }
    setSaving(true);
    try {
      if (mode === 'new') {
        const fd = new FormData();
        fd.append('name', name.trim());
        if (departmentId) fd.append('department_id', departmentId);
        fd.append('frequency', frequency);
        fd.append('file_type', 'pdf');
        fd.append('source', source);
        fd.append('required', String(required));
        fd.append('form_schema', JSON.stringify(parsedSchema));
        await api.postForm('/logbook-templates', fd);
        showToast('Template created');
      } else {
        const existing = templates.find(t => String(t.id) === selectedId);
        await api.put(`/logbook-templates/${selectedId}`, {
          name: name.trim(),
          department_id: departmentId || null,
          frequency,
          file_type: existing?.file_type ?? 'pdf',
          source,
          required,
          display_order: existing?.display_order ?? 0,
          form_schema: parsedSchema,
        });
        showToast('Template updated');
      }
      qc.invalidateQueries({ queryKey: ['logbook-templates'] });
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? 'Save failed', true);
    }
    setSaving(false);
  }

  const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px' }}>Template Builder — JSON Injector</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
          Paste a hand-authored form_schema JSON and create or update a Logbook Template directly — for
          complex (grid) templates that the visual form builder on the Log Book Templates page can't
          represent yet. Live preview renders through the exact same component the WO Checklist tab uses to fill a form.
        </p>
      </div>

      <Toast toast={toast} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select className="form-input" style={{ width: 280 }} value={mode === 'edit' ? selectedId : ''}
          onChange={e => {
            if (!e.target.value) { resetForNew(); return; }
            const t = templates.find(x => String(x.id) === e.target.value);
            if (t) loadTemplateForEdit(t);
          }}>
          <option value="">+ New template</option>
          {templates.map(t => <option key={t.id} value={t.id}>Edit: {t.name}</option>)}
        </select>
        {mode === 'edit' && <button className="btn btn-ghost btn-sm combo1" onClick={resetForNew}>Start new instead</button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left: metadata + JSON editor */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Name</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fire Extinguisher Inspection" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Department</label>
              <select className="form-input" value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
                <option value="">— Facility-wide —</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Frequency</label>
              <select className="form-input" value={frequency} onChange={e => setFrequency(e.target.value)}>
                {['daily', 'weekly', 'monthly', 'quarterly', 'annual', 'as_needed'].map(f => <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={source === 'corporate'} onChange={e => setSource(e.target.checked ? 'corporate' : 'building')} /> Corporate
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} /> Required
            </label>
          </div>
          <div>
            <label style={labelStyle}>form_schema JSON</label>
            <textarea
              className="form-input"
              value={jsonText}
              onChange={e => setJsonText(e.target.value)}
              rows={18}
              spellCheck={false}
              style={{ fontFamily: 'monospace', fontSize: 12, width: '100%', resize: 'vertical' }}
            />
            {parseError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{parseError}</div>}
          </div>
          <button className="btn btn-primary combo2" disabled={saving || !!parseError} onClick={handleSave} style={{ alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : mode === 'new' ? 'Create Template' : 'Save Changes'}
          </button>
        </div>

        {/* Right: live preview via the shared renderer */}
        <div className="card">
          <div style={labelStyle}>Live Preview</div>
          {parsedSchema ? (
            <LogbookFormRenderer schema={parsedSchema} values={previewValues} onChange={(k, v) => setPreviewValues(p => ({ ...p, [k]: v }))} checklistStyle />
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>Fix the JSON to see a preview.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Development Tab (inner tabs: Dev Todos + Database Viewer + Used Emails + Template Builder) ──
// Dev Todos/Database/Used Emails stay isSuperAdmin-only (the original boundary, before Template
// Builder moved in here as a tab) -- Template Builder itself is adminOnly (org_admin included),
// matching what it was as a standalone sidebar entry.
type DevInnerTab = 'todos' | 'database' | 'emails' | 'template-builder' | 'permissions' | 'billing-config' | 'audit-log';

const DevelopmentTab = ({ isSuperAdmin }: { isSuperAdmin: boolean }) => {
  const INNER_TABS: { id: DevInnerTab; label: string; icon: React.ReactNode; superAdminOnly?: boolean }[] = [
    { id: 'todos', label: 'Dev Todos', icon: <ListChecks size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
    { id: 'database', label: 'Database', icon: <Database size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
    { id: 'emails', label: 'Used Emails', icon: <Mail size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
    { id: 'template-builder', label: 'Template Builder', icon: <FileJson size={14} className="btn-icon order-tab-icon" /> },
    // Top-level "developer" control panel for role permissions — Admin-only (not org_admin, not
    // manager), same superAdminOnly boundary as Dev Todos/Database/Used Emails above.
    { id: 'permissions', label: 'Permissions', icon: <Shield size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
    // Billing prototype config (client/src/pages/Billing.tsx + BillingConfigContext.tsx) — same
    // superAdminOnly boundary as Database Viewer, since this is the "who's allowed to buy" control.
    { id: 'billing-config', label: 'Billing Configuration', icon: <CreditCard size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
    // Audit Log (v148) — login activity + activity log, read-only. Super-admin only, same boundary
    // as every other Development sibling tab above.
    { id: 'audit-log', label: 'Audit Log', icon: <ShieldCheck size={14} className="btn-icon order-tab-icon" />, superAdminOnly: true },
  ];
  const visibleTabs = INNER_TABS.filter(t => !t.superAdminOnly || isSuperAdmin);

  const [innerTab, setInnerTab] = useState<DevInnerTab>(() => {
    const stored = sessionStorage.getItem('settings_dev_inner_tab') as DevInnerTab | null;
    const allowed = stored && visibleTabs.some(t => t.id === stored);
    return allowed ? stored! : visibleTabs[0].id;
  });
  const handleInnerTab = (t: DevInnerTab) => {
    sessionStorage.setItem('settings_dev_inner_tab', t);
    setInnerTab(t);
  };

  // Sliding highlight for this tab bar -- same JS-measured technique already used for Inventory's
  // pill-tab switchers (Facility Keys/Resident Rooms sub-tabs, the main Catalog/Facility bar,
  // category pills), reusing the existing .pill-tab-slide-highlight/-btn classes rather than a
  // new pattern. JS-measured (not a fixed split) since labels are different widths and the number
  // of visible tabs varies by role (isSuperAdmin).
  const innerTabBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [innerTabHighlight, setInnerTabHighlight] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = innerTabBtnRefs.current[innerTab];
    if (el) setInnerTabHighlight({ left: el.offsetLeft, width: el.offsetWidth });
  }, [innerTab, visibleTabs.length]);

  return (
    <div>
      {/* inner tab bar — standard pill-tab-container/pill-tab switcher (same as Orders/Inventory/
          Task Manager), not a one-off button row */}
      <div className="pill-tab-container" style={{ marginBottom: 24, position: 'relative', overflow: 'hidden' }}>
        <div className="pill-tab-slide-highlight" style={{ width: innerTabHighlight.width, transform: `translateX(${innerTabHighlight.left}px)` }} />
        {visibleTabs.map(t => (
          <button
            key={t.id}
            ref={(el) => { innerTabBtnRefs.current[t.id] = el; }}
            className={`order-tab pill-tab pill-tab-slide-btn${innerTab === t.id ? ' active' : ''}`}
            style={{ position: 'relative', zIndex: 1 }}
            onClick={() => handleInnerTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {innerTab === 'todos' && isSuperAdmin && <DevTodosTab />}
      {innerTab === 'database' && isSuperAdmin && <DatabaseViewerTab />}
      {innerTab === 'emails' && isSuperAdmin && <UsedEmailsTab />}
      {innerTab === 'template-builder' && <TemplateBuilderTab />}
      {innerTab === 'permissions' && isSuperAdmin && <PermissionsTab />}
      {innerTab === 'billing-config' && isSuperAdmin && <BillingConfigTab />}
      {innerTab === 'audit-log' && isSuperAdmin && <AuditLog />}
    </div>
  );
};

// ─── Billing Configuration Tab ─────────────────────────────────────────────────
// Admin-only control panel for Billing & Plan (client/src/pages/Billing.tsx). Pricing/discount/
// mode config is server-backed (migration_v134/v135, server/src/routes/billing.ts) with a real
// per-org override capability -- an org picker at the top selects between the single global
// default and a specific org's override (a complete alternate config when one exists; orgs with
// no override just inherit the default). "Reset to Default" deletes that org's override row
// rather than editing it back to matching numbers.
const TIER_CONFIG_ORDER: PlanTierId[] = ['standard', 'pro', 'enterprise'];
const TIER_CONFIG_LABEL: Record<PlanTierId, string> = { standard: 'Standard', pro: 'Pro', enterprise: 'Enterprise' };

// The Global Default view's "Reset to Default" used to PUT a FACTORY_DEFAULTS constant defined
// here -- a second, client-side copy of the baseline that drifted out of sync with the real one
// (it still said standard: 49 long after Standard became 59, so resetting actively wrote the
// wrong price). It's gone: resetting now calls POST /billing/defaults/reset, which applies
// billing.ts's DEFAULT_ROW -- the one canonical baseline -- and returns the saved row. No
// baseline numbers live in this file anymore.

// Every editable field here saves to the server on blur (only when the value actually changed
// since the field was focused) -- flashing on every keystroke would be noisy for a multi-digit
// price. onCommit is awaited; success shows ONLY the inline border-glow + fading checkmark
// (buttonAnimations.css's fieldSavedGlow/fieldSavedCheck, ~1.3s) -- no toast, ever, for a
// per-field save. Failure shows a red border (persists until the next edit or next successful
// save -- not on a timer) plus a one-shot shake (fieldSavedShake, ~400ms) so a retry can shake
// again. Callers must NOT also toast on a rejected onCommit -- that was the actual bug: a shared
// persist() helper was toasting on every failure itself, ALWAYS pre-empting this inline state
// (and firing even when the failure came from here) since it toasted before the error ever
// reached this component's catch.
function SavedInput({ value, onChange, onCommit, type = 'number', min, max, disabled }: {
  value: number | string;
  onChange: (v: string) => void;
  onCommit?: () => void | Promise<void>;
  type?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
}) {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [shaking, setShaking] = useState(false);
  // Doubles as both "value when this focus session started" (the blur change-check) AND "last
  // confirmed-saved value" (what Escape reverts to) -- the two are the same value in practice,
  // since this also gets updated the moment a save actually succeeds, not just on focus.
  const focusValueRef = useRef(value);
  const skipNextBlurRef = useRef(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  return (
    <div style={{ position: 'relative' }}>
      <input
        className={`form-input saved-input-number${status === 'success' ? ' field-saved-flash' : ''}${status === 'error' ? ' field-saved-error-border' : ''}${shaking ? ' field-saved-shake' : ''}`}
        type={type}
        min={min}
        max={max}
        disabled={disabled}
        value={value}
        onKeyDown={(e) => {
          // Hiding the native spinner arrows (::-webkit-inner/outer-spin-button,
          // .saved-input-number in buttonAnimations.css) only hides the clickable UI -- the
          // browser's native increment/decrement-by-step behavior on ArrowUp/ArrowDown for
          // type="number" is a separate keyboard behavior, unaffected by that, and was still
          // silently stepping the value by 1. Blocked here; every other key is unaffected.
          if (type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) e.preventDefault();
          if (e.key === 'Enter') {
            // Same save path as blur, just triggered immediately instead of waiting for the user
            // to click away -- blur() fires the real blur event, so onBlur's own change-check and
            // success/error handling run exactly as they do today, nothing duplicated here.
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            // Revert to the last CONFIRMED-saved value (focusValueRef, not "whatever was typed
            // this session") and exit without saving. skipNextBlurRef tells the upcoming onBlur
            // (fired by blur() below) to bail out immediately -- needed because onChange's state
            // update is async, so by the time onBlur would naturally run, `value` may not have
            // re-rendered to the reverted value yet, which could otherwise still look "changed"
            // and fire a save onBlur was never supposed to make.
            e.preventDefault();
            skipNextBlurRef.current = true;
            if (status === 'error') setStatus('idle');
            onChange(String(focusValueRef.current));
            e.currentTarget.blur();
          }
        }}
        onFocus={() => { focusValueRef.current = value; }}
        onChange={(e) => {
          if (status === 'error') setStatus('idle'); // clear the error state on the next edit
          onChange(e.target.value);
        }}
        onBlur={async () => {
          if (skipNextBlurRef.current) { skipNextBlurRef.current = false; return; }
          if (String(focusValueRef.current) === String(value)) return;
          try {
            await onCommit?.();
            focusValueRef.current = value; // this is now the last confirmed-saved value too
            setStatus('idle');
            clearTimeout(successTimerRef.current);
            // Re-trigger the CSS animation even if it's already mid-flash from a rapid prior edit —
            // toggling off then on next frame restarts it instead of no-op'ing on an unchanged class.
            requestAnimationFrame(() => setStatus('success'));
            successTimerRef.current = setTimeout(() => setStatus((s) => (s === 'success' ? 'idle' : s)), 1300);
          } catch {
            setStatus('error');
            setShaking(false);
            clearTimeout(shakeTimerRef.current);
            requestAnimationFrame(() => setShaking(true));
            shakeTimerRef.current = setTimeout(() => setShaking(false), 500);
          }
        }}
      />
      {status === 'success' && <CheckCircle size={14} className="field-saved-check" />}
    </div>
  );
}

interface BillingOrgRow { id: number; name: string; slug: string; facility_count: number; has_override: boolean; }

function BillingConfigTab() {
  const { toast, showToast } = useToast();
  const qc = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState(''); // '' = Global Default

  const { data: orgsRes } = useQuery({
    queryKey: ['billing-orgs'],
    queryFn: () => api.get<BillingOrgRow[]>('/billing/orgs'),
  });
  const orgs = (orgsRes as unknown as { data: BillingOrgRow[] })?.data ?? [];
  const selectedOrg = orgs.find((o) => String(o.id) === selectedOrgId);

  const configQueryKey = selectedOrgId ? ['billing-config-org', selectedOrgId] : ['billing-config-defaults'];
  const { data: configRes } = useQuery({
    queryKey: configQueryKey,
    queryFn: () => selectedOrgId
      ? api.get<EffectiveBillingConfig>(`/billing/orgs/${selectedOrgId}`)
      : api.get<EffectiveBillingConfig>('/billing/defaults'),
  });
  const serverConfig = (configRes as unknown as { data: EffectiveBillingConfig })?.data;

  // Local buffer so typing feels instant; each field's own SavedInput persists it independently
  // on blur (matches the original per-field-autosave feel, now hitting the server instead of
  // localStorage) rather than requiring one page-wide Save button.
  const [form, setForm] = useState<EffectiveBillingConfig | null>(null);
  useEffect(() => { if (serverConfig) setForm(serverConfig); }, [serverConfig]);

  function numField(v: string): number {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  // No try/catch here on purpose -- this used to toast on every failure itself, which pre-empted
  // SavedInput's own inline error state (red border + shake) for every one of the 12 fields that
  // call this as their onCommit, since the toast fired before the rejection ever reached their
  // catch. Callers now decide how to surface a failure: SavedInput fields show it inline (no
  // toast), the Billing Mode buttons and Reset below show their own toast (no natural inline
  // affordance for a toggle button).
  async function persist(next: EffectiveBillingConfig) {
    const res = selectedOrgId
      ? await api.put(`/billing/orgs/${selectedOrgId}`, next)
      : await api.put('/billing/defaults', next);
    setForm((res as unknown as { data: EffectiveBillingConfig }).data);
    qc.invalidateQueries({ queryKey: ['billing-effective'] }); // Billing & Plan picks this up live
    qc.invalidateQueries({ queryKey: ['billing-orgs'] });
    // CONFIRMED root cause of "checkmark shows success but value reverts on tab switch": this
    // function invalidated every OTHER consumer's cache but never its own -- the global
    // QueryClient default is staleTime: 5 minutes (main.tsx), so without this, switching tabs
    // away from and back to BillingConfigTab remounted it and useQuery happily served the
    // 5-minute-old cached (pre-edit) value instead of refetching, even though the save itself had
    // already genuinely persisted (setForm above already shows the correct value in the CURRENT
    // view -- the bug only shows up after a remount reads the stale cache). Without this line the
    // save was never a false positive; the read on remount was just wrong.
    qc.invalidateQueries({ queryKey: configQueryKey });
    qc.invalidateQueries({ queryKey: ['billing-config-org'] }); // covers every org's cached override, not just the one currently selected
  }

  function setTierField(tierId: PlanTierId, field: keyof TierPricing, value: number) {
    setForm((f) => f ? { ...f, tiers: { ...f.tiers, [tierId]: { ...f.tiers[tierId], [field]: value } } } : f);
  }

  // Confirm-gated (below): this is a real, irreversible data loss for an org with a genuine
  // override (its own custom pricing/discounts/mode, gone the moment this runs -- DELETE
  // /billing/orgs/:orgId, unchanged, confirmed still just `DELETE FROM org_billing_overrides WHERE
  // org_id = $1` then a fresh getOrgEffective read) and, for Global Default, resets what every org
  // WITHOUT an override is currently charged. The button used to fire this directly on click with
  // no confirmation at all (not even window.confirm()) -- CLAUDE_RULES.md's custom-dark-modal rule
  // applies to both branches equally; gating only the org case would leave the other, equally
  // destructive branch on the exact same button one click away.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  async function doReset() {
    setResetting(true);
    if (selectedOrgId) {
      try {
        const res = await api.delete(`/billing/orgs/${selectedOrgId}`);
        setForm((res as unknown as { data: EffectiveBillingConfig }).data);
        qc.invalidateQueries({ queryKey: ['billing-effective'] });
        qc.invalidateQueries({ queryKey: ['billing-orgs'] });
        qc.invalidateQueries({ queryKey: configQueryKey }); // same missing-invalidation bug as persist() above
        showToast(`${selectedOrg?.name ?? 'Organization'} reset to the global default`);
        setResetConfirmOpen(false);
      } catch {
        showToast('Failed to reset organization override', true);
      }
    } else {
      try {
        // Real server-side reset (POST /billing/defaults/reset) against billing.ts's single
        // DEFAULT_ROW baseline. This used to be `persist(FACTORY_DEFAULTS)` -- a PUT of a
        // hardcoded copy of the baseline kept HERE, in the client. That copy still said
        // standard: 49 while the live default had been 59 for a while, so "Reset to Default"
        // genuinely wrote 49 over 59 every time it ran. It only looked like a no-op because the
        // value being reset FROM was also 49. The client no longer holds any baseline numbers.
        const res = await api.post('/billing/defaults/reset');
        setForm((res as unknown as { data: EffectiveBillingConfig }).data);
        qc.invalidateQueries({ queryKey: ['billing-effective'] });
        qc.invalidateQueries({ queryKey: ['billing-orgs'] });
        qc.invalidateQueries({ queryKey: configQueryKey });
        qc.invalidateQueries({ queryKey: ['billing-config-org'] });
        showToast('Global default reset to platform baseline');
        setResetConfirmOpen(false);
      } catch {
        showToast('Failed to reset global default', true);
      }
    }
    setResetting(false);
  }

  if (!form) {
    return <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>;
  }

  return (
    <div className="billing-btn-pulse-scope">
      <Toast toast={toast} />

      {/* Org picker — Global Default, or a specific org's override */}
      <div className="form-group" style={{ maxWidth: 360 }}>
        <label className="form-label">Editing</label>
        <DarkSelect
          value={selectedOrgId}
          onChange={setSelectedOrgId}
          options={[
            { value: '', label: 'Global Default' },
            ...orgs.map((o) => ({ value: String(o.id), label: `${o.name}${o.has_override ? ' (has override)' : ''}` })),
          ]}
        />
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '8px 0 20px' }}>
        {selectedOrgId
          ? (selectedOrg?.has_override
              ? `${selectedOrg.name} has its own override -- editing it directly.`
              : `${selectedOrg?.name ?? 'This org'} is currently using the global default. Editing any field here creates an override for this org only.`)
          : 'Editing the global default every org without an override inherits. Changes save automatically per field and apply immediately.'}
      </p>

      {/* Per-tier pricing */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 24 }}>
        {TIER_CONFIG_ORDER.map((tierId) => {
          const t = form.tiers[tierId];
          return (
            <div key={tierId} className="card" style={{ padding: 16 }}>
              <h4 style={{ margin: '0 0 12px' }}>{TIER_CONFIG_LABEL[tierId]}</h4>
              <div className="form-group">
                <label className="form-label">Price / facility / month ($)</label>
                <SavedInput min={0} value={t.basePricePerFacilityMonth}
                  onChange={(v) => setTierField(tierId, 'basePricePerFacilityMonth', numField(v))}
                  onCommit={() => persist(form)} />
              </div>
              <div className="form-group">
                <label className="form-label">Seats included / facility</label>
                <SavedInput min={0} value={t.seatsIncludedPerFacility}
                  onChange={(v) => setTierField(tierId, 'seatsIncludedPerFacility', numField(v))}
                  onCommit={() => persist(form)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Price / extra seat / month ($)</label>
                <SavedInput min={0} value={t.pricePerExtraSeatMonth}
                  onChange={(v) => setTierField(tierId, 'pricePerExtraSeatMonth', numField(v))}
                  onCommit={() => persist(form)} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Volume + annual discounts */}
      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 12px' }}>Discounts</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Volume discount — 1-10 facilities</label>
            <input className="form-input" type="text" value="0% (fixed)" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Volume discount — 11-20 facilities (%)</label>
            <SavedInput min={0} max={100} value={form.volumeDiscount10to20}
              onChange={(v) => setForm((f) => f ? { ...f, volumeDiscount10to20: numField(v) } : f)}
              onCommit={() => persist(form)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Volume discount — 21+ facilities (%)</label>
            <SavedInput min={0} max={100} value={form.volumeDiscount20plus}
              onChange={(v) => setForm((f) => f ? { ...f, volumeDiscount20plus: numField(v) } : f)}
              onCommit={() => persist(form)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Annual billing discount (%)</label>
            <SavedInput min={0} max={100} value={form.annualDiscount}
              onChange={(v) => setForm((f) => f ? { ...f, annualDiscount: numField(v) } : f)}
              onCommit={() => persist(form)} />
          </div>
        </div>
      </div>

      {/* Billing Mode — the "who's allowed to buy" control */}
      <div className="card" style={{ padding: 16, marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 4px' }}>Billing Mode</h4>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '0 0 12px' }}>
          Controls who can manage/purchase {selectedOrgId ? `${selectedOrg?.name ?? 'this org'}'s` : "an org's"} subscription on the Billing &amp; Plan page.
        </p>
        {/* Same shared SlidingToggle component as the Billing & Plan Monthly/Yearly cycle toggle,
            instead of the one-off flat-button style this used to be. */}
        <div style={{ width: 'fit-content' }}>
          <SlidingToggle
            value={form.billingMode}
            onChange={(mode) => {
              const next = { ...form, billingMode: mode };
              setForm(next);
              persist(next).catch(() => showToast('Failed to save billing mode', true));
            }}
            options={[
              { id: 'centralized' as const, label: 'Centralized — Org Admin manages' },
              { id: 'per_facility' as const, label: 'Per-Facility — Manager manages' },
            ]}
          />
        </div>
      </div>

      <button className="btn btn-ghost" onClick={() => setResetConfirmOpen(true)}>
        {selectedOrgId ? `Reset ${selectedOrg?.name ?? 'Org'} to Default` : 'Reset to Factory Defaults'}
      </button>

      {/* Reset confirm -- same modal-overlay/modal + btn-danger pattern as this file's own "Delete
          User?" modal above, not window.confirm(). Message names exactly what's discarded. */}
      {resetConfirmOpen && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !resetting) setResetConfirmOpen(false); }}>
          <div className="modal" style={{ maxWidth: 420, width: '95vw' }}>
            <h3 style={{ marginBottom: 12 }}>{selectedOrgId ? `Reset ${selectedOrg?.name ?? 'this org'} to Default?` : 'Reset to Factory Defaults?'}</h3>
            <p style={{ marginBottom: 24, color: 'var(--text-secondary)' }}>
              {selectedOrgId
                ? <>This permanently discards <strong>{selectedOrg?.name ?? 'this org'}&rsquo;s</strong> custom pricing, discounts, and billing mode override. It will immediately go back to inheriting the Global Default shown above. This cannot be undone.</>
                : <>This resets the <strong>Global Default</strong> config (pricing, discounts, billing mode) to factory values. Every organization currently WITHOUT its own override inherits this immediately. This cannot be undone.</>}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setResetConfirmOpen(false)} disabled={resetting}>Cancel</button>
              <button className="btn btn-danger" onClick={doReset} disabled={resetting}>{resetting ? 'Resetting…' : 'Reset'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Promotional AI credits — real, DB-backed (migration_v133 / server/src/routes/dev.ts),
          facility-scoped (not org-scoped like everything above). */}
      <PromoAiCreditsCard />

      {/* Discount codes — admin create/list; org-side redemption lives on Billing & Plan. */}
      <DiscountCodesCard />
    </div>
  );
}

// ─── Discount Codes (admin) ─────────────────────────────────────────────────────
// Create/list side of migration_v135's discount_codes table (server/src/routes/billing.ts).
// Redemption itself happens on the org's own Billing & Plan page (POST /api/billing/redeem),
// gated there to whichever role manages that org's plan under its own billing_mode.
interface DiscountCodeRow {
  id: number;
  code: string;
  discount_type: 'month' | 'year';
  discount_percent: string;
  created_at: string;
  used_by_org_id: number | null;
  used_by_org_name: string | null;
  used_at: string | null;
}

function DiscountCodesCard() {
  const { toast, showToast } = useToast();
  const qc = useQueryClient();
  const [discountType, setDiscountType] = useState<'month' | 'year'>('month');
  const [discountPercent, setDiscountPercent] = useState('10');
  const [creating, setCreating] = useState(false);

  const { data: codesRes } = useQuery({
    queryKey: ['billing-discount-codes'],
    queryFn: () => api.get<DiscountCodeRow[]>('/billing/discount-codes'),
  });
  const codes = (codesRes as unknown as { data: DiscountCodeRow[] })?.data ?? [];

  const [deletingId, setDeletingId] = useState<number | null>(null);

  async function handleCreate() {
    const pct = Number(discountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      showToast('Discount percent must be between 0 and 100', true);
      return;
    }
    setCreating(true);
    try {
      const res = await api.post<DiscountCodeRow>('/billing/discount-codes', {
        discount_type: discountType,
        discount_percent: pct,
      });
      const created = (res as unknown as { data: DiscountCodeRow }).data;
      showToast(`Code created: ${created.code}`);
      qc.invalidateQueries({ queryKey: ['billing-discount-codes'] });
    } catch {
      showToast('Failed to create discount code', true);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: number, code: string) {
    setDeletingId(id);
    try {
      await api.delete(`/billing/discount-codes/${id}`);
      showToast(`Code ${code} deleted`);
      qc.invalidateQueries({ queryKey: ['billing-discount-codes'] });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to delete code';
      showToast(message, true);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="card" style={{ padding: 16, marginTop: 24 }}>
      <Toast toast={toast} />
      <h4 style={{ margin: '0 0 4px' }}>Discount Codes</h4>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '0 0 14px' }}>
        A code replaces an org's volume/annual discount once redeemed (not stacked) -- 'month'
        substitutes the volume discount, 'year' substitutes the annual discount. Permanently spent
        after one use, by any org.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 12, alignItems: 'end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Type</label>
          <DarkSelect
            value={discountType}
            onChange={(v) => setDiscountType(v as 'month' | 'year')}
            options={[{ value: 'month', label: 'Month (replaces volume)' }, { value: 'year', label: 'Year (replaces annual)' }]}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Discount %</label>
          <input className="form-input" type="number" min={0} max={100} value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)} />
        </div>
        {/* justifySelf:'start' -- this button is a direct grid child; CSS grid's default
            justify-items:stretch was making it fill its whole column width like the two form
            fields beside it, instead of sizing to its own content like every other .btn in this
            panel (e.g. Reset to Factory Defaults, which isn't in a grid at all). */}
        <button className="btn btn-primary" disabled={creating} onClick={handleCreate} style={{ justifySelf: 'start' }}>
          {creating ? 'Creating…' : 'Create Code'}
        </button>
      </div>

      {codes.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Code</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>%</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Created</th>
                <th style={{ padding: '6px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{c.code}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{c.discount_type}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{c.discount_percent}%</td>
                  <td style={{ padding: '6px 8px' }}>
                    {c.used_by_org_id ? (
                      <span style={{ color: 'var(--text-secondary)' }}>
                        Used by <strong style={{ color: 'var(--text-primary)' }}>{c.used_by_org_name ?? `org #${c.used_by_org_id}`}</strong>
                        {c.used_at && ` on ${new Date(c.used_at).toLocaleDateString()}`}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--accent-success)', fontWeight: 600 }}>Unused</span>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    {/* Used codes aren't deletable -- server rejects it too (they're the permanent
                        record for whichever org redeemed them), so don't even offer the control. */}
                    {!c.used_by_org_id && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--accent-danger)' }}
                        disabled={deletingId === c.id}
                        onClick={() => handleDelete(c.id, c.code)}
                        title="Delete code"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Promotional AI credits (Task 3) ───────────────────────────────────────────
// Admin-settable per-facility bonus AI-extraction credits, on top of Pro's base 9/month limit --
// and the actual mechanism for letting a specific Standard-tier facility trial AI extraction
// without changing its plan_tier. Wired directly to GET/PUT /api/dev/ai-extraction-bonus
// (server/src/routes/dev.ts, same requireSuperAdmin boundary as Database Viewer) -- real data,
// consumed server-side by projects.ts's extract-proposal route.
interface AiBonusRow {
  facility_id: number;
  facility_name: string;
  org_id: number | null;
  org_name: string | null;
  bonus_credits: number;
  note: string | null;
  updated_at: string | null;
}

function PromoAiCreditsCard() {
  const { toast, showToast } = useToast();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [facilitySearch, setFacilitySearch] = useState('');
  const [facilityDropdownOpen, setFacilityDropdownOpen] = useState(false);
  const facilityPickerRef = useRef<HTMLDivElement>(null);
  const [bonusInput, setBonusInput] = useState('0');
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Org picker -- same GET /api/billing/orgs source BillingConfigTab's own org picker uses
  // (reusing that pattern rather than inventing a second one), scoping which facilities the
  // search below can even match instead of the old flat cross-org facility list.
  const { data: orgsRes } = useQuery({
    queryKey: ['billing-orgs'],
    queryFn: () => api.get<BillingOrgRow[]>('/billing/orgs'),
  });
  const orgs = (orgsRes as unknown as { data: BillingOrgRow[] })?.data ?? [];

  // Facility search — org-scoped via the existing GET /api/facilities?orgId= filter (already
  // supported server-side for the admin role, facilities.ts), not a new endpoint.
  const { data: facilitiesRes } = useQuery({
    queryKey: ['promo-ai-credits-facilities', selectedOrgId],
    queryFn: () => api.get<{ id: number; name: string }[]>('/facilities', { orgId: selectedOrgId }),
    enabled: !!selectedOrgId,
  });
  const orgFacilities = (facilitiesRes as unknown as { data: { id: number; name: string }[] })?.data ?? [];
  const filteredFacilities = facilitySearch.trim()
    ? orgFacilities.filter((f) => f.name.toLowerCase().includes(facilitySearch.trim().toLowerCase()))
    : orgFacilities;

  // Close the search dropdown on an outside click -- same pattern used elsewhere in this file.
  useEffect(() => {
    if (!facilityDropdownOpen) return;
    function handler(e: MouseEvent) {
      if (facilityPickerRef.current && !facilityPickerRef.current.contains(e.target as Node)) {
        setFacilityDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [facilityDropdownOpen]);

  function selectOrg(orgId: string) {
    setSelectedOrgId(orgId);
    setSelectedFacilityId('');
    setFacilitySearch('');
  }

  function selectFacility(facility: { id: number; name: string }) {
    setSelectedFacilityId(String(facility.id));
    setFacilitySearch(facility.name);
    setFacilityDropdownOpen(false);
  }

  const { data: bonusRes, refetch: refetchBonus } = useQuery({
    queryKey: ['promo-ai-credits-bonus'],
    queryFn: () => api.get<AiBonusRow[]>('/dev/ai-extraction-bonus'),
  });
  const bonusRows = (bonusRes as unknown as { data: AiBonusRow[] })?.data ?? [];
  const selectedRow = bonusRows.find((r) => String(r.facility_id) === selectedFacilityId);

  // Re-sync the form fields whenever the selected facility (or freshly-saved data) changes.
  useEffect(() => {
    setBonusInput(String(selectedRow?.bonus_credits ?? 0));
    setNoteInput(selectedRow?.note ?? '');
  }, [selectedFacilityId, selectedRow?.bonus_credits, selectedRow?.note]);

  async function handleSave() {
    if (!selectedFacilityId) return;
    setSaving(true);
    try {
      await api.put(`/dev/ai-extraction-bonus/${selectedFacilityId}`, {
        bonus_credits: Math.max(0, parseInt(bonusInput, 10) || 0),
        note: noteInput.trim() || null,
      });
      showToast('Promotional AI credits saved');
      await refetchBonus();
    } catch {
      showToast('Failed to save promotional AI credits', true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ padding: 16, marginTop: 24 }}>
      <Toast toast={toast} />
      <h4 style={{ margin: '0 0 4px' }}>Promotional AI Credits</h4>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '0 0 14px' }}>
        Per-facility bonus AI-extraction credits, on top of Pro's base 9/month. Also the trial
        mechanism for a Standard-tier facility — a bonus balance lets it through (Pro-equivalent
        access) up to that amount without changing its plan.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 12 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Organization</label>
          <DarkSelect
            value={selectedOrgId}
            onChange={selectOrg}
            placeholder="Select an organization…"
            options={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
          />
        </div>
        {/* Search-as-you-type, scoped to the selected org's facilities only -- not DarkSelect,
            which is click-to-open/click-to-pick with no typing/filter behavior of its own. */}
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }} ref={facilityPickerRef}>
          <label className="form-label">Facility</label>
          <input
            className="form-input"
            type="text"
            placeholder={selectedOrgId ? 'Type to search…' : 'Select an organization first'}
            disabled={!selectedOrgId}
            value={facilitySearch}
            onFocus={() => setFacilityDropdownOpen(true)}
            onChange={(e) => {
              setFacilitySearch(e.target.value);
              setFacilityDropdownOpen(true);
              if (selectedFacilityId) setSelectedFacilityId(''); // typing again starts a fresh pick
            }}
          />
          {facilityDropdownOpen && selectedOrgId && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 20,
              background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
              maxHeight: 220, overflowY: 'auto',
            }}>
              {filteredFacilities.length === 0 ? (
                <div style={{ padding: '8px 12px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No matching facilities</div>
              ) : filteredFacilities.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => selectFacility(f)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                    background: String(f.id) === selectedFacilityId ? 'var(--bg-hover)' : 'transparent',
                    border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8125rem',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = String(f.id) === selectedFacilityId ? 'var(--bg-hover)' : 'transparent'; }}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Bonus credits / month</label>
          <input className="form-input" type="number" min={0} value={bonusInput}
            disabled={!selectedFacilityId}
            onChange={(e) => setBonusInput(e.target.value)} />
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Note (optional — e.g. "Holiday promo Dec 2026" or "Buckingham Valley trial")</label>
        <input className="form-input" type="text" value={noteInput}
          disabled={!selectedFacilityId}
          onChange={(e) => setNoteInput(e.target.value)} />
      </div>
      {selectedRow?.updated_at && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '0 0 12px' }}>
          Last updated {new Date(selectedRow.updated_at).toLocaleString()}
        </p>
      )}
      <button className="btn btn-primary" disabled={!selectedFacilityId || saving} onClick={handleSave}>
        {saving ? 'Saving…' : 'Save'}
      </button>

      {bonusRows.some((r) => r.bonus_credits > 0) && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>
            Facilities with an active bonus
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bonusRows.filter((r) => r.bonus_credits > 0).map((r) => (
              <div key={r.facility_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.8125rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {r.facility_name}{r.org_name ? ` (${r.org_name})` : ''}{r.note ? ` — ${r.note}` : ''}
                </span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, flexShrink: 0 }}>+{r.bonus_credits}/mo</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Permissions Tab ──────────────────────────────────────────────────────────
// Generic role-permission config editor, backed by permission_configs (migration_v120.sql /
// server/src/routes/permissions.ts). Built to add more permission types later without a
// redesign — PERMISSION_DEFS is the only thing that needs a new entry; the card/toggle rendering
// below is already generic over it. Admin-only (isSuperAdmin gate in DevelopmentTab above) both
// client-side here and server-side (requireSuperAdmin on the /api/permissions routes).
type PermissionType = 'wo_edit_closed' | 'wo_delete' | 'logbook_template_edit_delete' | 'project_advance_without_bid';
interface PermissionConfig {
  id: number;
  permission_type: PermissionType;
  allowed_roles: string[];
  updated_at: string;
  updated_by: number | null;
}
const ALL_ROLES: { value: string; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'manager', label: 'Manager' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'viewer', label: 'Viewer' },
];
const ROLE_LABEL: Record<string, string> = Object.fromEntries(ALL_ROLES.map(r => [r.value, r.label]));
// lockedRoles = permanently allowed, not stored/configurable (only wo_edit_closed has one today —
// Admin/Org Admin's override is enforced in code, work-orders.ts, not this table).
const PERMISSION_DEFS: { type: PermissionType; label: string; description: string; lockedRoles?: string[] }[] = [
  {
    type: 'wo_edit_closed',
    label: 'Edit a Closed/Completed Work Order',
    description: 'Allows editing a work order after it has been marked Done or Cancelled. Admin and Org Admin can always do this.',
    lockedRoles: ['admin', 'org_admin'],
  },
  {
    type: 'wo_delete',
    label: 'Delete a Work Order',
    description: 'Allows permanently deleting a work order.',
  },
  {
    type: 'logbook_template_edit_delete',
    label: 'Edit or Delete a Logbook Template',
    description: 'Allows editing or deleting a logbook template (does not include already-submitted logbook entries).',
  },
  {
    type: 'project_advance_without_bid',
    label: 'Advance a Project Past a Stage Without an Approved Bid',
    description: 'Allows advancing a project from Approved to Scheduling without first selecting a recommended bid.',
  },
];

function PermissionsTab() {
  const qc = useQueryClient();
  const { toast, showToast } = useToast();
  const [savingType, setSavingType] = useState<PermissionType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['permission-configs'],
    queryFn: () => api.get<PermissionConfig[]>('/permissions'),
  });
  const configs = data?.data ?? [];

  async function toggleRole(type: PermissionType, role: string, currentRoles: string[]) {
    const nextRoles = currentRoles.includes(role) ? currentRoles.filter(r => r !== role) : [...currentRoles, role];
    setSavingType(type);
    try {
      await api.put(`/permissions/${type}`, { allowed_roles: nextRoles });
      await qc.invalidateQueries({ queryKey: ['permission-configs'] });
      showToast('Permission updated');
    } catch (err: any) {
      showToast(err?.response?.data?.error ?? 'Update failed', true);
    }
    setSavingType(null);
  }

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: '1rem' }}>Permissions</div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
          Control which roles can perform specific actions. Changes take effect immediately for every user.
        </div>
      </div>
      <Toast toast={toast} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {PERMISSION_DEFS.map(def => {
          const config = configs.find(c => c.permission_type === def.type);
          const allowedRoles = config?.allowed_roles ?? [];
          const toggleableRoles = ALL_ROLES.filter(r => !def.lockedRoles?.includes(r.value));
          const saving = savingType === def.type;
          return (
            <div key={def.type} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{def.label}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2, marginBottom: 12 }}>{def.description}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {def.lockedRoles?.map(role => (
                  <span
                    key={role}
                    title="Always allowed — not configurable"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '7px 14px', borderRadius: 9, fontSize: '0.8rem', fontWeight: 500,
                      border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-muted)',
                    }}
                  >
                    <Lock size={11} /> {ROLE_LABEL[role]}
                  </span>
                ))}
                {toggleableRoles.map(r => {
                  const on = allowedRoles.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      disabled={saving}
                      className={`category-pill${on ? ' active' : ''}`}
                      onClick={() => toggleRole(def.type, r.value, allowedRoles)}
                      style={{
                        padding: '7px 14px', borderRadius: 9, fontSize: '0.8rem', fontWeight: 500,
                        cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
                        border: on ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-default)',
                        background: on ? 'rgba(59,130,246,0.13)' : 'var(--bg-elevated)',
                        color: on ? '#93c5fd' : 'var(--text-secondary)',
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Used Emails Tab ──────────────────────────────────────────────────────────
const VENDOR_EMAILS_KEY = 'proqure_vendor_emails';

function UsedEmailsTab() {
  const [emails, setEmails] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(VENDOR_EMAILS_KEY) || '[]') as string[];
    } catch {
      return [];
    }
  });
  const [confirmClear, setConfirmClear] = useState(false);

  function removeEmail(email: string) {
    const updated = emails.filter((e) => e !== email);
    localStorage.setItem(VENDOR_EMAILS_KEY, JSON.stringify(updated));
    setEmails(updated);
  }

  function clearAll() {
    localStorage.removeItem(VENDOR_EMAILS_KEY);
    setEmails([]);
    setConfirmClear(false);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '1rem' }}>Vendor Email History</div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
            Emails saved from vendor share actions. Used for autocomplete suggestions.
          </div>
        </div>
        {emails.length > 0 && !confirmClear && (
          <button
            className="combo3-danger"
            onClick={() => setConfirmClear(true)}
            style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
          >
            Clear all
          </button>
        )}
        {confirmClear && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Clear all saved emails?</span>
            {/* Solid destructive confirm -- base btn-danger, not combo3-danger (that's the
                outline/ghost row-action style) and not combo2 (blue pulse ring would contradict
                the danger signal) -- same established exception noted in BUTTON_SYSTEM.md. */}
            <button className="btn btn-danger btn-sm" onClick={clearAll}>
              Yes, clear
            </button>
            <button className="btn btn-ghost btn-sm combo1" onClick={() => setConfirmClear(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {emails.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          No emails saved yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {emails.map((email) => (
            <div
              key={email}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 14px', borderRadius: 8,
                background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
              }}
            >
              <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{email}</span>
              <button
                className="combo-jello-spin"
                data-tint="red"
                onClick={() => removeEmail(email)}
                onMouseLeave={spinIconOut}
                style={{
                  border: 'none', cursor: 'pointer', padding: '2px 6px',
                  color: '#f87171', borderRadius: 4,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
                title="Remove"
              >
                <X size={13} className="btn-icon" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dev Todos Tab ────────────────────────────────────────────────────────────
interface DevTodo {
  id: number;
  title: string;
  description: string | null;
  category: string;
  priority: 'high' | 'medium' | 'low';
  status: 'open' | 'in_progress' | 'done';
  sort_order: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#6b7280',
};
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  open:        { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
  in_progress: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  done:        { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e' },
};

function DevTodosTab() {
  const qc = useQueryClient();
  const { data: todosRes, isLoading } = useQuery({
    queryKey: ['dev-todos'],
    queryFn: () => api.get<DevTodo[]>('/dev-todos'),
  });
  const todos: DevTodo[] = (todosRes as unknown as { data: DevTodo[] })?.data ?? [];

  const [filterCat, setFilterCat] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [searchQ, setSearchQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', description: '', category: '', newCategory: '', priority: 'medium', status: 'open' });
  const [addSaving, setAddSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: '', newCategory: '', priority: 'medium', status: 'open' });
  const [editSaving, setEditSaving] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());

  const categories = Array.from(new Set(todos.map(t => t.category))).sort();

  const filtered = todos.filter(t => {
    if (filterCat && t.category !== filterCat) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (searchQ && !t.title.toLowerCase().includes(searchQ.toLowerCase()) && !(t.description ?? '').toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  const grouped = filtered.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {} as Record<string, DevTodo[]>);

  function getCategory(form: typeof addForm) {
    return form.newCategory.trim() || form.category || 'General';
  }

  async function handleAdd() {
    if (!addForm.title.trim()) return;
    setAddSaving(true);
    try {
      await api.post('/dev-todos', {
        title: addForm.title.trim(),
        description: addForm.description.trim() || null,
        category: getCategory(addForm),
        priority: addForm.priority,
      });
      qc.invalidateQueries({ queryKey: ['dev-todos'] });
      setShowAdd(false);
      setAddForm({ title: '', description: '', category: '', newCategory: '', priority: 'medium', status: 'open' });
    } finally { setAddSaving(false); }
  }

  async function handleUpdate(id: number, patch: Partial<DevTodo>) {
    await api.put(`/dev-todos/${id}`, patch);
    qc.invalidateQueries({ queryKey: ['dev-todos'] });
  }

  async function handleToggleDone(todo: DevTodo) {
    const next = todo.status === 'done' ? 'open' : 'done';
    await handleUpdate(todo.id, { status: next });
  }

  async function handleDelete(id: number) {
    await api.delete(`/dev-todos/${id}`);
    qc.invalidateQueries({ queryKey: ['dev-todos'] });
  }

  function openEdit(todo: DevTodo) {
    setEditId(todo.id);
    setEditForm({ title: todo.title, description: todo.description ?? '', category: todo.category, newCategory: '', priority: todo.priority, status: todo.status });
  }

  async function saveEdit() {
    if (!editId || !editForm.title.trim()) return;
    setEditSaving(true);
    try {
      await api.put(`/dev-todos/${editId}`, {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        category: getCategory(editForm),
        priority: editForm.priority,
        status: editForm.status,
      });
      qc.invalidateQueries({ queryKey: ['dev-todos'] });
      setEditId(null);
    } finally { setEditSaving(false); }
  }

  function toggleCat(cat: string) {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }

  const totalOpen = todos.filter(t => t.status !== 'done').length;
  const totalDone = todos.filter(t => t.status === 'done').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0 }}>Dev Todos</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {totalOpen} open · {totalDone} done · {todos.length} total
            </p>
          </div>
          <button className="btn btn-primary btn-sm combo2" onClick={() => setShowAdd(s => !s)} onMouseLeave={spinIconOut}>
            <Plus size={14} className="btn-icon" /> Add Todo
          </button>
        </div>

        {/* Add form */}
        {showAdd && (
          <div style={{ padding: 16, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.875rem' }}>New Todo</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="form-input" placeholder="Title *" value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} />
              <input className="form-input" placeholder="Description" value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Category</label>
                  <select className="form-input" value={addForm.category} onChange={e => setAddForm(f => ({ ...f, category: e.target.value, newCategory: '' }))}>
                    <option value="">— existing —</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>New Category</label>
                  <input className="form-input" placeholder="Or type new…" value={addForm.newCategory} onChange={e => setAddForm(f => ({ ...f, newCategory: e.target.value, category: '' }))} />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Priority</label>
                  <select className="form-input" value={addForm.priority} onChange={e => setAddForm(f => ({ ...f, priority: e.target.value }))}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm combo2" disabled={addSaving || !addForm.title.trim()} onClick={handleAdd}>
                  {addSaving ? 'Saving…' : 'Add'}
                </button>
                <button className="btn btn-ghost btn-sm combo1" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 140 }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input className="form-input" placeholder="Search…" value={searchQ} onChange={e => setSearchQ(e.target.value)} style={{ paddingLeft: 28, fontSize: '0.8125rem' }} />
          </div>
          <select className="form-input" style={{ flex: '0 1 170px', fontSize: '0.8125rem' }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="form-input" style={{ flex: '0 1 130px', fontSize: '0.8125rem' }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select className="form-input" style={{ flex: '0 1 130px', fontSize: '0.8125rem' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
          </select>
        </div>
      </div>

      {/* Todo list grouped by category */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="loading-spinner" /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No todos match your filters.</div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Category header */}
            <div
              onClick={() => toggleCat(cat)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                cursor: 'pointer', borderBottom: collapsedCats.has(cat) ? 'none' : '1px solid var(--border-subtle)',
                background: 'rgba(59,130,246,0.05)',
              }}
            >
              <span style={{ flex: 1, fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{cat}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {items.filter(i => i.status !== 'done').length} open / {items.length}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{collapsedCats.has(cat) ? '▶' : '▼'}</span>
            </div>

            {/* Items */}
            {!collapsedCats.has(cat) && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {items.map((todo, idx) => (
                  <div key={todo.id} style={{
                    borderLeft: `3px solid ${PRIORITY_COLORS[todo.priority] ?? '#6b7280'}`,
                    borderBottom: idx < items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    padding: '12px 16px',
                    opacity: todo.status === 'done' ? 0.6 : 1,
                  }}>
                    {editId === todo.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input className="form-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Title *" />
                        <input className="form-input" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.75rem' }}>Category</label>
                            <select className="form-input" value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value, newCategory: '' }))}>
                              {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.75rem' }}>New Cat</label>
                            <input className="form-input" placeholder="Override…" value={editForm.newCategory} onChange={e => setEditForm(f => ({ ...f, newCategory: e.target.value }))} />
                          </div>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.75rem' }}>Priority</label>
                            <select className="form-input" value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}>
                              <option value="high">High</option>
                              <option value="medium">Medium</option>
                              <option value="low">Low</option>
                            </select>
                          </div>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.75rem' }}>Status</label>
                            <select className="form-input" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="done">Done</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-sm combo2" disabled={editSaving || !editForm.title.trim()} onClick={saveEdit}>{editSaving ? '…' : 'Save'}</button>
                          <button className="btn btn-ghost btn-sm combo1" onClick={() => setEditId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={todo.status === 'done'}
                          onChange={() => handleToggleDone(todo)}
                          style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer', accentColor: '#22c55e' }}
                        />
                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              fontWeight: 500, fontSize: '0.9rem',
                              textDecoration: todo.status === 'done' ? 'line-through' : 'none',
                              color: todo.status === 'done' ? 'var(--text-muted)' : 'var(--text-primary)',
                            }}>
                              {todo.title}
                            </span>
                            <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: `${PRIORITY_COLORS[todo.priority]}20`, color: PRIORITY_COLORS[todo.priority], border: `1px solid ${PRIORITY_COLORS[todo.priority]}40` }}>
                              {todo.priority}
                            </span>
                            <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, ...STATUS_COLORS[todo.status] }}>
                              {todo.status.replace('_', ' ')}
                            </span>
                          </div>
                          {todo.description && (
                            <div style={{ marginTop: 3, fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                              {todo.description}
                            </div>
                          )}
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button className="btn btn-ghost btn-sm combo-jello-spin" data-tint="neutral" style={{ padding: '4px 6px' }} onClick={() => openEdit(todo)} onMouseLeave={spinIconOut}><Pencil size={13} className="btn-icon" /></button>
                          <button className="btn btn-ghost btn-sm combo-jello-spin" data-tint="red" style={{ padding: '4px 6px', color: 'var(--accent-danger)' }} onClick={() => handleDelete(todo.id)} onMouseLeave={spinIconOut}><Trash2 size={13} className="btn-icon" /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Facility Tab ─────────────────────────────────────────────────────────────

const TIMEZONES = [
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Phoenix','America/Anchorage','Pacific/Honolulu',
];
const FACILITY_TYPES = [
  'Assisted Living','Skilled Nursing','Memory Care','Independent Living',
  'Continuing Care Retirement Community','Adult Day Care','Rehabilitation',
  'Long-Term Care','Other',
];

// ─── FacilityFormCard: top-level component (NOT nested) to avoid focus loss ───
interface FacilityFormCardProps {
  fac: import('../types/index.ts').Facility;
  readOnly: boolean;
  isEditing: boolean;
  form: Partial<import('../types/index.ts').Facility>;
  onField: (k: keyof import('../types/index.ts').Facility, v: unknown) => void;
  onSave: (e: React.FormEvent) => void;
  onEdit: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving: boolean;
  savedOk: boolean;
  saveError: string | null;
  isAdmin: boolean;
  logoRef: React.RefObject<HTMLInputElement>;
  onLogoUrlSave: (facilityId: number, url: string) => Promise<void>;
  onRemoveLogo: (facilityId: number) => Promise<void>;
}

function FacilityFormCard({ fac, readOnly, isEditing, form, onField, onSave, onEdit, onCancel, onDelete, saving, savedOk, saveError, isAdmin, logoRef, onLogoUrlSave, onRemoveLogo }: FacilityFormCardProps) {
  const [logoMode, setLogoMode] = useState<'file' | 'url'>('file');
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoZoomed, setLogoZoomed] = useState(false);

  const currentLogo = getImageSrc(fac.logo_url ?? fac.logo_path);

  async function handleSaveLogoUrl() {
    if (!logoUrlInput.trim()) return;
    setLogoSaving(true);
    try { await onLogoUrlSave(fac.id, logoUrlInput.trim()); setLogoUrlInput(''); } finally { setLogoSaving(false); }
  }
  async function handleRemove() {
    setLogoSaving(true);
    try { await onRemoveLogo(fac.id); } finally { setLogoSaving(false); }
  }

  const data = isEditing ? form : fac;
  const inp = (k: keyof import('../types/index.ts').Facility) => ({
    className: 'form-input',
    disabled: readOnly || !isEditing,
    value: (data[k] as string) ?? '',
    onChange: isEditing ? (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onField(k, e.target.value) : undefined,
    style: readOnly ? { opacity: 0.75, cursor: 'not-allowed' } as React.CSSProperties : undefined,
    autoComplete: 'new-password' as const,
    readOnly: isEditing,
    onFocus: isEditing ? (e: React.FocusEvent<HTMLInputElement>) => e.target.removeAttribute('readOnly') : undefined,
  });

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      {/* Logo + name row */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20 }}>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div
            // Click-to-enlarge -- same modal-overlay/portal pattern as LogbookTemplates.tsx's
            // template preview zoom (previewZoomed), reused here rather than a new lightbox.
            onClick={currentLogo ? () => setLogoZoomed(true) : undefined}
            title={currentLogo ? 'Click to enlarge' : undefined}
            style={{
              width: 80, height: 80, borderRadius: 'var(--radius-md)',
              // White (not the theme's dark var(--bg-elevated)) whenever there's an actual logo to
              // show -- matches the zoom modal's own background (below), which is already white
              // for exactly this reason. Dark navy line-art logos on a transparent background were
              // nearly invisible against the app's dark theme here, even though the same logo
              // rendered fine in the zoom modal. Empty state (no logo yet) keeps the dark themed
              // background so the placeholder Building2 icon still reads correctly against it.
              background: currentLogo ? '#fff' : 'var(--bg-elevated)',
              border: '2px dashed var(--border-default)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', position: 'relative',
              cursor: currentLogo ? 'zoom-in' : 'default',
            }}
          >
            {currentLogo ? (
              <img src={currentLogo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <Building2 size={32} color="var(--text-muted)" style={{ opacity: 0.3 }} />
            )}
          </div>
          {/* Logo controls — always visible to non-readOnly admins. Widened from the previous
              width:80 (matched to the logo box above it) to 150 -- that 80px column was forcing
              every button/toggle here down to 0.65-0.7rem font and 3-4px padding to fit, fighting
              .btn-sm's own sane defaults (padding:5px 10px, font-size:0.8125rem, global.css:194)
              instead of just using them. Toggle now reuses the app's existing category-pill
              mechanics (buttonAnimations.css) instead of a one-off unstyled pair of buttons with
              no hover/press feedback at all. */}
          {!readOnly && isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 150 }}>
              {/* Toggle */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className={`category-pill${logoMode === 'file' ? ' active' : ''}`}
                  onClick={() => setLogoMode('file')}
                  style={{
                    flex: 1, padding: '5px 0', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                    border: logoMode === 'file' ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-default)',
                    background: logoMode === 'file' ? 'rgba(59,130,246,0.13)' : 'var(--bg-elevated)',
                    color: logoMode === 'file' ? '#93c5fd' : 'var(--text-secondary)',
                  }}
                >File</button>
                <button
                  type="button"
                  className={`category-pill${logoMode === 'url' ? ' active' : ''}`}
                  onClick={() => setLogoMode('url')}
                  style={{
                    flex: 1, padding: '5px 0', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                    border: logoMode === 'url' ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-default)',
                    background: logoMode === 'url' ? 'rgba(59,130,246,0.13)' : 'var(--bg-elevated)',
                    color: logoMode === 'url' ? '#93c5fd' : 'var(--text-secondary)',
                  }}
                >URL</button>
              </div>
              {logoMode === 'file' ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm combo1"
                  style={{ width: '100%' }}
                  disabled={logoSaving}
                  onMouseLeave={spinIconOut}
                  onClick={() => { if (logoRef.current) { logoRef.current.dataset.fid = String(fac.id); logoRef.current.click(); } }}
                >
                  <Upload size={13} className="btn-icon" /> Upload
                </button>
              ) : null}
              {logoMode === 'file' && (
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.3 }}>
                  PNG or SVG recommended — supports transparent backgrounds
                </div>
              )}
              {logoMode === 'url' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input
                    className="form-input"
                    value={logoUrlInput}
                    onChange={(e) => setLogoUrlInput(e.target.value)}
                    placeholder="https://..."
                    style={{ fontSize: '0.8125rem' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveLogoUrl()}
                  />
                  <button type="button" className="btn btn-primary btn-sm combo2" style={{ width: '100%' }} disabled={!logoUrlInput.trim() || logoSaving} onMouseLeave={spinIconOut} onClick={handleSaveLogoUrl}>Save URL</button>
                </div>
              ) : null}
              {currentLogo && (
                <button type="button" className="btn btn-sm combo3-danger" style={{ width: '100%' }} disabled={logoSaving} onMouseLeave={spinIconOut} onClick={handleRemove}>
                  <Trash2 size={13} className="btn-icon" /> Remove
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>{fac.name}</h3>
              <span className={`badge ${fac.is_active ? 'badge-green' : 'badge-gray'}`}>{fac.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            {isAdmin && !isEditing && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={onEdit}>
                  <Pencil size={14} /> Edit
                </button>
                {onDelete && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={onDelete}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
          {readOnly && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)' }}>
              <Lock size={13} color="var(--accent-warning)" />
              <span style={{ fontSize: '0.8125rem', color: 'var(--accent-warning)' }}>Contact your administrator to make changes</span>
            </div>
          )}
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={onSave} autoComplete="off">
          {/* Dummy fields to trick browser autofill */}
          <input type="text" style={{ display: 'none' }} autoComplete="username" />
          <input type="password" style={{ display: 'none' }} autoComplete="new-password" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Facility Name</label>
              <input {...inp('name') as React.InputHTMLAttributes<HTMLInputElement>} name="facility_name" data-lpignore="true" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Address</label>
              <input {...inp('address') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_address" placeholder="Street address" data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input {...inp('city') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_city" placeholder="City" data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input {...inp('state') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_state" placeholder="IL" maxLength={2} data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input {...inp('zip') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_zip" placeholder="62701" data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input {...inp('phone') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_phone" placeholder="(217) 555-0100" data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" {...inp('email') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_email" placeholder="info@facility.com" data-lpignore="true" />
            </div>
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-select" value={(form.timezone as string) ?? ''} onChange={(e) => onField('timezone', e.target.value)} autoComplete="new-password">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Facility Type</label>
              <select className="form-select" value={(form.facility_type as string) ?? ''} onChange={(e) => onField('facility_type', e.target.value)} autoComplete="new-password">
                <option value="">Select…</option>
                {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Number of Beds</label>
              <input
                type="number" min={0} className="form-input" name="fac_beds"
                value={(form.bed_count as number) ?? ''}
                onChange={(e) => onField('bed_count', e.target.value ? Number(e.target.value) : null)}
                autoComplete="new-password" readOnly={true}
                onFocus={(e) => e.target.removeAttribute('readOnly')} data-lpignore="true"
              />
            </div>
            <div className="form-group">
              <label className="form-label">License Number</label>
              <input {...inp('license_number') as React.InputHTMLAttributes<HTMLInputElement>} name="fac_license" placeholder="State license #" data-lpignore="true" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Status</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!(form.is_active ?? true)} onChange={(e) => onField('is_active', e.target.checked)} />
                <span style={{ fontSize: '0.875rem' }}>Active</span>
              </label>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows={3} value={(form.notes as string) ?? ''} onChange={(e) => onField('notes', e.target.value)} style={{ minHeight: 72 }} autoComplete="new-password" />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            {savedOk && <span style={{ color: 'var(--accent-success)', fontSize: '0.875rem' }}>✓ Facility updated successfully</span>}
            {saveError && <span style={{ color: 'var(--accent-danger)', fontSize: '0.875rem' }}>✗ {saveError}</span>}
          </div>
        </form>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: '0.875rem' }}>
          {[
            ['Address', [fac.address, fac.city, fac.state, fac.zip].filter(Boolean).join(', ')],
            ['Phone', fac.phone],
            ['Email', fac.email],
            ['Type', fac.facility_type],
            ['Beds', fac.bed_count],
            ['License', fac.license_number],
            ['Timezone', fac.timezone],
          ].filter(([, v]) => v).map(([label, val]) => (
            <div key={String(label)}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>{label}</div>
              <div style={{ color: 'var(--text-primary)' }}>{String(val)}</div>
            </div>
          ))}
          {fac.notes && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 2 }}>Notes</div>
              <div style={{ color: 'var(--text-secondary)' }}>{fac.notes}</div>
            </div>
          )}
        </div>
      )}

      {logoZoomed && currentLogo && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setLogoZoomed(false)}>
          <div className="modal" style={{ maxWidth: 480, width: '92vw', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '14px 18px', flexShrink: 0 }}>
              <h3 className="modal-title">{fac.name} — Logo</h3>
              <button className="modal-close" onClick={() => setLogoZoomed(false)}><X size={18} /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
              <img src={currentLogo} alt="logo" style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function FacilityTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isOrgAdmin = user?.role === 'org_admin';
  const logoRef = useRef<HTMLInputElement>(null);

  // Admin org picker
  const [selectedOrgId, setSelectedOrgId] = useState<number>(0);
  const { data: allOrgsRes } = useQuery({
    queryKey: ['facility-tab-orgs'],
    queryFn: () => api.get<OrgRow[]>('/organizations'),
    enabled: isAdmin,
  });
  const allOrgs: OrgRow[] = (allOrgsRes as unknown as { data: OrgRow[] })?.data ?? [];

  const facilitiesQueryKey = isAdmin
    ? ['facilities', selectedOrgId]
    : ['facilities'];

  const { data: facilitiesRes, refetch } = useQuery({
    queryKey: facilitiesQueryKey,
    queryFn: () => {
      if (isAdmin && selectedOrgId) {
        return api.get<import('../types/index.ts').Facility[]>(`/facilities?orgId=${selectedOrgId}`);
      }
      return api.get<import('../types/index.ts').Facility[]>('/facilities');
    },
  });

  const allFacilities = facilitiesRes?.data ?? [];
  const ownFacility = isAdmin
    ? allFacilities[0]
    : (allFacilities.find((f) => f.id === user?.facilityId) ?? allFacilities[0]);
  const sisterFacilities = isAdmin
    ? allFacilities.slice(1)
    : allFacilities.filter((f) => f.id !== user?.facilityId);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<import('../types/index.ts').Facility>>({});
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [newFacilityName, setNewFacilityName] = useState('');
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const [addingSister, setAddingSister] = useState(false);
  const [deleteFacilityConfirm, setDeleteFacilityConfirm] = useState<import('../types/index.ts').Facility | null>(null);
  const [deleteFacilityError, setDeleteFacilityError] = useState<string | null>(null);

  async function handleDeleteFacility(fac: import('../types/index.ts').Facility) {
    setDeleteFacilityError(null);
    try {
      await api.delete(`/facilities/${fac.id}`);
      await refetch();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to delete facility.';
      setDeleteFacilityError(msg);
    }
    setDeleteFacilityConfirm(null);
  }

  function openEdit(fac: import('../types/index.ts').Facility) {
    setEditingId(fac.id);
    setForm({ ...fac });
    setSavedOk(false);
    setSaveError(null);
  }

  const onField = (k: keyof import('../types/index.ts').Facility, v: unknown) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`/facilities/${editingId}`, {
        name: form.name, address: form.address, city: form.city,
        state: form.state, zip: form.zip, phone: form.phone,
        email: form.email, timezone: form.timezone, facility_type: form.facility_type,
        bed_count: form.bed_count, license_number: form.license_number,
        notes: form.notes, is_active: form.is_active,
      });
      await refetch();
      setEditingId(null);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to save. Please try again.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(facilityId: number, file: File) {
    const fd = new FormData();
    fd.append('logo', file);
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/facilities/${facilityId}/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    await refetch();
  }

  async function handleSaveLogoUrl(facilityId: number, url: string) {
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/facilities/${facilityId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ logo_url: url }),
    });
    await refetch();
  }

  async function handleRemoveLogo(facilityId: number) {
    const token = localStorage.getItem('accessToken');
    await fetch(`/api/facilities/${facilityId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ logo_url: null }),
    });
    await refetch();
  }

  async function handleAddFacility(e: React.FormEvent) {
    e.preventDefault();
    if (!newFacilityName.trim()) return;
    setAddingSister(true);
    try {
      await api.post('/facilities', { name: newFacilityName.trim() });
      await refetch();
      setShowAddFacility(false);
      setNewFacilityName('');
    } catch {
      // silent
    } finally {
      setAddingSister(false);
    }
  }

  // For admin: require an org to be selected before showing facilities
  if (!isAdmin && !ownFacility) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Admin: org picker */}
      {isAdmin && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Organization</label>
            <select
              className="form-input"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(Number(e.target.value))}
              style={{ maxWidth: 360 }}
            >
              <option value={0}>— Select Organization —</option>
              {allOrgs.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Show facilities only when an org is selected (admin) or always (org_admin) */}
      {(isAdmin && !selectedOrgId) ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)' }}>
          Select an organization above to manage its facilities.
        </div>
      ) : !ownFacility ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <div className="loading-spinner" />
        </div>
      ) : (
      <>
      <input
        ref={logoRef}
        type="file"
        // Explicit list matching server/src/routes/facilities.ts's logoUpload fileFilter exactly
        // (image/jpeg|png|svg+xml|webp) -- was the generic "image/*", which let the OS picker
        // offer formats the server would reject (e.g. HEIC/AVIF/BMP) instead of narrowing to
        // exactly what's actually accepted.
        accept="image/png,image/svg+xml,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          const fid = Number(e.target.dataset.fid);
          if (file && fid) { await handleLogoUpload(fid, file); }
          e.target.value = '';
        }}
      />

      {/* Own / primary facility */}
      <FacilityFormCard
        fac={ownFacility} readOnly={!isAdmin && !isOrgAdmin}
        isEditing={editingId === ownFacility.id}
        form={form} onField={onField} onSave={handleSave}
        onEdit={() => openEdit(ownFacility)}
        onCancel={() => { setEditingId(null); setSaveError(null); }}
        saving={saving} savedOk={savedOk} saveError={saveError}
        isAdmin={isAdmin} logoRef={logoRef}
        onLogoUrlSave={handleSaveLogoUrl}
        onRemoveLogo={handleRemoveLogo}
      />

      {/* Sister facilities (admin / org_admin) */}
      {(isAdmin || isOrgAdmin) && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Sister Facilities</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddFacility(true)}>
              <Plus size={14} /> Add Facility
            </button>
          </div>

          {sisterFacilities.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)' }}>
              No other facilities in the organization.
            </div>
          ) : (
            sisterFacilities.map((fac) => (
              <FacilityFormCard key={fac.id}
                fac={fac} readOnly={false}
                isEditing={editingId === fac.id}
                form={form} onField={onField} onSave={handleSave}
                onEdit={() => openEdit(fac)}
                onCancel={() => { setEditingId(null); setSaveError(null); }}
                onDelete={() => setDeleteFacilityConfirm(fac)}
                saving={saving} savedOk={savedOk} saveError={saveError}
                isAdmin={isAdmin} logoRef={logoRef}
                onLogoUrlSave={handleSaveLogoUrl}
                onRemoveLogo={handleRemoveLogo}
              />
            ))
          )}

          {/* Delete facility error */}
          {deleteFacilityError && (
            <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.875rem' }}>
              {deleteFacilityError}
            </div>
          )}

          {/* Delete facility confirm */}
          {deleteFacilityConfirm && (
            <div className="modal-overlay">
              <div className="modal" style={{ maxWidth: 380, width: '95vw' }}>
                <h3 style={{ marginBottom: 12 }}>Delete Facility?</h3>
                <p style={{ marginBottom: 24, color: 'var(--text-secondary)' }}>
                  Delete <strong>{deleteFacilityConfirm.name}</strong>? This will remove the facility permanently.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setDeleteFacilityConfirm(null)}>Cancel</button>
                  <button className="btn btn-danger" onClick={() => handleDeleteFacility(deleteFacilityConfirm)}>Delete</button>
                </div>
              </div>
            </div>
          )}

          {/* Add Facility modal */}
          {showAddFacility && (
            <div className="modal-overlay"
              onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
              onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setShowAddFacility(false); mouseDownTarget.current = null; }}
            >
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                <div className="modal-header">
                  <h3 className="modal-title">Add New Facility</h3>
                  <button className="modal-close" onClick={() => setShowAddFacility(false)}><X size={18} /></button>
                </div>
                <form onSubmit={handleAddFacility} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Facility Name *</label>
                    <input className="form-input" type="text" required value={newFacilityName} onChange={(e) => setNewFacilityName(e.target.value)} autoFocus placeholder="e.g. Sunrise Gardens East" />
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
                    You can fill in all other details after creating the facility.
                  </p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => setShowAddFacility(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={addingSister || !newFacilityName.trim()}>
                      {addingSister ? 'Creating…' : 'Create Facility'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ─── Orders Settings Tab ───────────────────────────────────────────────────────

const DEFAULT_ORDER_SUBJECT = 'Order Approval Request - {item_name}';
const DEFAULT_ORDER_BODY =
  `Hello,\n\nI am requesting approval for the following order:\n\nItem: {item_name}\nSupplier: {supplier}\nQuantity: {qty}\nUnit Price: ${'{unit_price}'}\nPO#: {po_number}\n\nPlease review and approve.\n\nThank you,\n{requester_name}`;

const DEFAULT_SUPPLIERS = KNOWN_SUPPLIERS;

function OrderSuppliersCard() {
  const { data: res, refetch } = useQuery({
    queryKey: ['order-suppliers'],
    queryFn: () => api.get<Record<string, string>>('/settings/facility-settings', { keys: 'order_suppliers' }),
  });
  const raw = (res as unknown as { data: Record<string, string> })?.data?.['order_suppliers'];
  const [suppliers, setSuppliers] = useState<string[] | null>(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialise once from server (or use defaults)
  if (suppliers === null && res !== undefined) {
    try {
      setSuppliers(raw ? JSON.parse(raw) : [...DEFAULT_SUPPLIERS]);
    } catch { setSuppliers([...DEFAULT_SUPPLIERS]); }
  }
  const list = suppliers ?? [...DEFAULT_SUPPLIERS];

  async function saveList(next: string[]) {
    setSaving(true);
    try {
      await api.post('/settings/facility-settings', { settings: { order_suppliers: JSON.stringify(next) } });
      setSuppliers(next);
      await refetch();
    } finally { setSaving(false); }
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name || list.includes(name)) return;
    setNewName('');
    saveList([...list, name]);
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h3 style={{ marginBottom: 6 }}>Order From / Suppliers</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 16 }}>
        These entries populate the "Order From" dropdown when adding items to orders. The first entry is the default.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {list.map((s) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
            <span style={{ flex: 1, fontSize: '0.875rem' }}>{s}</span>
            {list[0] === s && <span style={{ fontSize: '0.7rem', padding: '1px 6px', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)', borderRadius: 10 }}>default</span>}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--accent-danger)', padding: '2px 6px' }}
              disabled={saving}
              onClick={() => saveList(list.filter((x) => x !== s))}
              title="Remove"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {list.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No suppliers yet.</p>}
      </div>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, maxWidth: 360 }}>
        <input
          className="form-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Add supplier name…"
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !newName.trim()}>
          <Plus size={14} /> Add
        </button>
      </form>
    </div>
  );
}

function OrdersSettingsTab() {
  const { facilityId } = useFacility();

  const { data: res, refetch } = useQuery({
    queryKey: ['order-email-settings', facilityId],
    queryFn: () => api.get<Record<string, string>>('/settings/facility-settings', {
      keys: 'order_email_recipient,order_email_subject,order_email_body',
    }),
    enabled: facilityId > 0,
  });
  const settings = (res as unknown as { data: Record<string, string> })?.data ?? {};

  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // Re-initialise fields whenever facility changes
  useEffect(() => {
    if (!res) return;
    setRecipient(settings['order_email_recipient'] ?? '');
    setSubject(settings['order_email_subject'] ?? DEFAULT_ORDER_SUBJECT);
    setBody(settings['order_email_body'] ?? DEFAULT_ORDER_BODY);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, res]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/settings/facility-settings', {
        settings: {
          order_email_recipient: recipient,
          order_email_subject: subject,
          order_email_body: body,
        },
      });
      await refetch();
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } finally { setSaving(false); }
  }

  const VARS = ['{item_name}', '{supplier}', '{qty}', '{unit_price}', '{po_number}', '{requester_name}'];

  return (
    <div>
      <OrderSuppliersCard />
      <div className="card">
        <h3 style={{ marginBottom: 6 }}>Order Email Settings</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 20 }}>
          Configure the default approval email template. Use the variables below to inject order details.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
          {VARS.map(v => (
            <code key={v} style={{ fontSize: '0.75rem', padding: '2px 6px', background: 'var(--bg-elevated)', borderRadius: 4, border: '1px solid var(--border-subtle)', color: 'var(--accent-primary)' }}>{v}</code>
          ))}
        </div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560 }}>
          <div className="form-group">
            <label className="form-label">Default Recipient Email</label>
            <input className="form-input" type="email" value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="approvals@facility.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Subject Template</label>
            <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Body Template</label>
            <textarea className="form-textarea" rows={12} value={body} onChange={e => setBody(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.8125rem', minHeight: 220 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Template'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => { setSubject(DEFAULT_ORDER_SUBJECT); setBody(DEFAULT_ORDER_BODY); }}>
              Reset to Default
            </button>
            {savedOk && <span style={{ color: 'var(--accent-success)', fontSize: '0.875rem' }}>✓ Saved</span>}
          </div>
        </form>
      </div>
    </div>
  );
}


interface DropdownOption {
  id: number;
  dropdown_type: string;
  label: string;
  value: string;
  facility_id: number | null;
  org_id: number | null;
  sort_order: number;
  parent_id: number | null;
  is_active?: boolean;
  tags?: string[];
}

const isPatientRoom = (label: string) => /^\d{3}$/.test(label.trim());
const TAG_SUGGESTIONS = ['Patient room', 'Staff area', 'Storage', 'Common area', 'Utility', 'Office'];

// ─── Section card (collapsible wrapper) ───────────────────────────────────────

function SectionCard({
  storageKey, defaultExpanded = false,
  title, scopeLabel, scopeIsOrg,
  accentColor, activeCount, children,
}: {
  storageKey: string; defaultExpanded?: boolean;
  title: string; scopeLabel: string; scopeIsOrg: boolean;
  accentColor: string; activeCount: number;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { const v = sessionStorage.getItem(storageKey); return v === null ? defaultExpanded : v === 'true'; }
    catch { return defaultExpanded; }
  });
  function toggle() {
    setExpanded(p => { const n = !p; try { sessionStorage.setItem(storageKey, String(n)); } catch {} return n; });
  }
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
      <div
        onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', userSelect: 'none', background: `${accentColor}0d`, borderBottom: expanded ? '1px solid var(--border-subtle)' : 'none' }}
      >
        <ChevronDown size={14} style={{ transition: 'transform 150ms', transform: expanded ? 'none' : 'rotate(-90deg)', color: 'var(--text-muted)', flexShrink: 0 }} />
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{title}</span>
        <span className={scopeIsOrg ? 'badge badge-blue' : 'badge badge-amber'} style={{ fontSize: 9 }}>{scopeLabel}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{activeCount} active</span>
      </div>
      {expanded && (
        <div style={{ height: 'calc(100vh - 280px)', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Column toolbar (icon-only with tooltips) ─────────────────────────────────

function ColumnToolbar({
  title, selectedItem, canManage,
  onAdd, addTooltip,
  onEdit, onDeactivate, onActivate, onDelete,
}: {
  title: string;
  selectedItem: DropdownOption | null;
  canManage: boolean;
  onAdd?: () => void;
  addTooltip?: string;
  onEdit?: () => void;
  onDeactivate?: () => void;
  onActivate?: () => void;
  onDelete?: () => void;
}) {
  const [hoveredBtn, setHoveredBtn] = useState('');
  const isSelected = selectedItem !== null;
  const isActive = isSelected && selectedItem!.is_active !== false;
  const isInactive = isSelected && selectedItem!.is_active === false;

  function iconBtn(
    id: string, icon: React.ReactNode, tip: string,
    enabled: boolean, color: string, onClick?: () => void,
  ) {
    return (
      <button
        key={id} type="button"
        onClick={enabled ? onClick : undefined}
        onMouseEnter={() => setHoveredBtn(id)}
        onMouseLeave={() => setHoveredBtn('')}
        style={{
          width: 32, height: 32, borderRadius: 8,
          border: '0.5px solid var(--border-default)',
          background: 'transparent',
          cursor: enabled ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', color: enabled ? color : 'var(--text-muted)',
          opacity: enabled ? 1 : 0.3, flexShrink: 0,
        }}
      >
        {icon}
        {hoveredBtn === id && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-elevated)', border: '0.5px solid var(--border-default)',
            borderRadius: 4, padding: '3px 8px', fontSize: 11,
            color: 'var(--text-primary)', whiteSpace: 'nowrap',
            zIndex: 10, pointerEvents: 'none',
          }}>{tip}</div>
        )}
      </button>
    );
  }

  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ padding: '6px 10px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '0.5px solid var(--border-subtle)' }}>
        {title}
      </div>
      {canManage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 6px', borderBottom: '0.5px solid var(--border-subtle)' }}>
          {onAdd && iconBtn('add', <Plus size={14}/>, addTooltip ?? 'Add', true, 'var(--accent-primary)', onAdd)}
          {(onEdit || onDeactivate || onActivate || onDelete) && (
            <div style={{ width: 1, height: 16, background: 'var(--border-default)', margin: '0 2px' }}/>
          )}
          {onEdit && iconBtn('edit', <Pencil size={14}/>, 'Edit selected', isSelected, 'var(--text-primary)', isSelected ? onEdit : undefined)}
          {onDeactivate && iconBtn('deactivate', <EyeOff size={14}/>, 'Deactivate selected', isActive, 'var(--accent-warning)', isActive ? onDeactivate : undefined)}
          {onActivate && iconBtn('activate', <Eye size={14}/>, 'Activate selected', isInactive, 'var(--accent-success)', isInactive ? onActivate : undefined)}
          {onDelete && iconBtn('delete', <Trash2 size={14}/>, 'Delete selected', isSelected, 'var(--accent-danger)', isSelected ? onDelete : undefined)}
        </div>
      )}
    </div>
  );
}

// ─── Inline edit input ────────────────────────────────────────────────────────

function InlineEditInput({ defaultValue, onSave, onCancel, disabled }: {
  defaultValue: string; onSave: (v: string) => void; onCancel: () => void; disabled?: boolean;
}) {
  return (
    <input
      autoFocus
      defaultValue={defaultValue}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
        if (e.key === 'Escape') { onCancel(); }
      }}
      onBlur={e => onSave(e.target.value)}
      style={{ flex: 1, fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--accent-primary)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', outline: 'none', width: '100%' }}
      disabled={disabled}
    />
  );
}

// ─── Wheelchairs Section ──────────────────────────────────────────────────────

function WheelchairsSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();

  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandLabel, setNewBrandLabel] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [newModelLabel, setNewModelLabel] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [brandError, setBrandError] = useState<string | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);

  const { data: brands = [] } = useDropdownOptions('wheelchair_brand', { includeInactive: true });
  const { data: models = [] } = useDropdownOptions('wheelchair_model', {
    includeInactive: true,
    parentId: selectedBrandId,
    enabled: selectedBrandId !== null,
  });

  const selectedBrand = (brands as DropdownOption[]).find(b => b.id === selectedBrandId) ?? null;
  const selectedModel = (models as DropdownOption[]).find(m => m.id === selectedModelId) ?? null;

  async function saveBrandEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedBrandId(null);
      invalidateDropdowns(qc, 'wheelchair_brand');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function saveModelEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedModelId(null);
      invalidateDropdowns(qc, 'wheelchair_model');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionBrand(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedBrandId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedBrandId}`);
      else await api.patch(`/dropdowns/${selectedBrandId}`, { is_active: action === 'activate' });
      setSelectedBrandId(null);
      invalidateDropdowns(qc, 'wheelchair_brand');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionModel(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedModelId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedModelId}`);
      else await api.patch(`/dropdowns/${selectedModelId}`, { is_active: action === 'activate' });
      setSelectedModelId(null);
      invalidateDropdowns(qc, 'wheelchair_model');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function addBrand() {
    const label = newBrandLabel.trim();
    if (!label) return;
    setSaving(true); setBrandError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: 'wheelchair_brand', label, value: label });
      setNewBrandLabel(''); setAddingBrand(false);
      invalidateDropdowns(qc, 'wheelchair_brand');
    } catch { setBrandError('Failed to add brand'); } finally { setSaving(false); }
  }

  async function addModel() {
    const label = newModelLabel.trim();
    if (!label) return;
    setSaving(true); setModelError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: 'wheelchair_model', label, value: label, parent_id: selectedBrandId ?? null });
      setNewModelLabel(''); setAddingModel(false);
      invalidateDropdowns(qc, 'wheelchair_model');
    } catch { setModelError('Failed to add model'); } finally { setSaving(false); }
  }

  const rowStyle = (selected: boolean, inactive: boolean): React.CSSProperties => ({
    height: 36, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
    background: selected ? 'rgba(59,130,246,0.08)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
    borderLeft: selected ? '2px solid var(--accent-primary)' : '2px solid transparent',
    opacity: inactive ? 0.55 : 1, cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ── LEFT: Brands ── */}
      <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
        <ColumnToolbar
          title="Brands" selectedItem={selectedBrand} canManage={canManage}
          onAdd={() => { setAddingBrand(true); setNewBrandLabel(''); }} addTooltip="Add brand"
          onEdit={() => setEditingId(selectedBrandId)}
          onDeactivate={() => void actionBrand('deactivate')}
          onActivate={() => void actionBrand('activate')}
          onDelete={() => void actionBrand('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingBrand && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Brand name…" value={newBrandLabel}
                onChange={e => setNewBrandLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addBrand(); } if (e.key === 'Escape') setAddingBrand(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingBrand(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {(brands as DropdownOption[]).length === 0 && !addingBrand && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No brands yet</div>
          )}
          {(brands as DropdownOption[]).map(b => editingId === b.id ? (
            <div key={b.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={b.label} onSave={v => void saveBrandEdit(b.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={b.id}
              onClick={() => { setSelectedBrandId(p => p === b.id ? null : b.id); setSelectedModelId(null); setEditingId(null); }}
              style={rowStyle(selectedBrandId === b.id, b.is_active === false)}
              onMouseEnter={e => { if (selectedBrandId !== b.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedBrandId !== b.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: b.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: b.is_active === false ? 'line-through' : 'none' }}>{b.label}</span>
            </div>
          ))}
        </div>
        {brandError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{brandError}</p>}
      </div>

      {/* ── RIGHT: Models ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <ColumnToolbar
          title={selectedBrandId ? `${selectedBrand?.label ?? ''} — Models` : 'Models'}
          selectedItem={selectedModel} canManage={canManage}
          onAdd={selectedBrandId !== null ? () => { setAddingModel(true); setNewModelLabel(''); } : undefined}
          addTooltip="Add model"
          onEdit={() => setEditingId(selectedModelId)}
          onDeactivate={() => void actionModel('deactivate')}
          onActivate={() => void actionModel('activate')}
          onDelete={() => void actionModel('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingModel && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Model name…" value={newModelLabel}
                onChange={e => setNewModelLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addModel(); } if (e.key === 'Escape') setAddingModel(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingModel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {!selectedBrandId && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>← Select a brand to view its models</div>
          )}
          {selectedBrandId && (models as DropdownOption[]).length === 0 && !addingModel && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No models for this brand</div>
          )}
          {(models as DropdownOption[]).map(m => editingId === m.id ? (
            <div key={m.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={m.label} onSave={v => void saveModelEdit(m.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={m.id}
              onClick={() => setSelectedModelId(p => p === m.id ? null : m.id)}
              style={rowStyle(selectedModelId === m.id, m.is_active === false)}
              onMouseEnter={e => { if (selectedModelId !== m.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedModelId !== m.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: m.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: m.is_active === false ? 'line-through' : 'none' }}>{m.label}</span>
            </div>
          ))}
        </div>
        {modelError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{modelError}</p>}
      </div>
    </div>
  );
}

// ─── Generic Equipment Brand/Model Section ────────────────────────────────────

function EquipmentBrandModelSection({ brandType, modelType, modelLabel = 'Models', canManage }: {
  brandType: string; modelType: string; modelLabel?: string; canManage: boolean;
}) {
  const qc = useQueryClient();
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [addingBrand, setAddingBrand] = useState(false);
  const [newBrandLabel, setNewBrandLabel] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [newModelLabel, setNewModelLabel] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [brandError, setBrandError] = useState<string | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);

  const { data: brands = [] } = useDropdownOptions(brandType, { includeInactive: true });
  const { data: models = [] } = useDropdownOptions(modelType, {
    includeInactive: true,
    parentId: selectedBrandId,
    enabled: selectedBrandId !== null,
  });

  const dedupedBrands = (brands as DropdownOption[]).reduce((acc, b) => {
    if (!acc.find(x => x.label === b.label)) acc.push(b);
    return acc;
  }, [] as DropdownOption[]);
  const selectedBrand = dedupedBrands.find(b => b.id === selectedBrandId) ?? null;
  const selectedModel = (models as DropdownOption[]).find(m => m.id === selectedModelId) ?? null;

  async function saveBrandEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedBrandId(null);
      invalidateDropdowns(qc, brandType);
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function saveModelEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedModelId(null);
      invalidateDropdowns(qc, modelType);
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionBrand(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedBrandId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedBrandId}`);
      else await api.patch(`/dropdowns/${selectedBrandId}`, { is_active: action === 'activate' });
      setSelectedBrandId(null);
      invalidateDropdowns(qc, brandType);
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionModel(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedModelId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedModelId}`);
      else await api.patch(`/dropdowns/${selectedModelId}`, { is_active: action === 'activate' });
      setSelectedModelId(null);
      invalidateDropdowns(qc, modelType);
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function addBrand() {
    const label = newBrandLabel.trim();
    if (!label) return;
    setSaving(true); setBrandError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: brandType, label, value: label });
      setNewBrandLabel(''); setAddingBrand(false);
      invalidateDropdowns(qc, brandType);
    } catch { setBrandError('Failed to add brand'); } finally { setSaving(false); }
  }

  async function addModel() {
    const label = newModelLabel.trim();
    if (!label) return;
    setSaving(true); setModelError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: modelType, label, value: label, parent_id: selectedBrandId ?? null });
      setNewModelLabel(''); setAddingModel(false);
      invalidateDropdowns(qc, modelType);
    } catch { setModelError(`Failed to add ${modelLabel.toLowerCase().replace(/s$/, '')}`); } finally { setSaving(false); }
  }

  const rowStyle = (selected: boolean, inactive: boolean): React.CSSProperties => ({
    height: 36, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
    background: selected ? 'rgba(59,130,246,0.08)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
    borderLeft: selected ? '2px solid var(--accent-primary)' : '2px solid transparent',
    opacity: inactive ? 0.55 : 1, cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ── LEFT: Brands ── */}
      <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
        <ColumnToolbar
          title="Brands" selectedItem={selectedBrand} canManage={canManage}
          onAdd={() => { setAddingBrand(true); setNewBrandLabel(''); }} addTooltip="Add brand"
          onEdit={() => setEditingId(selectedBrandId)}
          onDeactivate={() => void actionBrand('deactivate')}
          onActivate={() => void actionBrand('activate')}
          onDelete={() => void actionBrand('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingBrand && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Brand name…" value={newBrandLabel}
                onChange={e => setNewBrandLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addBrand(); } if (e.key === 'Escape') setAddingBrand(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingBrand(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {dedupedBrands.length === 0 && !addingBrand && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No brands yet</div>
          )}
          {dedupedBrands.map(b => editingId === b.id ? (
            <div key={b.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={b.label} onSave={v => void saveBrandEdit(b.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={b.id}
              onClick={() => { setSelectedBrandId(p => p === b.id ? null : b.id); setSelectedModelId(null); setEditingId(null); }}
              style={rowStyle(selectedBrandId === b.id, b.is_active === false)}
              onMouseEnter={e => { if (selectedBrandId !== b.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedBrandId !== b.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: b.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: b.is_active === false ? 'line-through' : 'none' }}>{b.label}</span>
            </div>
          ))}
        </div>
        {brandError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{brandError}</p>}
      </div>

      {/* ── RIGHT: Models/Colors ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <ColumnToolbar
          title={selectedBrandId ? `${selectedBrand?.label ?? ''} — ${modelLabel}` : modelLabel}
          selectedItem={selectedModel} canManage={canManage}
          onAdd={selectedBrandId !== null ? () => { setAddingModel(true); setNewModelLabel(''); } : undefined}
          addTooltip={`Add ${modelLabel.toLowerCase().replace(/s$/, '')}`}
          onEdit={() => setEditingId(selectedModelId)}
          onDeactivate={() => void actionModel('deactivate')}
          onActivate={() => void actionModel('activate')}
          onDelete={() => void actionModel('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingModel && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder={`${modelLabel.toLowerCase().replace(/s$/, '')} name…`} value={newModelLabel}
                onChange={e => setNewModelLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addModel(); } if (e.key === 'Escape') setAddingModel(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingModel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {!selectedBrandId && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>← Select a brand to view its {modelLabel.toLowerCase()}</div>
          )}
          {selectedBrandId && (models as DropdownOption[]).length === 0 && !addingModel && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No {modelLabel.toLowerCase()} for this brand</div>
          )}
          {(models as DropdownOption[]).map(m => editingId === m.id ? (
            <div key={m.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={m.label} onSave={v => void saveModelEdit(m.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={m.id}
              onClick={() => setSelectedModelId(p => p === m.id ? null : m.id)}
              style={rowStyle(selectedModelId === m.id, m.is_active === false)}
              onMouseEnter={e => { if (selectedModelId !== m.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedModelId !== m.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: m.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: m.is_active === false ? 'line-through' : 'none' }}>{m.label}</span>
            </div>
          ))}
        </div>
        {modelError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{modelError}</p>}
      </div>
    </div>
  );
}

// ─── Areas & Locations Section ────────────────────────────────────────────────

function AreasLocationsSection({ facilityId, canManage }: { facilityId: number | null | undefined; canManage: boolean }) {
  const qc = useQueryClient();

  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [selectedLocId, setSelectedLocId] = useState<number | null>(null);
  const [addingArea, setAddingArea] = useState(false);
  const [newAreaLabel, setNewAreaLabel] = useState('');
  const [addingLoc, setAddingLoc] = useState(false);
  const [newLocLabel, setNewLocLabel] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [addingTagId, setAddingTagId] = useState<number | null>(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [areaError, setAreaError] = useState<string | null>(null);
  const [locError, setLocError] = useState<string | null>(null);

  // Legacy keys Tasks.tsx still reads /dropdowns through directly (['dropdown-areas', facilityId],
  // ['dropdown-locations', facilityId, areaId]) — not yet migrated to useDropdownOptions, so kept
  // alongside invalidateDropdowns() below rather than folded into it (it's facility-scoped here,
  // not a fixed key invalidateDropdowns' own legacy list could express for every facility).
  const invalidateLegacyAreaLocationKeys = () => {
    qc.invalidateQueries({ queryKey: ['dropdown-areas', facilityId] });
    qc.invalidateQueries({ queryKey: ['dropdown-locations', facilityId] });
  };

  const { data: areas = [] } = useDropdownOptions('area', {
    facilityId,
    includeInactive: true,
    enabled: facilityId != null && Number(facilityId) > 0,
  });

  const { data: locations = [] } = useDropdownOptions('location', {
    facilityId,
    parentId: selectedAreaId,
    includeInactive: true,
    enabled: facilityId != null && Number(facilityId) > 0 && selectedAreaId !== null,
  });

  const selectedArea = (areas as DropdownOption[]).find(a => a.id === selectedAreaId) ?? null;
  const selectedLoc = (locations as DropdownOption[]).find(l => l.id === selectedLocId) ?? null;
  const activeLocCount = (locations as DropdownOption[]).filter(l => l.is_active !== false).length;

  async function saveAreaEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedAreaId(null);
      invalidateDropdowns(qc, 'area');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function saveLocEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null); setSelectedLocId(null);
      invalidateDropdowns(qc, 'location');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionArea(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedAreaId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedAreaId}`);
      else await api.patch(`/dropdowns/${selectedAreaId}`, { is_active: action === 'activate' });
      setSelectedAreaId(null);
      invalidateDropdowns(qc, 'area');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function actionLoc(action: 'activate' | 'deactivate' | 'delete') {
    if (!selectedLocId) return;
    setSaving(true);
    try {
      if (action === 'delete') await api.delete(`/dropdowns/${selectedLocId}`);
      else await api.patch(`/dropdowns/${selectedLocId}`, { is_active: action === 'activate' });
      setSelectedLocId(null);
      invalidateDropdowns(qc, 'location');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function addArea() {
    const label = newAreaLabel.trim();
    if (!label) return;
    setSaving(true); setAreaError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: 'area', label, value: label, facility_id: facilityId });
      setNewAreaLabel(''); setAddingArea(false);
      invalidateDropdowns(qc, 'area');
      invalidateLegacyAreaLocationKeys();
    } catch { setAreaError('Failed to add area'); } finally { setSaving(false); }
  }

  async function addLocation() {
    const label = newLocLabel.trim();
    if (!label || !selectedAreaId) return;
    setSaving(true); setLocError(null);
    try {
      const value = label.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
      await api.post('/dropdowns', { dropdown_type: 'location', label, value, facility_id: facilityId, parent_id: selectedAreaId });
      setNewLocLabel(''); setAddingLoc(false);
      invalidateDropdowns(qc, 'location');
      invalidateLegacyAreaLocationKeys();
    } catch { setLocError('Failed to add location'); } finally { setSaving(false); }
  }

  async function addTag(id: number, currentTags: string[], tag: string) {
    const t = tag.trim();
    if (!t || currentTags.includes(t)) { setAddingTagId(null); return; }
    try {
      await api.patch(`/dropdowns/${id}`, { tags: [...currentTags, t] });
      setAddingTagId(null); setNewTagInput('');
      invalidateDropdowns(qc, 'location');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ }
  }

  async function removeTag(id: number, currentTags: string[], tag: string) {
    try {
      await api.patch(`/dropdowns/${id}`, { tags: currentTags.filter(t => t !== tag) });
      invalidateDropdowns(qc, 'location');
      invalidateLegacyAreaLocationKeys();
    } catch { /* silent */ }
  }

  const rowStyle = (selected: boolean, inactive: boolean): React.CSSProperties => ({
    minHeight: 36, display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
    background: selected ? 'rgba(59,130,246,0.08)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
    borderLeft: selected ? '2px solid var(--accent-primary)' : '2px solid transparent',
    opacity: inactive ? 0.55 : 1, cursor: 'pointer', overflow: 'hidden',
  });

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ── LEFT: Areas ── */}
      <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
        <ColumnToolbar
          title="Areas" selectedItem={selectedArea} canManage={canManage}
          onAdd={() => { setAddingArea(true); setNewAreaLabel(''); }} addTooltip="Add area"
          onEdit={() => setEditingId(selectedAreaId)}
          onDeactivate={() => void actionArea('deactivate')}
          onActivate={() => void actionArea('activate')}
          onDelete={() => void actionArea('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingArea && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Area name…" value={newAreaLabel}
                onChange={e => setNewAreaLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addArea(); } if (e.key === 'Escape') setAddingArea(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingArea(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {(areas as DropdownOption[]).length === 0 && !addingArea && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No areas yet</div>
          )}
          {(areas as DropdownOption[]).map(area => editingId === area.id ? (
            <div key={area.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={area.label} onSave={v => void saveAreaEdit(area.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={area.id}
              onClick={() => { setSelectedAreaId(p => p === area.id ? null : area.id); setSelectedLocId(null); setEditingId(null); }}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', background: selectedAreaId === area.id ? 'rgba(59,130,246,0.08)' : 'transparent', borderBottom: '1px solid var(--border-subtle)', borderLeft: selectedAreaId === area.id ? '2px solid var(--accent-primary)' : '2px solid transparent', opacity: area.is_active === false ? 0.55 : 1, cursor: 'pointer' }}
              onMouseEnter={e => { if (selectedAreaId !== area.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedAreaId !== area.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: area.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: area.is_active === false ? 'line-through' : 'none' }}>{area.label}</span>
              {selectedAreaId === area.id && activeLocCount > 0 && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{activeLocCount}</span>
              )}
            </div>
          ))}
        </div>
        {areaError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{areaError}</p>}
      </div>

      {/* ── RIGHT: Locations ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <ColumnToolbar
          title={selectedArea ? `${selectedArea.label} — Locations` : 'Locations'}
          selectedItem={selectedLoc} canManage={canManage}
          onAdd={selectedAreaId !== null ? () => { setAddingLoc(true); setNewLocLabel(''); } : undefined}
          addTooltip="Add location"
          onEdit={() => setEditingId(selectedLocId)}
          onDeactivate={() => void actionLoc('deactivate')}
          onActivate={() => void actionLoc('activate')}
          onDelete={() => void actionLoc('delete')}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingLoc && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Location name…" value={newLocLabel}
                onChange={e => setNewLocLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addLocation(); } if (e.key === 'Escape') setAddingLoc(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingLoc(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          )}
          {!selectedAreaId && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>← Select an area</div>
          )}
          {selectedAreaId && (locations as DropdownOption[]).length === 0 && !addingLoc && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No locations for this area</div>
          )}
          {(locations as DropdownOption[]).map(loc => editingId === loc.id ? (
            <div key={loc.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <InlineEditInput defaultValue={loc.label} onSave={v => void saveLocEdit(loc.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
            </div>
          ) : (
            <div key={loc.id}
              onClick={() => setSelectedLocId(p => p === loc.id ? null : loc.id)}
              style={rowStyle(selectedLocId === loc.id, loc.is_active === false)}
              onMouseEnter={e => { if (selectedLocId !== loc.id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { if (selectedLocId !== loc.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: loc.is_active === false ? '#6b7280' : '#10b981' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', textDecoration: loc.is_active === false ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.label}</span>

              {/* Tags — right-aligned */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', flexShrink: 0 }}>
                {(loc.tags ?? []).map((tag: string) => (
                  <span key={tag} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(99,102,241,0.12)', color: '#6366f1', border: '0.5px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                    {tag}
                    {canManage && (
                      <span onClick={e => { e.stopPropagation(); void removeTag(loc.id, loc.tags ?? [], tag); }} style={{ fontSize: 9, opacity: 0.7, cursor: 'pointer' }}>×</span>
                    )}
                  </span>
                ))}

                {canManage && addingTagId !== loc.id && (
                  <span onClick={e => { e.stopPropagation(); setAddingTagId(loc.id); setNewTagInput(''); }}
                    style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, border: '0.5px dashed var(--border-default)', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0 }}>
                    + tag
                  </span>
                )}

                {addingTagId === loc.id && (
                  <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <input
                      autoFocus value={newTagInput}
                      onChange={e => setNewTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addTag(loc.id, loc.tags ?? [], newTagInput); } if (e.key === 'Escape') setAddingTagId(null); }}
                      onBlur={() => setTimeout(() => setAddingTagId(null), 150)}
                      style={{ width: 80, fontSize: 11, padding: '1px 4px', borderRadius: 4, border: '1px solid var(--accent-primary)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', outline: 'none' }}
                    />
                    <div style={{ position: 'absolute', top: '100%', right: 0, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 6, zIndex: 20, minWidth: 120, boxShadow: 'var(--shadow-md)' }}>
                      {TAG_SUGGESTIONS
                        .filter(s => !(loc.tags ?? []).includes(s) && s.toLowerCase().includes(newTagInput.toLowerCase()))
                        .map(s => (
                          <div key={s}
                            onMouseDown={e => { e.preventDefault(); void addTag(loc.id, loc.tags ?? [], s); }}
                            style={{ padding: '5px 10px', fontSize: 11, cursor: 'pointer', color: 'var(--text-primary)' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                            {s}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {locError && <p style={{ margin: '0 8px 4px', fontSize: 11, color: '#ef4444' }}>{locError}</p>}
      </div>
    </div>
  );
}

// ─── Ticket Items Section ────────────────────────────────────────────────────

function TicketItemsSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: items = [] } = useDropdownOptions('ticket_item', { includeInactive: true });

  async function addItem() {
    const label = newItemLabel.trim();
    if (!label) return;
    setSaving(true); setError(null);
    try {
      await api.post('/dropdowns', { dropdown_type: 'ticket_item', label, value: label });
      setNewItemLabel(''); setAddingItem(false);
      invalidateDropdowns(qc, 'ticket_item');
    } catch { setError('Failed to add item'); } finally { setSaving(false); }
  }

  async function saveEdit(id: number, newLabel: string) {
    if (!newLabel.trim()) { setEditingId(null); return; }
    setSaving(true);
    try {
      await api.patch(`/dropdowns/${id}`, { label: newLabel.trim(), value: newLabel.trim() });
      setEditingId(null);
      invalidateDropdowns(qc, 'ticket_item');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function deleteItem(id: number) {
    setSaving(true);
    try {
      await api.delete(`/dropdowns/${id}`);
      invalidateDropdowns(qc, 'ticket_item');
    } catch { /* silent */ } finally { setSaving(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {canManage && (
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          {addingItem ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '4px 8px' }}
                placeholder="Item name…" value={newItemLabel}
                onChange={e => setNewItemLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addItem(); } if (e.key === 'Escape') setAddingItem(false); }}
                disabled={saving} />
              <button type="button" onClick={() => setAddingItem(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
            </div>
          ) : (
            <button type="button" onClick={() => { setAddingItem(true); setNewItemLabel(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', fontSize: 12, padding: 0 }}>
              <Plus size={13}/> Add item
            </button>
          )}
          {error && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#ef4444' }}>{error}</p>}
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {(items as DropdownOption[]).length === 0 && !addingItem && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No ticket items yet</div>
        )}
        {(items as DropdownOption[]).map(item => editingId === item.id ? (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <InlineEditInput defaultValue={item.label} onSave={v => void saveEdit(item.id, v)} onCancel={() => setEditingId(null)} disabled={saving} />
          </div>
        ) : (
          <div key={item.id}
            style={{ height: 36, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', borderBottom: '1px solid var(--border-subtle)', opacity: item.is_active === false ? 0.55 : 1 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: item.is_active === false ? '#6b7280' : '#10b981' }} />
            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
            {canManage && (
              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                <button type="button" onClick={() => setEditingId(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '3px 5px', display: 'flex', alignItems: 'center', borderRadius: 4 }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <Pencil size={12}/>
                </button>
                <button type="button" onClick={() => void deleteItem(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', padding: '3px 5px', display: 'flex', alignItems: 'center', borderRadius: 4 }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.08)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <Trash2 size={12}/>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BOM Equipment Section ────────────────────────────────────────────────────
function equipTypeToBrand(t: string): string {
  const m: Record<string, string> = {
    wheelchair: 'wheelchair_brand', bed: 'bed_brand',
    concentrators: 'concentrator_brand', concentrator: 'concentrator_brand',
    lift: 'lift_brand', hvac: 'hvac_brand', other: 'other_brand',
    shower_chairs: 'shower_chair_brand', shower_chair: 'shower_chair_brand',
    paint: 'paint_brand',
  };
  return m[t.toLowerCase()] ?? `${t}_brand`;
}
function equipTypeToModel(t: string): string {
  const m: Record<string, string> = {
    wheelchair: 'wheelchair_model', bed: 'bed_model',
    concentrators: 'concentrator_model', concentrator: 'concentrator_model',
    lift: 'lift_model', hvac: 'hvac_model', other: 'other_model',
    shower_chairs: 'shower_chair_model', shower_chair: 'shower_chair_model',
    paint: 'paint_color',
  };
  return m[t.toLowerCase()] ?? `${t}_model`;
}

type BomEquipmentEntry = { id: number; type: string; displayName: string; brand: string | null; model: string | null; facilitySupplyType: string | null };

function BomEquipmentSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { data: bomData, isLoading: loading } = useQuery({
    queryKey: ['bom-equipment-types'],
    queryFn: async () => (await api.get('/inventory/bom-equipment')) as unknown as { types: any[]; entries: BomEquipmentEntry[] },
  });
  const entries = bomData?.entries ?? [];
  const [saving, setSaving] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [addingType, setAddingType] = useState(false);
  const [newTypeEquipType, setNewTypeEquipType] = useState('');
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editEquipType, setEditEquipType] = useState('');
  const [typeError, setTypeError] = useState<string | null>(null);

  function authHeaders() {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
  }

  function refreshEntries() {
    return qc.invalidateQueries({ queryKey: ['bom-equipment-types'] });
  }

  const typeEntries = entries.filter(e => e.brand === null);
  const selectedTypeEntry = typeEntries.find(e => e.id === selectedTypeId) ?? null;

  const selectedTypeAsOpt: DropdownOption | null = selectedTypeEntry
    ? { id: selectedTypeEntry.id, label: selectedTypeEntry.displayName, value: selectedTypeEntry.type, is_active: true, dropdown_type: '', facility_id: null, org_id: null, sort_order: 0, parent_id: null }
    : null;

  async function addType() {
    const et = newTypeEquipType.trim();
    const dn = newTypeLabel.trim();
    if (!et || !dn) { setTypeError('Both fields are required'); return; }
    setSaving(true); setTypeError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/inventory/bom-equipment`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ equipment_type: et, display_name: dn, brand: null, model: null }),
      });
      if (!res.ok) { const d = await res.json(); setTypeError(d.error || 'Failed to add'); return; }
      setNewTypeEquipType(''); setNewTypeLabel(''); setAddingType(false);
      await refreshEntries();
    } catch { setTypeError('Failed to add'); } finally { setSaving(false); }
  }

  async function saveEdit(id: number) {
    const label = editLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      const body = { equipment_type: editEquipType.trim(), display_name: label, brand: null, model: null };
      const res = await fetch(`${import.meta.env.VITE_API_URL}/inventory/bom-equipment/${id}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) return;
      setEditingId(null);
      setSelectedTypeId(null);
      await refreshEntries();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  async function deleteSelected(id: number) {
    setSaving(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/inventory/bom-equipment/${id}`, { method: 'DELETE', headers: authHeaders() });
      setSelectedTypeId(null);
      await refreshEntries();
    } catch { /* silent */ } finally { setSaving(false); }
  }

  const rowStyle = (selected: boolean): React.CSSProperties => ({
    height: 36, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
    background: selected ? 'rgba(59,130,246,0.08)' : 'transparent',
    borderBottom: '1px solid var(--border-subtle)',
    borderLeft: selected ? '2px solid var(--accent-primary)' : '2px solid transparent',
    cursor: 'pointer',
  });

  if (loading) return <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ── LEFT: Equipment Types ── */}
      <div style={{ width: 200, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column' }}>
        <ColumnToolbar
          title="Equipment Types" selectedItem={selectedTypeAsOpt} canManage={canManage}
          onAdd={() => { setAddingType(true); setNewTypeEquipType(''); setNewTypeLabel(''); setTypeError(null); }}
          addTooltip="Add equipment type"
          onEdit={selectedTypeEntry ? () => { setEditingId(selectedTypeEntry.id); setEditLabel(selectedTypeEntry.displayName); setEditEquipType(selectedTypeEntry.type); } : undefined}
          onDelete={selectedTypeEntry ? () => void deleteSelected(selectedTypeEntry.id) : undefined}
        />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {addingType && (
            <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Type key (e.g. wheelchair)" value={newTypeEquipType}
                onChange={e => setNewTypeEquipType(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addType(); } if (e.key === 'Escape') setAddingType(false); }}
                disabled={saving} />
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                  placeholder="Display name" value={newTypeLabel}
                  onChange={e => setNewTypeLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addType(); } if (e.key === 'Escape') setAddingType(false); }}
                  disabled={saving} />
                <button type="button" onClick={() => { setAddingType(false); setTypeError(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
              </div>
              {typeError && <p style={{ margin: 0, fontSize: 11, color: '#ef4444' }}>{typeError}</p>}
            </div>
          )}
          {typeEntries.length === 0 && !addingType && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>No types yet</div>
          )}
          {typeEntries.map(e => editingId === e.id ? (
            <div key={e.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <input autoFocus className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                placeholder="Type key" value={editEquipType}
                onChange={ev => setEditEquipType(ev.target.value)}
                onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); void saveEdit(e.id); } if (ev.key === 'Escape') setEditingId(null); }}
                disabled={saving} />
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input className="form-input" style={{ flex: 1, fontSize: 12, padding: '3px 6px' }}
                  placeholder="Display name" value={editLabel}
                  onChange={ev => setEditLabel(ev.target.value)}
                  onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); void saveEdit(e.id); } if (ev.key === 'Escape') setEditingId(null); }}
                  disabled={saving} />
                <button type="button" onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 2px' }}><X size={12}/></button>
              </div>
            </div>
          ) : (
            <div key={e.id}
              onClick={() => { setSelectedTypeId(p => p === e.id ? null : e.id); setEditingId(null); }}
              style={rowStyle(selectedTypeId === e.id)}
              onMouseEnter={ev => { if (selectedTypeId !== e.id) (ev.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={ev => { if (selectedTypeId !== e.id) (ev.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: '#6366f1' }} />
              <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.displayName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Brands + Models via dropdown_options ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {!selectedTypeEntry ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, fontStyle: 'italic' }}>
            ← Select an equipment type to view its brands
          </div>
        ) : (
          <EquipmentBrandModelSection
            key={`${selectedTypeEntry.type}-brands`}
            brandType={equipTypeToBrand(selectedTypeEntry.type)}
            modelType={equipTypeToModel(selectedTypeEntry.type)}
            modelLabel={selectedTypeEntry.type === 'paint' ? 'Colors' : 'Models'}
            canManage={canManage}
          />
        )}
      </div>
    </div>
  );
}

// ─── DropdownsTab ─────────────────────────────────────────────────────────────

function DropdownsTab() {
  const { user } = useAuth();
  const { facilityId: contextFacilityId } = useFacility();
  // contextFacilityId defaults to 0 when no override — treat 0 as "not set"
  // Fall back to the JWT facilityId from the user object directly
  const facilityId = (contextFacilityId && contextFacilityId > 0)
    ? contextFacilityId
    : (user?.facilityId ?? null);
  const canManage = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const canManageBom = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const [dropdownFilter, setDropdownFilter] = useState<'all'|'inventory'|'locations'|'workorders'>('all');

  const { data: brandOptions = [] } = useDropdownOptions('wheelchair_brand', { includeInactive: true });

  const { data: areaOptions = [] } = useDropdownOptions('area', {
    facilityId,
    includeInactive: true,
    enabled: facilityId != null && Number(facilityId) > 0,
  });

  const { data: ticketItemOptions = [] } = useDropdownOptions('ticket_item', { includeInactive: true });

  const activeBrandCount = (brandOptions as DropdownOption[]).filter(b => b.is_active !== false).length;
  const activeTicketCount = (ticketItemOptions as DropdownOption[]).filter(t => t.is_active !== false).length;
  const activeAreaCount = (areaOptions as DropdownOption[]).filter(a => a.is_active !== false).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>Dropdown Options</h3>
        <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          Manage the options shown in dropdown menus across the app.
        </p>
      </div>

      <div style={{ display:'flex', gap:6, borderBottom:'0.5px solid var(--border-subtle)', paddingBottom:10 }}>
        {([
          { key:'all',        label:'All' },
          { key:'inventory',  label:'Inventory' },
          { key:'locations',  label:'Areas & Locations' },
          { key:'workorders', label:'Work Orders' },
        ] as const).map(t => (
          <button key={t.key}
            onClick={() => setDropdownFilter(t.key)}
            style={{
              fontSize:12, fontWeight:500,
              padding:'5px 14px', borderRadius:20,
              border:'0.5px solid var(--border-default)',
              background: dropdownFilter===t.key ? 'var(--accent-primary)' : 'transparent',
              color: dropdownFilter===t.key ? '#fff' : 'var(--text-secondary)',
              cursor:'pointer'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard
          storageKey="dropdown_section_wheelchairs" defaultExpanded={false}
          title="Wheelchairs" scopeLabel="Org-wide" scopeIsOrg={true}
          accentColor="#3b82f6" activeCount={activeBrandCount}
        >
          <WheelchairsSection canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_beds" defaultExpanded={false}
          title="Beds" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#8b5cf6" activeCount={0}>
          <EquipmentBrandModelSection brandType="bed_brand" modelType="bed_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_concentrators" defaultExpanded={false}
          title="Concentrators" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#10b981" activeCount={0}>
          <EquipmentBrandModelSection brandType="concentrator_brand" modelType="concentrator_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_lifts" defaultExpanded={false}
          title="Lifts" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#f59e0b" activeCount={0}>
          <EquipmentBrandModelSection brandType="lift_brand" modelType="lift_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_hvac" defaultExpanded={false}
          title="HVAC" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#06b6d4" activeCount={0}>
          <EquipmentBrandModelSection brandType="hvac_brand" modelType="hvac_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_other_equip" defaultExpanded={false}
          title="Other Equipment" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#6b7280" activeCount={0}>
          <EquipmentBrandModelSection brandType="other_brand" modelType="other_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_paint" defaultExpanded={false}
          title="Paint" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#ec4899" activeCount={0}>
          <EquipmentBrandModelSection brandType="paint_brand" modelType="paint_color" modelLabel="Colors" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && (
        <SectionCard storageKey="dropdown_section_shower_chairs" defaultExpanded={false}
          title="Shower Chairs" scopeLabel="Org-wide" scopeIsOrg={true} accentColor="#84cc16" activeCount={0}>
          <EquipmentBrandModelSection brandType="shower_chair_brand" modelType="shower_chair_model" canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'inventory') && canManageBom && (
        <SectionCard
          storageKey="dropdown_section_bom_equipment" defaultExpanded={false}
          title="Equipment BOM" scopeLabel="Org-wide" scopeIsOrg={true}
          accentColor="#6366f1" activeCount={0}
        >
          <BomEquipmentSection canManage={canManageBom} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'workorders') && (
        <SectionCard
          storageKey="dropdown_section_ticket_items" defaultExpanded={false}
          title="Ticket Items" scopeLabel="Org-wide" scopeIsOrg={true}
          accentColor="#8b5cf6" activeCount={activeTicketCount}
        >
          <TicketItemsSection canManage={canManage} />
        </SectionCard>
      )}

      {(dropdownFilter === 'all' || dropdownFilter === 'locations') && (
        <SectionCard
          storageKey="dropdown_section_areas" defaultExpanded={true}
          title="Areas & Locations" scopeLabel="Facility-specific" scopeIsOrg={false}
          accentColor="#8b5cf6" activeCount={activeAreaCount}
        >
          <AreasLocationsSection facilityId={facilityId} canManage={canManage} />
        </SectionCard>
      )}
    </div>
  );
}
